"use client"

// src/WorkspacesComponent.tsx
import React6 from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// src/context/AppConfigContext.tsx
import { createContext, useContext } from "react";
import { jsx } from "react/jsx-runtime";
var AppConfigContext = createContext(void 0);
var AppConfigProvider = ({ children, app_id, debug = false }) => {
  return /* @__PURE__ */ jsx(AppConfigContext.Provider, { value: { app_id, debug }, children });
};
var useAppConfig = () => {
  const context = useContext(AppConfigContext);
  if (!context) {
    throw new Error("useAppConfig must be used within AppConfigProvider");
  }
  return context;
};

// src/utils/logger.ts
var useLogger = () => {
  const { debug } = useAppConfig();
  return {
    debug: (...args) => {
      if (debug)
        console.log("[Workspaces]", ...args);
    },
    error: (...args) => {
      if (debug)
        console.error("[Workspaces]", ...args);
    }
  };
};
var logger = {
  debug: (debug, ...args) => {
    if (debug)
      console.log("[Workspaces]", ...args);
  },
  error: (debug, ...args) => {
    if (debug)
      console.error("[Workspaces]", ...args);
  }
};

// src/components/views/GlobalAdminView.tsx
import { useState as useState8, useMemo as useMemo3 } from "react";
import { useQuery as useQuery3, useMutation as useMutation3, useQueryClient as useQueryClient3 } from "@tanstack/react-query";
import { Button as Button8 } from "@umd-dit-ai-workspace/shared-ui";
import { Card as Card3, CardContent as CardContent3, CardDescription, CardHeader as CardHeader3, CardTitle as CardTitle3 } from "@umd-dit-ai-workspace/shared-ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@umd-dit-ai-workspace/shared-ui";

// src/components/organizations/EditOrganizationDialog.tsx
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@umd-dit-ai-workspace/shared-ui";
import { Button } from "@umd-dit-ai-workspace/shared-ui";
import { Label } from "@umd-dit-ai-workspace/shared-ui";
import { Input } from "@umd-dit-ai-workspace/shared-ui";
import { Textarea } from "@umd-dit-ai-workspace/shared-ui";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
function EditOrganizationDialog({
  organization,
  open,
  onOpenChange,
  onSave,
  isPending
}) {
  const [formData, setFormData] = useState({
    orgId: "",
    name: "",
    description: ""
  });
  useEffect(() => {
    if (organization) {
      setFormData({
        orgId: organization.orgId,
        name: organization.name,
        description: organization.description || ""
      });
    } else {
      setFormData({
        orgId: "",
        name: "",
        description: ""
      });
    }
  }, [organization]);
  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.name.trim()) {
      onSave(organization, {
        orgId: !organization && formData.orgId.trim() ? formData.orgId.trim() : void 0,
        name: formData.name.trim(),
        description: formData.description.trim() || void 0
      });
    }
  };
  return /* @__PURE__ */ jsx2(Dialog, { open, onOpenChange, children: /* @__PURE__ */ jsxs(DialogContent, { className: "max-w-md", children: [
    /* @__PURE__ */ jsxs(DialogHeader, { children: [
      /* @__PURE__ */ jsx2(DialogTitle, { children: organization ? "Edit Organization" : "Create Organization" }),
      /* @__PURE__ */ jsx2(DialogDescription, { children: organization ? "Update the organization details below." : "Fill in the details to create a new organization." })
    ] }),
    /* @__PURE__ */ jsxs("form", { onSubmit: handleSubmit, children: [
      /* @__PURE__ */ jsxs("div", { className: "space-y-4 py-4", children: [
        /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsx2(Label, { htmlFor: "org_id", children: "Organization ID" }),
          /* @__PURE__ */ jsx2(
            Input,
            {
              id: "org_id",
              value: organization ? organization.orgId : formData.orgId,
              onChange: (e) => setFormData((prev) => ({ ...prev, orgId: e.target.value })),
              disabled: !!organization,
              className: organization ? "bg-muted" : "",
              placeholder: organization ? "" : "Enter organization ID (optional)"
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsx2(Label, { htmlFor: "name", children: "Organization Name" }),
          /* @__PURE__ */ jsx2(
            Input,
            {
              id: "name",
              value: formData.name,
              onChange: (e) => setFormData((prev) => ({ ...prev, name: e.target.value })),
              placeholder: "Organization name",
              required: true
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsx2(Label, { htmlFor: "description", children: "Description (Optional)" }),
          /* @__PURE__ */ jsx2(
            Textarea,
            {
              id: "description",
              value: formData.description,
              onChange: (e) => setFormData((prev) => ({ ...prev, description: e.target.value })),
              placeholder: "Describe the organization's purpose",
              rows: 3
            }
          )
        ] })
      ] }),
      /* @__PURE__ */ jsxs(DialogFooter, { children: [
        /* @__PURE__ */ jsx2(
          Button,
          {
            type: "button",
            variant: "outline",
            onClick: () => onOpenChange(false),
            disabled: isPending,
            children: "Cancel"
          }
        ),
        /* @__PURE__ */ jsx2(Button, { type: "submit", disabled: isPending, children: organization ? "Save Changes" : "Create Organization" })
      ] })
    ] })
  ] }) });
}

// src/components/organizations/OrganizationActionsDropdown.tsx
import { useState as useState3 } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@umd-dit-ai-workspace/shared-ui";
import { Button as Button3 } from "@umd-dit-ai-workspace/shared-ui";
import { MoreHorizontal, Edit, Trash2 as Trash22, Users } from "lucide-react";

// src/components/organizations/OrganizationMembersDialog.tsx
import { useState as useState2, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog as Dialog2,
  DialogContent as DialogContent2,
  DialogDescription as DialogDescription2,
  DialogHeader as DialogHeader2,
  DialogTitle as DialogTitle2,
  Badge,
  Button as Button2,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
  SearchableSelect
} from "@umd-dit-ai-workspace/shared-ui";
import { Building2, Shield, User as UserIcon, Plus, Trash2, Edit2, X } from "lucide-react";

// src/api/api.ts
var globalApiInstance = null;
function initializeWorkspacesApi(api_instance) {
  globalApiInstance = api_instance;
  console.log("[Workspaces] API initialized with provided instance");
}
function getWorkspacesApi() {
  if (!globalApiInstance) {
    throw new Error("Workspaces API not initialized. Call initializeWorkspacesApi(apiInstance) first.");
  }
  return globalApiInstance;
}

// src/api/users-api.ts
var useUsersApi = () => {
  const logger2 = useLogger();
  const apiInstance = getWorkspacesApi();
  return {
    getAllUsers: async () => {
      logger2.debug("Fetching all users");
      const response = await apiInstance.get(`/admin/users`);
      return response.data;
    },
    getOrgUsers: async (org_id) => {
      logger2.debug("Fetching users for org:", org_id);
      const response = await apiInstance.get(`/admin/organizations/${org_id}/users`);
      return response.data;
    },
    createUser: async (user_id) => {
      logger2.debug("Creating user:", user_id);
      const response = await apiInstance.post(`/admin/users`, {
        user_id,
        is_global_admin: false
      });
      return response.data;
    },
    updateUser: async (data) => {
      logger2.debug("Updating user:", data.userId);
      const response = await apiInstance.put(`/admin/users/${data.userId}`, data);
      return response.data;
    },
    deleteUser: async (user_id) => {
      logger2.debug("Deleting user:", user_id);
      await apiInstance.delete(`/admin/users/${user_id}`);
    },
    getUser: async (user_id) => {
      logger2.debug("Fetching user profile:", user_id);
      const response = await apiInstance.get(`/admin/users/${user_id}`);
      return response.data;
    },
    getUserOrganizations: async (user_id) => {
      logger2.debug("Fetching user organizations:", user_id);
      const response = await apiInstance.get(`/admin/users/${user_id}/organizations`);
      return response.data;
    },
    getUserWorkspacesInOrg: async (user_id, org_id) => {
      logger2.debug("Fetching user workspaces in org:", { user_id, org_id });
      const response = await apiInstance.get(
        `/admin/users/${user_id}/organizations/${org_id}/workspaces`
      );
      return response.data;
    },
    addUserToOrg: async (org_id, user_id, role) => {
      logger2.debug("Adding user to organization:", { org_id, user_id, role });
      await apiInstance.post(`/admin/organizations/${org_id}/users`, { user_id, role });
    },
    removeUserFromOrg: async (org_id, user_id) => {
      logger2.debug("Removing user from organization:", { org_id, user_id });
      await apiInstance.delete(`/admin/organizations/${org_id}/users/${user_id}`);
    },
    updateUserOrgRole: async (org_id, user_id, role) => {
      logger2.debug("Updating user org role:", { org_id, user_id, role });
      await apiInstance.put(`/admin/organizations/${org_id}/users/${user_id}/role`, { role });
    },
    addUserToWorkspace: async (org_id, ws_id, user_id, role) => {
      logger2.debug("Adding user to workspace:", { org_id, ws_id, user_id, role });
      await apiInstance.post(`/admin/organizations/${org_id}/workspaces/${ws_id}/users`, { user_id, role });
    },
    removeUserFromWorkspace: async (org_id, ws_id, user_id) => {
      logger2.debug("Removing user from workspace:", { org_id, ws_id, user_id });
      await apiInstance.delete(`/admin/organizations/${org_id}/workspaces/${ws_id}/users/${user_id}`);
    },
    updateUserWorkspaceRole: async (org_id, ws_id, user_id, role) => {
      logger2.debug("Updating user workspace role:", { org_id, ws_id, user_id, role });
      await apiInstance.put(`/admin/organizations/${org_id}/workspaces/${ws_id}/users/${user_id}/role`, { role });
    },
    setUserImpersonation: async (impersonating_user_id) => {
      logger2.debug("Setting user impersonation:", { impersonating_user_id });
      const response = await apiInstance.put(`/admin/users/impersonation`, {
        impersonating_user_id
      });
      return response.data;
    }
  };
};
var fetchUserProfile = async () => {
  const apiInstance = getWorkspacesApi();
  try {
    const response = await apiInstance.get(`/user/profile`);
    return response.data;
  } catch (error) {
    console.error("[Workspaces API] Error fetching user profile:", error);
    throw error;
  }
};

// src/components/organizations/OrganizationMembersDialog.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var OrganizationMembersDialog = ({
  organization,
  open,
  onOpenChange
}) => {
  const usersApi = useUsersApi();
  const queryClient2 = useQueryClient();
  const { toast } = useToast();
  const [addingMemberMode, setAddingMemberMode] = useState2(false);
  const [selectedUserId, setSelectedUserId] = useState2("");
  const [selectedRole, setSelectedRole] = useState2("member");
  const [editingUserId, setEditingUserId] = useState2(null);
  const { data: members = [], isLoading } = useQuery({
    queryKey: ["org-members", organization?.orgId],
    queryFn: async () => {
      if (!organization?.orgId)
        return [];
      const users = await usersApi.getOrgUsers(organization.orgId);
      return users;
    },
    enabled: open && !!organization?.orgId
  });
  const { data: allUsers = [] } = useQuery({
    queryKey: ["all-users"],
    queryFn: () => usersApi.getAllUsers(),
    enabled: open && addingMemberMode
  });
  const availableUserOptions = useMemo(() => {
    const memberIds = new Set(members.map((m) => m.userId));
    return allUsers.filter((user) => !memberIds.has(user.userId)).map((user) => ({
      value: user.userId,
      label: user.userId
    }));
  }, [allUsers, members]);
  const getRoleBadgeVariant = (role) => {
    return role === "admin" ? "default" : "secondary";
  };
  const addMemberMutation = useMutation({
    mutationFn: ({ userId, role }) => {
      if (!organization?.orgId)
        throw new Error("No organization selected");
      return usersApi.addUserToOrg(organization.orgId, userId, role);
    },
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["org-members", organization?.orgId] });
      toast({ title: "Success", description: "Member added to organization" });
      setAddingMemberMode(false);
      setSelectedUserId("");
      setSelectedRole("member");
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add member",
        variant: "destructive"
      });
    }
  });
  const removeMemberMutation = useMutation({
    mutationFn: (userId) => {
      if (!organization?.orgId)
        throw new Error("No organization selected");
      return usersApi.removeUserFromOrg(organization.orgId, userId);
    },
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["org-members", organization?.orgId] });
      toast({ title: "Success", description: "Member removed from organization" });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to remove member",
        variant: "destructive"
      });
    }
  });
  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }) => {
      if (!organization?.orgId)
        throw new Error("No organization selected");
      return usersApi.updateUserOrgRole(organization.orgId, userId, role);
    },
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["org-members", organization?.orgId] });
      toast({ title: "Success", description: "Member role updated" });
      setEditingUserId(null);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update role",
        variant: "destructive"
      });
    }
  });
  if (!organization)
    return null;
  return /* @__PURE__ */ jsx3(Dialog2, { open, onOpenChange, children: /* @__PURE__ */ jsxs2(DialogContent2, { className: "max-w-2xl max-h-[85vh] overflow-y-auto", children: [
    /* @__PURE__ */ jsxs2(DialogHeader2, { children: [
      /* @__PURE__ */ jsxs2(DialogTitle2, { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx3(Building2, { className: "h-5 w-5" }),
        "Organization Members"
      ] }),
      /* @__PURE__ */ jsxs2(DialogDescription2, { children: [
        organization.name,
        " (",
        organization.orgId,
        ")"
      ] })
    ] }),
    isLoading ? /* @__PURE__ */ jsx3("div", { className: "text-center py-8", children: "Loading members..." }) : /* @__PURE__ */ jsx3("div", { className: "space-y-4", children: /* @__PURE__ */ jsxs2(Card, { children: [
      /* @__PURE__ */ jsx3(CardHeader, { children: /* @__PURE__ */ jsxs2("div", { className: "flex items-center justify-between", children: [
        /* @__PURE__ */ jsxs2("div", { children: [
          /* @__PURE__ */ jsxs2(CardTitle, { className: "flex items-center gap-2 text-lg", children: [
            /* @__PURE__ */ jsx3(UserIcon, { className: "h-4 w-4" }),
            "Members"
          ] }),
          /* @__PURE__ */ jsxs2("p", { className: "text-sm text-muted-foreground mt-1", children: [
            members.length,
            " member",
            members.length !== 1 ? "s" : ""
          ] })
        ] }),
        !addingMemberMode && /* @__PURE__ */ jsxs2(Button2, { size: "sm", onClick: () => setAddingMemberMode(true), children: [
          /* @__PURE__ */ jsx3(Plus, { className: "h-4 w-4 mr-1" }),
          "Add Member"
        ] })
      ] }) }),
      /* @__PURE__ */ jsxs2(CardContent, { children: [
        addingMemberMode && /* @__PURE__ */ jsxs2("div", { className: "mb-4 p-4 border rounded-md bg-accent/10 space-y-3", children: [
          /* @__PURE__ */ jsxs2("div", { className: "flex items-center justify-between", children: [
            /* @__PURE__ */ jsx3("h4", { className: "text-sm font-semibold", children: "Add Member" }),
            /* @__PURE__ */ jsx3(Button2, { size: "sm", variant: "ghost", onClick: () => {
              setAddingMemberMode(false);
              setSelectedUserId("");
            }, children: /* @__PURE__ */ jsx3(X, { className: "h-4 w-4" }) })
          ] }),
          /* @__PURE__ */ jsxs2("div", { className: "space-y-2", children: [
            /* @__PURE__ */ jsx3(
              SearchableSelect,
              {
                options: availableUserOptions,
                value: selectedUserId,
                onValueChange: setSelectedUserId,
                placeholder: "Select user",
                searchPlaceholder: "Search users...",
                emptyMessage: "No users available"
              }
            ),
            /* @__PURE__ */ jsxs2(Select, { value: selectedRole, onValueChange: setSelectedRole, children: [
              /* @__PURE__ */ jsx3(SelectTrigger, { children: /* @__PURE__ */ jsx3(SelectValue, { placeholder: "Select role" }) }),
              /* @__PURE__ */ jsxs2(SelectContent, { children: [
                /* @__PURE__ */ jsx3(SelectItem, { value: "member", children: "Member" }),
                /* @__PURE__ */ jsx3(SelectItem, { value: "admin", children: "Admin" })
              ] })
            ] }),
            /* @__PURE__ */ jsx3(
              Button2,
              {
                onClick: () => addMemberMutation.mutate({ userId: selectedUserId, role: selectedRole }),
                disabled: !selectedUserId || addMemberMutation.isPending,
                className: "w-full",
                children: addMemberMutation.isPending ? "Adding..." : "Add Member"
              }
            )
          ] })
        ] }),
        members.length === 0 ? /* @__PURE__ */ jsx3("p", { className: "text-sm text-muted-foreground", children: "No members in this organization" }) : /* @__PURE__ */ jsx3("div", { className: "space-y-2", children: members.map((member) => /* @__PURE__ */ jsxs2(
          "div",
          {
            className: "flex items-center justify-between p-3 rounded-md border bg-card",
            children: [
              /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-2 flex-1", children: [
                /* @__PURE__ */ jsx3(UserIcon, { className: "h-4 w-4 text-muted-foreground" }),
                /* @__PURE__ */ jsx3("span", { className: "font-medium", children: member.userId })
              ] }),
              editingUserId === member.userId ? /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsxs2(
                  Select,
                  {
                    value: member.role || "member",
                    onValueChange: (role) => updateRoleMutation.mutate({ userId: member.userId, role }),
                    children: [
                      /* @__PURE__ */ jsx3(SelectTrigger, { className: "w-32", children: /* @__PURE__ */ jsx3(SelectValue, {}) }),
                      /* @__PURE__ */ jsxs2(SelectContent, { children: [
                        /* @__PURE__ */ jsx3(SelectItem, { value: "member", children: "Member" }),
                        /* @__PURE__ */ jsx3(SelectItem, { value: "admin", children: "Admin" })
                      ] })
                    ]
                  }
                ),
                /* @__PURE__ */ jsx3(Button2, { size: "sm", variant: "ghost", onClick: () => setEditingUserId(null), children: /* @__PURE__ */ jsx3(X, { className: "h-4 w-4" }) })
              ] }) : /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsxs2(Badge, { variant: getRoleBadgeVariant(member.role || "member"), children: [
                  member.role === "admin" && /* @__PURE__ */ jsx3(Shield, { className: "h-3 w-3 mr-1" }),
                  member.role || "member"
                ] }),
                /* @__PURE__ */ jsx3(Button2, { size: "sm", variant: "ghost", onClick: () => setEditingUserId(member.userId), children: /* @__PURE__ */ jsx3(Edit2, { className: "h-3 w-3" }) }),
                /* @__PURE__ */ jsx3(
                  Button2,
                  {
                    size: "sm",
                    variant: "ghost",
                    onClick: () => removeMemberMutation.mutate(member.userId),
                    disabled: removeMemberMutation.isPending,
                    children: /* @__PURE__ */ jsx3(Trash2, { className: "h-3 w-3 text-destructive" })
                  }
                )
              ] })
            ]
          },
          member.userId
        )) })
      ] })
    ] }) })
  ] }) });
};

// src/components/organizations/OrganizationActionsDropdown.tsx
import { Fragment, jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
function OrganizationActionsDropdown({
  organization,
  onEdit,
  onDelete
}) {
  const [open, setOpen] = useState3(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState3(false);
  return /* @__PURE__ */ jsxs3(Fragment, { children: [
    /* @__PURE__ */ jsxs3(DropdownMenu, { open, onOpenChange: setOpen, children: [
      /* @__PURE__ */ jsx4(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsxs3(Button3, { variant: "ghost", size: "icon", children: [
        /* @__PURE__ */ jsx4(MoreHorizontal, { className: "h-4 w-4" }),
        /* @__PURE__ */ jsx4("span", { className: "sr-only", children: "Open menu" })
      ] }) }),
      /* @__PURE__ */ jsxs3(DropdownMenuContent, { align: "end", children: [
        /* @__PURE__ */ jsx4(DropdownMenuLabel, { children: "Organization Actions" }),
        /* @__PURE__ */ jsx4(DropdownMenuSeparator, {}),
        /* @__PURE__ */ jsxs3(DropdownMenuItem, { onClick: () => {
          onEdit(organization);
          setOpen(false);
        }, children: [
          /* @__PURE__ */ jsx4(Edit, { className: "mr-2 h-4 w-4" }),
          "Edit Organization"
        ] }),
        /* @__PURE__ */ jsxs3(DropdownMenuItem, { onClick: () => {
          setMembersDialogOpen(true);
          setOpen(false);
        }, children: [
          /* @__PURE__ */ jsx4(Users, { className: "mr-2 h-4 w-4" }),
          "Edit Members"
        ] }),
        onDelete && /* @__PURE__ */ jsxs3(Fragment, { children: [
          /* @__PURE__ */ jsx4(DropdownMenuSeparator, {}),
          /* @__PURE__ */ jsxs3(
            DropdownMenuItem,
            {
              className: "text-destructive",
              onClick: () => {
                onDelete(organization);
                setOpen(false);
              },
              children: [
                /* @__PURE__ */ jsx4(Trash22, { className: "mr-2 h-4 w-4" }),
                "Delete Organization"
              ]
            }
          )
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx4(
      OrganizationMembersDialog,
      {
        organization,
        open: membersDialogOpen,
        onOpenChange: setMembersDialogOpen
      }
    )
  ] });
}

// src/components/workspaces/EditWorkspaceDialog.tsx
import { useState as useState4, useEffect as useEffect2 } from "react";
import {
  Dialog as Dialog3,
  DialogContent as DialogContent3,
  DialogDescription as DialogDescription3,
  DialogHeader as DialogHeader3,
  DialogTitle as DialogTitle3,
  DialogFooter as DialogFooter2
} from "@umd-dit-ai-workspace/shared-ui";
import { Button as Button4 } from "@umd-dit-ai-workspace/shared-ui";
import { Label as Label2 } from "@umd-dit-ai-workspace/shared-ui";
import { Input as Input2 } from "@umd-dit-ai-workspace/shared-ui";
import { Textarea as Textarea2 } from "@umd-dit-ai-workspace/shared-ui";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
function EditWorkspaceDialog({
  workspace,
  open,
  onOpenChange,
  onSave,
  isPending
}) {
  const isCreateMode = !workspace;
  const [formData, setFormData] = useState4({
    wsId: "",
    name: "",
    description: ""
  });
  useEffect2(() => {
    if (workspace) {
      setFormData({
        wsId: workspace.wsId,
        name: workspace.name,
        description: workspace.description || ""
      });
    } else {
      setFormData({
        wsId: "",
        name: "",
        description: ""
      });
    }
  }, [workspace, open]);
  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.name.trim()) {
      if (isCreateMode) {
        onSave(null, {
          ws_id: formData.wsId.trim(),
          name: formData.name.trim(),
          description: formData.description.trim() || void 0
        });
      } else {
        onSave(workspace, {
          name: formData.name.trim(),
          description: formData.description.trim() || void 0
        });
      }
    }
  };
  return /* @__PURE__ */ jsx5(Dialog3, { open, onOpenChange, children: /* @__PURE__ */ jsxs4(DialogContent3, { children: [
    /* @__PURE__ */ jsxs4(DialogHeader3, { children: [
      /* @__PURE__ */ jsx5(DialogTitle3, { children: isCreateMode ? "Create Workspace" : "Edit Workspace" }),
      /* @__PURE__ */ jsx5(DialogDescription3, { children: isCreateMode ? "Fill in the details to create a new workspace." : "Update the workspace details below." })
    ] }),
    /* @__PURE__ */ jsxs4("form", { onSubmit: handleSubmit, children: [
      /* @__PURE__ */ jsxs4("div", { className: "space-y-4 py-4", children: [
        /* @__PURE__ */ jsxs4("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsx5(Label2, { htmlFor: "ws_id", children: "Workspace ID" }),
          /* @__PURE__ */ jsx5(
            Input2,
            {
              id: "ws_id",
              value: formData.wsId,
              onChange: isCreateMode ? (e) => setFormData((prev) => ({ ...prev, wsId: e.target.value })) : void 0,
              placeholder: isCreateMode ? "workspace-id" : void 0,
              disabled: !isCreateMode,
              className: !isCreateMode ? "bg-muted" : "",
              required: isCreateMode
            }
          ),
          isCreateMode && /* @__PURE__ */ jsx5("p", { className: "text-sm text-muted-foreground", children: "Use lowercase letters, numbers, and hyphens only" })
        ] }),
        /* @__PURE__ */ jsxs4("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsx5(Label2, { htmlFor: "name", children: "Workspace Name" }),
          /* @__PURE__ */ jsx5(
            Input2,
            {
              id: "name",
              value: formData.name,
              onChange: (e) => setFormData((prev) => ({ ...prev, name: e.target.value })),
              placeholder: "Workspace name",
              required: true
            }
          )
        ] }),
        /* @__PURE__ */ jsxs4("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsx5(Label2, { htmlFor: "description", children: "Description (Optional)" }),
          /* @__PURE__ */ jsx5(
            Textarea2,
            {
              id: "description",
              value: formData.description,
              onChange: (e) => setFormData((prev) => ({ ...prev, description: e.target.value })),
              placeholder: "Describe the workspace purpose",
              rows: 3
            }
          )
        ] })
      ] }),
      /* @__PURE__ */ jsxs4(DialogFooter2, { children: [
        /* @__PURE__ */ jsx5(
          Button4,
          {
            type: "button",
            variant: "outline",
            onClick: () => onOpenChange(false),
            disabled: isPending,
            children: "Cancel"
          }
        ),
        /* @__PURE__ */ jsx5(
          Button4,
          {
            type: "submit",
            disabled: isPending || isCreateMode && (!formData.wsId || !formData.name),
            children: isCreateMode ? "Create Workspace" : "Save Changes"
          }
        )
      ] })
    ] })
  ] }) });
}

// src/components/workspaces/DeleteWorkspaceDialog.tsx
import { useState as useState5, useEffect as useEffect3 } from "react";
import {
  Dialog as Dialog4,
  DialogContent as DialogContent4,
  DialogHeader as DialogHeader4,
  DialogTitle as DialogTitle4,
  DialogFooter as DialogFooter3,
  DialogDescription as DialogDescription4
} from "@umd-dit-ai-workspace/shared-ui";
import { Button as Button5 } from "@umd-dit-ai-workspace/shared-ui";
import { Label as Label3 } from "@umd-dit-ai-workspace/shared-ui";
import { Input as Input3 } from "@umd-dit-ai-workspace/shared-ui";
import { AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@umd-dit-ai-workspace/shared-ui";
import { jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
function DeleteWorkspaceDialog({
  workspace,
  open,
  onOpenChange,
  onDelete,
  isPending
}) {
  const [confirmationText, setConfirmationText] = useState5("");
  const [isConfirmed, setIsConfirmed] = useState5(false);
  useEffect3(() => {
    setConfirmationText("");
    setIsConfirmed(false);
  }, [open]);
  useEffect3(() => {
    if (workspace && confirmationText === workspace.name) {
      setIsConfirmed(true);
    } else {
      setIsConfirmed(false);
    }
  }, [confirmationText, workspace]);
  const handleSubmit = (e) => {
    e.preventDefault();
    if (workspace && isConfirmed) {
      onDelete(workspace);
    }
  };
  return /* @__PURE__ */ jsx6(Dialog4, { open, onOpenChange, children: /* @__PURE__ */ jsxs5(DialogContent4, { className: "sm:max-w-[425px]", children: [
    /* @__PURE__ */ jsxs5(DialogHeader4, { children: [
      /* @__PURE__ */ jsx6(DialogTitle4, { className: "text-destructive", children: "Delete Workspace" }),
      /* @__PURE__ */ jsx6(DialogDescription4, { children: "This action cannot be undone. All workspace data will be permanently deleted." })
    ] }),
    workspace && /* @__PURE__ */ jsxs5("form", { onSubmit: handleSubmit, children: [
      /* @__PURE__ */ jsxs5("div", { className: "space-y-4 py-4", children: [
        /* @__PURE__ */ jsxs5(Alert, { variant: "destructive", className: "bg-red-50 border-red-200 text-red-800 dark:bg-red-950 dark:border-red-800 dark:text-red-200", children: [
          /* @__PURE__ */ jsx6(AlertTriangle, { className: "h-4 w-4 text-red-600 dark:text-red-400" }),
          /* @__PURE__ */ jsxs5(AlertDescription, { children: [
            "You are about to delete ",
            /* @__PURE__ */ jsx6("strong", { children: workspace.name }),
            ". This will delete all bills, files, and data associated with this workspace."
          ] })
        ] }),
        /* @__PURE__ */ jsxs5("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsxs5(Label3, { htmlFor: "confirmation", className: "text-red-600 dark:text-red-400", children: [
            "Type ",
            /* @__PURE__ */ jsx6("strong", { children: workspace.name }),
            " to confirm deletion"
          ] }),
          /* @__PURE__ */ jsx6(
            Input3,
            {
              id: "confirmation",
              value: confirmationText,
              onChange: (e) => setConfirmationText(e.target.value),
              placeholder: `Type "${workspace.name}" to confirm`,
              className: "border-red-200 focus-visible:ring-red-400",
              autoComplete: "off"
            }
          )
        ] })
      ] }),
      /* @__PURE__ */ jsxs5(DialogFooter3, { children: [
        /* @__PURE__ */ jsx6(
          Button5,
          {
            type: "button",
            variant: "outline",
            onClick: () => onOpenChange(false),
            disabled: isPending,
            children: "Cancel"
          }
        ),
        /* @__PURE__ */ jsx6(
          Button5,
          {
            type: "submit",
            variant: "destructive",
            disabled: !isConfirmed || isPending,
            children: isPending ? "Deleting..." : "Delete Workspace"
          }
        )
      ] })
    ] })
  ] }) });
}

// src/components/workspaces/WorkspaceActionsDropdown.tsx
import { useState as useState7 } from "react";
import {
  DropdownMenu as DropdownMenu2,
  DropdownMenuContent as DropdownMenuContent2,
  DropdownMenuItem as DropdownMenuItem2,
  DropdownMenuLabel as DropdownMenuLabel2,
  DropdownMenuSeparator as DropdownMenuSeparator2,
  DropdownMenuTrigger as DropdownMenuTrigger2
} from "@umd-dit-ai-workspace/shared-ui";
import { Button as Button7 } from "@umd-dit-ai-workspace/shared-ui";
import { MoreHorizontal as MoreHorizontal2, Edit as Edit3, Trash2 as Trash24, Users as Users2 } from "lucide-react";

// src/components/workspaces/WorkspaceMembersDialog.tsx
import { useState as useState6, useMemo as useMemo2 } from "react";
import { useQuery as useQuery2, useMutation as useMutation2, useQueryClient as useQueryClient2 } from "@tanstack/react-query";
import {
  Dialog as Dialog5,
  DialogContent as DialogContent5,
  DialogDescription as DialogDescription5,
  DialogHeader as DialogHeader5,
  DialogTitle as DialogTitle5,
  Badge as Badge2,
  Button as Button6,
  Card as Card2,
  CardContent as CardContent2,
  CardHeader as CardHeader2,
  CardTitle as CardTitle2,
  Select as Select2,
  SelectContent as SelectContent2,
  SelectItem as SelectItem2,
  SelectTrigger as SelectTrigger2,
  SelectValue as SelectValue2,
  useToast as useToast2,
  SearchableSelect as SearchableSelect2
} from "@umd-dit-ai-workspace/shared-ui";
import { FolderOpen, Shield as Shield2, User as UserIcon2, Plus as Plus2, Trash2 as Trash23, Edit2 as Edit22, X as X2 } from "lucide-react";

// src/api/workspace-api.ts
var useWorkspaceApi = () => {
  const logger2 = useLogger();
  const apiInstance = getWorkspacesApi();
  return {
    // Regular user methods
    fetchOrgWorkspaces: async (org_id) => {
      if (!org_id) {
        throw new Error("Organization ID is required");
      }
      logger2.debug("Fetching accessible workspaces for org:", org_id);
      try {
        const response = await apiInstance.get(`/org/${org_id}/workspaces`);
        logger2.debug("Fetched workspaces:", response.data.length);
        return response.data;
      } catch (error) {
        logger2.error("Error fetching workspaces:", error);
        throw error;
      }
    },
    getWorkspace: async (org_id, ws_id) => {
      logger2.debug("Fetching workspace:", org_id, ws_id);
      const response = await apiInstance.get(`/org/${org_id}/ws/${ws_id}`);
      logger2.debug("Fetched workspace:", response.data);
      return response.data;
    },
    // Admin methods (merged from admin-workspace-api.ts)
    adminListWorkspaces: async (org_id) => {
      logger2.debug("Admin: Fetching workspaces for organization:", org_id);
      const response = await apiInstance.get(
        `/org/${org_id}/workspaces`
      );
      logger2.debug("Admin: Fetched workspaces:", response.data);
      return response.data;
    },
    adminCreateWorkspace: async (org_id, data) => {
      logger2.debug("Admin: Creating workspace for organization:", org_id, data);
      const response = await apiInstance.post(
        `/admin/org/${org_id}/workspaces`,
        data
      );
      logger2.debug("Admin: Created workspace:", response.data);
      return response.data;
    },
    adminGetWorkspace: async (org_id, ws_id) => {
      logger2.debug("Admin: Fetching workspace:", org_id, ws_id);
      const response = await apiInstance.get(
        `/org/${org_id}/ws/${ws_id}`
      );
      logger2.debug("Admin: Fetched workspace:", response.data);
      return response.data;
    },
    adminUpdateWorkspace: async (org_id, ws_id, data) => {
      logger2.debug("Admin: Updating workspace:", org_id, ws_id, data);
      const response = await apiInstance.put(
        `/admin/org/${org_id}/workspaces/${ws_id}`,
        data
      );
      logger2.debug("Admin: Updated workspace:", response.data);
      return response.data;
    },
    adminDeleteWorkspace: async (org_id, ws_id) => {
      logger2.debug("Admin: Deleting workspace:", org_id, ws_id);
      await apiInstance.delete(`/admin/org/${org_id}/workspaces/${ws_id}`);
      logger2.debug("Admin: Deleted workspace");
    },
    getWorkspaceUsers: async (org_id, ws_id) => {
      logger2.debug("Fetching users for workspace:", { org_id, ws_id });
      const response = await apiInstance.get(
        `/admin/organizations/${org_id}/workspaces/${ws_id}/users`
      );
      logger2.debug("Fetched workspace users:", response.data);
      return response.data;
    }
  };
};
var fetchOrgWorkspaces = async (org_id) => {
  if (!org_id) {
    throw new Error("Organization ID is required");
  }
  const apiInstance = getWorkspacesApi();
  try {
    const response = await apiInstance.get(`/org/${org_id}/workspaces`);
    return response.data;
  } catch (error) {
    console.error("[Workspaces API] Error fetching workspaces:", error);
    throw error;
  }
};

// src/components/workspaces/WorkspaceMembersDialog.tsx
import { jsx as jsx7, jsxs as jsxs6 } from "react/jsx-runtime";
var WorkspaceMembersDialog = ({
  workspace,
  open,
  onOpenChange
}) => {
  const usersApi = useUsersApi();
  const workspaceApi = useWorkspaceApi();
  const queryClient2 = useQueryClient2();
  const { toast } = useToast2();
  const [addingMemberMode, setAddingMemberMode] = useState6(false);
  const [selectedUserId, setSelectedUserId] = useState6("");
  const [selectedRole, setSelectedRole] = useState6("member");
  const [editingUserId, setEditingUserId] = useState6(null);
  const { data: members = [], isLoading } = useQuery2({
    queryKey: ["workspace-members", workspace?.orgId, workspace?.wsId],
    queryFn: async () => {
      if (!workspace?.orgId || !workspace?.wsId)
        return [];
      const users = await workspaceApi.getWorkspaceUsers(workspace.orgId, workspace.wsId);
      return users;
    },
    enabled: open && !!workspace?.orgId && !!workspace?.wsId
  });
  const { data: allUsers = [] } = useQuery2({
    queryKey: ["all-users"],
    queryFn: () => usersApi.getAllUsers(),
    enabled: open && addingMemberMode
  });
  const availableUserOptions = useMemo2(() => {
    const memberIds = new Set(members.map((m) => m.userId));
    return allUsers.filter((user) => !memberIds.has(user.userId)).map((user) => ({
      value: user.userId,
      label: user.userId
    }));
  }, [allUsers, members]);
  const getRoleBadgeVariant = (role) => {
    return role === "admin" ? "default" : "secondary";
  };
  const addMemberMutation = useMutation2({
    mutationFn: ({ userId, role }) => {
      if (!workspace?.orgId || !workspace?.wsId)
        throw new Error("No workspace selected");
      return usersApi.addUserToWorkspace(workspace.orgId, workspace.wsId, userId, role);
    },
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["workspace-members", workspace?.orgId, workspace?.wsId] });
      toast({ title: "Success", description: "Member added to workspace" });
      setAddingMemberMode(false);
      setSelectedUserId("");
      setSelectedRole("member");
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add member",
        variant: "destructive"
      });
    }
  });
  const removeMemberMutation = useMutation2({
    mutationFn: (userId) => {
      if (!workspace?.orgId || !workspace?.wsId)
        throw new Error("No workspace selected");
      return usersApi.removeUserFromWorkspace(workspace.orgId, workspace.wsId, userId);
    },
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["workspace-members", workspace?.orgId, workspace?.wsId] });
      toast({ title: "Success", description: "Member removed from workspace" });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to remove member",
        variant: "destructive"
      });
    }
  });
  const updateRoleMutation = useMutation2({
    mutationFn: ({ userId, role }) => {
      if (!workspace?.orgId || !workspace?.wsId)
        throw new Error("No workspace selected");
      return usersApi.updateUserWorkspaceRole(workspace.orgId, workspace.wsId, userId, role);
    },
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["workspace-members", workspace?.orgId, workspace?.wsId] });
      toast({ title: "Success", description: "Member role updated" });
      setEditingUserId(null);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update role",
        variant: "destructive"
      });
    }
  });
  if (!workspace)
    return null;
  const workspaceName = "name" in workspace ? workspace.name : workspace.wsId;
  return /* @__PURE__ */ jsx7(Dialog5, { open, onOpenChange, children: /* @__PURE__ */ jsxs6(DialogContent5, { className: "max-w-2xl max-h-[85vh] overflow-y-auto", children: [
    /* @__PURE__ */ jsxs6(DialogHeader5, { children: [
      /* @__PURE__ */ jsxs6(DialogTitle5, { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx7(FolderOpen, { className: "h-5 w-5" }),
        "Workspace Members"
      ] }),
      /* @__PURE__ */ jsxs6(DialogDescription5, { children: [
        workspaceName,
        " (",
        workspace.orgId,
        "/",
        workspace.wsId,
        ")"
      ] })
    ] }),
    isLoading ? /* @__PURE__ */ jsx7("div", { className: "text-center py-8", children: "Loading members..." }) : /* @__PURE__ */ jsx7("div", { className: "space-y-4", children: /* @__PURE__ */ jsxs6(Card2, { children: [
      /* @__PURE__ */ jsx7(CardHeader2, { children: /* @__PURE__ */ jsxs6("div", { className: "flex items-center justify-between", children: [
        /* @__PURE__ */ jsxs6("div", { children: [
          /* @__PURE__ */ jsxs6(CardTitle2, { className: "flex items-center gap-2 text-lg", children: [
            /* @__PURE__ */ jsx7(UserIcon2, { className: "h-4 w-4" }),
            "Members"
          ] }),
          /* @__PURE__ */ jsxs6("p", { className: "text-sm text-muted-foreground mt-1", children: [
            members.length,
            " member",
            members.length !== 1 ? "s" : ""
          ] })
        ] }),
        !addingMemberMode && /* @__PURE__ */ jsxs6(Button6, { size: "sm", onClick: () => setAddingMemberMode(true), children: [
          /* @__PURE__ */ jsx7(Plus2, { className: "h-4 w-4 mr-1" }),
          "Add Member"
        ] })
      ] }) }),
      /* @__PURE__ */ jsxs6(CardContent2, { children: [
        addingMemberMode && /* @__PURE__ */ jsxs6("div", { className: "mb-4 p-4 border rounded-md bg-accent/10 space-y-3", children: [
          /* @__PURE__ */ jsxs6("div", { className: "flex items-center justify-between", children: [
            /* @__PURE__ */ jsx7("h4", { className: "text-sm font-semibold", children: "Add Member" }),
            /* @__PURE__ */ jsx7(Button6, { size: "sm", variant: "ghost", onClick: () => {
              setAddingMemberMode(false);
              setSelectedUserId("");
            }, children: /* @__PURE__ */ jsx7(X2, { className: "h-4 w-4" }) })
          ] }),
          /* @__PURE__ */ jsxs6("div", { className: "space-y-2", children: [
            /* @__PURE__ */ jsx7(
              SearchableSelect2,
              {
                options: availableUserOptions,
                value: selectedUserId,
                onValueChange: setSelectedUserId,
                placeholder: "Select user",
                searchPlaceholder: "Search users...",
                emptyMessage: "No users available"
              }
            ),
            /* @__PURE__ */ jsxs6(Select2, { value: selectedRole, onValueChange: setSelectedRole, children: [
              /* @__PURE__ */ jsx7(SelectTrigger2, { children: /* @__PURE__ */ jsx7(SelectValue2, { placeholder: "Select role" }) }),
              /* @__PURE__ */ jsxs6(SelectContent2, { children: [
                /* @__PURE__ */ jsx7(SelectItem2, { value: "member", children: "Member" }),
                /* @__PURE__ */ jsx7(SelectItem2, { value: "admin", children: "Admin" })
              ] })
            ] }),
            /* @__PURE__ */ jsx7(
              Button6,
              {
                onClick: () => addMemberMutation.mutate({ userId: selectedUserId, role: selectedRole }),
                disabled: !selectedUserId || addMemberMutation.isPending,
                className: "w-full",
                children: addMemberMutation.isPending ? "Adding..." : "Add Member"
              }
            )
          ] })
        ] }),
        members.length === 0 ? /* @__PURE__ */ jsx7("p", { className: "text-sm text-muted-foreground", children: "No members in this workspace" }) : /* @__PURE__ */ jsx7("div", { className: "space-y-2", children: members.map((member) => /* @__PURE__ */ jsxs6(
          "div",
          {
            className: "flex items-center justify-between p-3 rounded-md border bg-card",
            children: [
              /* @__PURE__ */ jsxs6("div", { className: "flex items-center gap-2 flex-1", children: [
                /* @__PURE__ */ jsx7(UserIcon2, { className: "h-4 w-4 text-muted-foreground" }),
                /* @__PURE__ */ jsx7("span", { className: "font-medium", children: member.userId })
              ] }),
              editingUserId === member.userId ? /* @__PURE__ */ jsxs6("div", { className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsxs6(
                  Select2,
                  {
                    value: member.role || "member",
                    onValueChange: (role) => updateRoleMutation.mutate({ userId: member.userId, role }),
                    children: [
                      /* @__PURE__ */ jsx7(SelectTrigger2, { className: "w-32", children: /* @__PURE__ */ jsx7(SelectValue2, {}) }),
                      /* @__PURE__ */ jsxs6(SelectContent2, { children: [
                        /* @__PURE__ */ jsx7(SelectItem2, { value: "member", children: "Member" }),
                        /* @__PURE__ */ jsx7(SelectItem2, { value: "admin", children: "Admin" })
                      ] })
                    ]
                  }
                ),
                /* @__PURE__ */ jsx7(Button6, { size: "sm", variant: "ghost", onClick: () => setEditingUserId(null), children: /* @__PURE__ */ jsx7(X2, { className: "h-4 w-4" }) })
              ] }) : /* @__PURE__ */ jsxs6("div", { className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsxs6(Badge2, { variant: getRoleBadgeVariant(member.role || "member"), children: [
                  member.role === "admin" && /* @__PURE__ */ jsx7(Shield2, { className: "h-3 w-3 mr-1" }),
                  member.role || "member"
                ] }),
                /* @__PURE__ */ jsx7(Button6, { size: "sm", variant: "ghost", onClick: () => setEditingUserId(member.userId), children: /* @__PURE__ */ jsx7(Edit22, { className: "h-3 w-3" }) }),
                /* @__PURE__ */ jsx7(
                  Button6,
                  {
                    size: "sm",
                    variant: "ghost",
                    onClick: () => removeMemberMutation.mutate(member.userId),
                    disabled: removeMemberMutation.isPending,
                    children: /* @__PURE__ */ jsx7(Trash23, { className: "h-3 w-3 text-destructive" })
                  }
                )
              ] })
            ]
          },
          member.userId
        )) })
      ] })
    ] }) })
  ] }) });
};

// src/components/workspaces/WorkspaceActionsDropdown.tsx
import { Fragment as Fragment2, jsx as jsx8, jsxs as jsxs7 } from "react/jsx-runtime";
function WorkspaceActionsDropdown({
  workspace,
  onEdit,
  onDelete
}) {
  const [open, setOpen] = useState7(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState7(false);
  return /* @__PURE__ */ jsxs7(Fragment2, { children: [
    /* @__PURE__ */ jsxs7(DropdownMenu2, { open, onOpenChange: setOpen, children: [
      /* @__PURE__ */ jsx8(DropdownMenuTrigger2, { asChild: true, children: /* @__PURE__ */ jsxs7(Button7, { variant: "ghost", size: "icon", children: [
        /* @__PURE__ */ jsx8(MoreHorizontal2, { className: "h-4 w-4" }),
        /* @__PURE__ */ jsx8("span", { className: "sr-only", children: "Open menu" })
      ] }) }),
      /* @__PURE__ */ jsxs7(DropdownMenuContent2, { align: "end", children: [
        /* @__PURE__ */ jsx8(DropdownMenuLabel2, { children: "Workspace Actions" }),
        /* @__PURE__ */ jsx8(DropdownMenuSeparator2, {}),
        onEdit && /* @__PURE__ */ jsxs7(DropdownMenuItem2, { onClick: () => {
          onEdit(workspace);
          setOpen(false);
        }, children: [
          /* @__PURE__ */ jsx8(Edit3, { className: "mr-2 h-4 w-4" }),
          "Edit Name/Description"
        ] }),
        /* @__PURE__ */ jsxs7(DropdownMenuItem2, { onClick: () => {
          setMembersDialogOpen(true);
          setOpen(false);
        }, children: [
          /* @__PURE__ */ jsx8(Users2, { className: "mr-2 h-4 w-4" }),
          "Edit Members"
        ] }),
        onDelete && /* @__PURE__ */ jsxs7(Fragment2, { children: [
          /* @__PURE__ */ jsx8(DropdownMenuSeparator2, {}),
          /* @__PURE__ */ jsxs7(
            DropdownMenuItem2,
            {
              className: "text-destructive",
              onClick: () => {
                onDelete(workspace);
                setOpen(false);
              },
              children: [
                /* @__PURE__ */ jsx8(Trash24, { className: "mr-2 h-4 w-4" }),
                "Delete Workspace"
              ]
            }
          )
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx8(
      WorkspaceMembersDialog,
      {
        workspace,
        open: membersDialogOpen,
        onOpenChange: setMembersDialogOpen
      }
    )
  ] });
}

// src/api/organization-api.ts
var useOrganizationApi = () => {
  const logger2 = useLogger();
  const apiInstance = getWorkspacesApi();
  return {
    listOrganizations: async () => {
      logger2.debug("Fetching organizations");
      const response = await apiInstance.get(
        `/organizations`
      );
      logger2.debug("Fetched organizations:", response.data);
      return response.data;
    },
    createOrganization: async (data) => {
      logger2.debug("Creating organization:", data);
      const response = await apiInstance.post(
        `/admin/organizations`,
        data
      );
      logger2.debug("Created organization:", response.data);
      return response.data;
    },
    getOrganization: async (org_id) => {
      logger2.debug("Fetching organization:", org_id);
      const response = await apiInstance.get(
        `/organizations/${org_id}`
      );
      logger2.debug("Fetched organization:", response.data);
      return response.data;
    },
    updateOrganization: async (org_id, data) => {
      logger2.debug("Updating organization:", org_id, data);
      const response = await apiInstance.put(
        `/admin/organizations/${org_id}`,
        data
      );
      logger2.debug("Updated organization:", response.data);
      return response.data;
    },
    deleteOrganization: async (org_id) => {
      logger2.debug("Deleting organization:", org_id);
      await apiInstance.delete(`/admin/organizations/${org_id}`);
      logger2.debug("Deleted organization");
    }
  };
};
var fetchOrganizations = async () => {
  const apiInstance = getWorkspacesApi();
  try {
    const response = await apiInstance.get(`/organizations`);
    return response.data;
  } catch (error) {
    console.error("[Workspaces API] Error fetching organizations:", error);
    throw error;
  }
};

// src/components/views/GlobalAdminView.tsx
import { cn } from "@umd-dit-ai-workspace/shared-ui";
import { Building2 as Building22, FolderOpen as FolderOpen2, Plus as Plus3, Search } from "lucide-react";
import { useToast as useToast3 } from "@umd-dit-ai-workspace/shared-ui";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from "@umd-dit-ai-workspace/shared-ui";
import { Fragment as Fragment3, jsx as jsx9, jsxs as jsxs8 } from "react/jsx-runtime";
var GlobalAdminView = ({
  selectedOrganizationId,
  selectedWorkspaceId,
  className,
  onNavigate
}) => {
  const [editOrgDialog, setEditOrgDialog] = useState8(false);
  const [editWsDialog, setEditWsDialog] = useState8(false);
  const [deleteWsDialog, setDeleteWsDialog] = useState8(false);
  const [selectedOrg, setSelectedOrg] = useState8(null);
  const [selectedWs, setSelectedWs] = useState8(null);
  const [searchQuery, setSearchQuery] = useState8("");
  const queryClient2 = useQueryClient3();
  const { toast } = useToast3();
  const organizationApi = useOrganizationApi();
  const workspaceApi = useWorkspaceApi();
  const { data: organizations, isLoading: orgsLoading } = useQuery3({
    queryKey: ["organizations"],
    queryFn: () => organizationApi.listOrganizations()
  });
  const { data: workspaces, isLoading: wsLoading } = useQuery3({
    queryKey: ["workspaces", selectedOrganizationId],
    queryFn: () => workspaceApi.adminListWorkspaces(selectedOrganizationId),
    enabled: !!selectedOrganizationId
  });
  const { data: currentOrganization } = useQuery3({
    queryKey: ["organization", selectedOrganizationId],
    queryFn: () => organizationApi.getOrganization(selectedOrganizationId),
    enabled: !!selectedOrganizationId
  });
  const { data: currentWorkspace } = useQuery3({
    queryKey: ["workspace", selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => workspaceApi.adminGetWorkspace(selectedOrganizationId, selectedWorkspaceId),
    enabled: !!selectedOrganizationId && !!selectedWorkspaceId
  });
  const { data: workspace } = useQuery3({
    queryKey: ["workspace", selectedWorkspaceId],
    queryFn: () => workspaceApi.adminGetWorkspace(selectedOrganizationId, selectedWorkspaceId),
    enabled: !!selectedOrganizationId && !!selectedWorkspaceId
  });
  const createOrganizationMutation = useMutation3({
    mutationFn: (data) => organizationApi.createOrganization(data),
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["organizations"] });
      toast({
        title: "Organization created",
        description: "The organization has been created successfully."
      });
      setEditOrgDialog(false);
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to create organization";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });
  const updateOrganizationMutation = useMutation3({
    mutationFn: ({ orgId, data }) => organizationApi.updateOrganization(orgId, data),
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["organizations"] });
      toast({
        title: "Organization updated",
        description: "The organization has been updated successfully."
      });
      setEditOrgDialog(false);
      setSelectedOrg(null);
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to update organization";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });
  const deleteOrganizationMutation = useMutation3({
    mutationFn: (orgId) => organizationApi.deleteOrganization(orgId),
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["organizations"] });
      toast({
        title: "Organization deleted",
        description: "The organization has been deleted successfully."
      });
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to delete organization";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });
  const createWorkspaceMutation = useMutation3({
    mutationFn: (data) => workspaceApi.adminCreateWorkspace(selectedOrganizationId, data),
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["workspaces", selectedOrganizationId] });
      toast({
        title: "Workspace created",
        description: "The workspace has been created successfully."
      });
      setEditWsDialog(false);
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to create workspace";
      toast({
        title: "Error creating workspace",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });
  const updateWorkspaceMutation = useMutation3({
    mutationFn: ({ wsId, data }) => workspaceApi.adminUpdateWorkspace(selectedOrganizationId, wsId, data),
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["workspaces", selectedOrganizationId] });
      queryClient2.invalidateQueries({ queryKey: ["workspace", selectedWorkspaceId] });
      toast({
        title: "Workspace updated",
        description: "The workspace has been updated successfully."
      });
      setEditWsDialog(false);
      setSelectedWs(null);
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to update workspace";
      toast({
        title: "Error updating workspace",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });
  const deleteWorkspaceMutation = useMutation3({
    mutationFn: (wsId) => workspaceApi.adminDeleteWorkspace(selectedOrganizationId, wsId),
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["workspaces", selectedOrganizationId] });
      toast({
        title: "Workspace deleted",
        description: "The workspace has been deleted successfully."
      });
      setDeleteWsDialog(false);
      setSelectedWs(null);
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to delete workspace";
      toast({
        title: "Error deleting workspace",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });
  const filteredWorkspaces = useMemo3(() => {
    if (!workspaces)
      return [];
    if (!searchQuery)
      return workspaces;
    const query = searchQuery.toLowerCase();
    return workspaces.filter(
      (ws) => ws.name.toLowerCase().includes(query) || ws.wsId.toLowerCase().includes(query) || ws.description?.toLowerCase().includes(query)
    );
  }, [workspaces, searchQuery]);
  if (!selectedOrganizationId) {
    return /* @__PURE__ */ jsxs8("div", { className: cn("global-admin-view", className), children: [
      /* @__PURE__ */ jsxs8("div", { className: "space-y-6", children: [
        /* @__PURE__ */ jsx9(Breadcrumb, { children: /* @__PURE__ */ jsx9(BreadcrumbList, { children: /* @__PURE__ */ jsx9(BreadcrumbItem, { children: /* @__PURE__ */ jsx9(BreadcrumbPage, { children: "Organizations" }) }) }) }),
        /* @__PURE__ */ jsxs8("div", { className: "flex items-center justify-between", children: [
          /* @__PURE__ */ jsxs8("div", { children: [
            /* @__PURE__ */ jsx9("h2", { className: "text-2xl font-bold", children: "Organizations" }),
            /* @__PURE__ */ jsx9("p", { className: "text-muted-foreground", children: "Manage all organizations in the system" })
          ] }),
          /* @__PURE__ */ jsxs8(Button8, { onClick: () => setEditOrgDialog(true), children: [
            /* @__PURE__ */ jsx9(Plus3, { className: "mr-2 h-4 w-4" }),
            "Create Organization"
          ] })
        ] }),
        orgsLoading ? /* @__PURE__ */ jsx9("div", { className: "text-center py-8", children: "Loading organizations..." }) : /* @__PURE__ */ jsx9("div", { className: "grid gap-4", children: organizations?.map((org) => /* @__PURE__ */ jsxs8(
          Card3,
          {
            className: "cursor-pointer hover:bg-accent transition-colors",
            onClick: () => onNavigate?.(org.orgId),
            children: [
              /* @__PURE__ */ jsx9(CardHeader3, { children: /* @__PURE__ */ jsxs8("div", { className: "flex items-center justify-between", children: [
                /* @__PURE__ */ jsxs8("div", { children: [
                  /* @__PURE__ */ jsxs8(CardTitle3, { className: "flex items-center gap-2", children: [
                    /* @__PURE__ */ jsx9(Building22, { className: "h-5 w-5" }),
                    org.name
                  ] }),
                  /* @__PURE__ */ jsx9(CardDescription, { children: org.description || "No description" })
                ] }),
                /* @__PURE__ */ jsx9("div", { onClick: (e) => e.stopPropagation(), children: /* @__PURE__ */ jsx9(
                  OrganizationActionsDropdown,
                  {
                    organization: org,
                    onEdit: () => {
                      setSelectedOrg(org);
                      setEditOrgDialog(true);
                    },
                    onDelete: () => {
                      deleteOrganizationMutation.mutate(org.orgId);
                    }
                  }
                ) })
              ] }) }),
              /* @__PURE__ */ jsx9(CardContent3, { children: /* @__PURE__ */ jsxs8("p", { className: "text-sm text-muted-foreground", children: [
                "ID: ",
                org.orgId
              ] }) })
            ]
          },
          org.orgId
        )) })
      ] }),
      /* @__PURE__ */ jsx9(
        EditOrganizationDialog,
        {
          organization: selectedOrg,
          open: editOrgDialog,
          onOpenChange: (open) => {
            setEditOrgDialog(open);
            if (!open) {
              setSelectedOrg(null);
            }
          },
          onSave: (org, data) => {
            if (org) {
              updateOrganizationMutation.mutate({ orgId: org.orgId, data });
            } else {
              createOrganizationMutation.mutate(data);
            }
          },
          isPending: createOrganizationMutation.isPending || updateOrganizationMutation.isPending
        }
      )
    ] });
  }
  if (!selectedWorkspaceId) {
    return /* @__PURE__ */ jsxs8("div", { className: cn("global-admin-view", className), children: [
      /* @__PURE__ */ jsxs8("div", { className: "space-y-6", children: [
        /* @__PURE__ */ jsx9(Breadcrumb, { children: /* @__PURE__ */ jsxs8(BreadcrumbList, { children: [
          /* @__PURE__ */ jsx9(BreadcrumbItem, { children: /* @__PURE__ */ jsx9(
            BreadcrumbLink,
            {
              className: "cursor-pointer",
              onClick: () => onNavigate?.(),
              children: "Organizations"
            }
          ) }),
          /* @__PURE__ */ jsx9(BreadcrumbSeparator, {}),
          /* @__PURE__ */ jsx9(BreadcrumbItem, { children: /* @__PURE__ */ jsx9(BreadcrumbPage, { children: currentOrganization?.name || selectedOrganizationId }) })
        ] }) }),
        /* @__PURE__ */ jsxs8("div", { className: "flex items-center justify-between", children: [
          /* @__PURE__ */ jsxs8("div", { children: [
            /* @__PURE__ */ jsx9("h2", { className: "text-2xl font-bold", children: "Workspaces" }),
            /* @__PURE__ */ jsx9("p", { className: "text-muted-foreground", children: "Manage workspaces in this organization" })
          ] }),
          /* @__PURE__ */ jsxs8(Button8, { onClick: () => setEditWsDialog(true), children: [
            /* @__PURE__ */ jsx9(Plus3, { className: "mr-2 h-4 w-4" }),
            "Create Workspace"
          ] })
        ] }),
        /* @__PURE__ */ jsx9("div", { className: "w-full max-w-md", children: /* @__PURE__ */ jsxs8("div", { className: "flex items-center gap-2 px-3 py-1.5 text-sm border rounded-md bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2", children: [
          /* @__PURE__ */ jsx9(Search, { className: "h-3.5 w-3.5 text-muted-foreground shrink-0" }),
          /* @__PURE__ */ jsx9(
            "input",
            {
              type: "search",
              placeholder: "Search workspaces...",
              value: searchQuery,
              onChange: (e) => setSearchQuery(e.target.value),
              className: "flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
            }
          )
        ] }) }),
        wsLoading ? /* @__PURE__ */ jsx9("div", { className: "text-center py-8", children: "Loading workspaces..." }) : filteredWorkspaces.length === 0 ? /* @__PURE__ */ jsx9("div", { className: "text-center py-8 text-muted-foreground", children: searchQuery ? "No workspaces found matching your search." : "No workspaces in this organization." }) : /* @__PURE__ */ jsx9("div", { className: "rounded-md border", children: /* @__PURE__ */ jsxs8(Table, { children: [
          /* @__PURE__ */ jsx9(TableHeader, { children: /* @__PURE__ */ jsxs8(TableRow, { children: [
            /* @__PURE__ */ jsx9(TableHead, { className: "text-xs", children: "Name" }),
            /* @__PURE__ */ jsx9(TableHead, { className: "text-xs", children: "Description" }),
            /* @__PURE__ */ jsx9(TableHead, { className: "text-xs w-[100px]", children: "Actions" })
          ] }) }),
          /* @__PURE__ */ jsx9(TableBody, { children: filteredWorkspaces.map((ws) => /* @__PURE__ */ jsxs8(TableRow, { children: [
            /* @__PURE__ */ jsx9(TableCell, { className: "font-medium", children: /* @__PURE__ */ jsxs8("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx9(FolderOpen2, { className: "h-4 w-4 text-muted-foreground" }),
              /* @__PURE__ */ jsxs8("div", { children: [
                /* @__PURE__ */ jsx9("div", { children: ws.name }),
                /* @__PURE__ */ jsx9("div", { className: "text-xs text-muted-foreground", children: ws.wsId })
              ] })
            ] }) }),
            /* @__PURE__ */ jsx9(TableCell, { className: "text-sm text-muted-foreground", children: ws.description || "-" }),
            /* @__PURE__ */ jsx9(TableCell, { children: /* @__PURE__ */ jsx9(
              WorkspaceActionsDropdown,
              {
                workspace: ws,
                onEdit: () => {
                  setSelectedWs(ws);
                  setEditWsDialog(true);
                },
                onDelete: () => {
                  setSelectedWs(ws);
                  setDeleteWsDialog(true);
                }
              }
            ) })
          ] }, ws.wsId)) })
        ] }) })
      ] }),
      /* @__PURE__ */ jsx9(
        EditWorkspaceDialog,
        {
          workspace: selectedWs,
          open: editWsDialog,
          onOpenChange: (open) => {
            setEditWsDialog(open);
            if (!open) {
              setSelectedWs(null);
            }
          },
          onSave: (ws, data) => {
            if (ws) {
              updateWorkspaceMutation.mutate({ wsId: ws.wsId, data });
            } else {
              createWorkspaceMutation.mutate(data);
            }
          },
          isPending: createWorkspaceMutation.isPending || updateWorkspaceMutation.isPending
        }
      ),
      selectedWs && /* @__PURE__ */ jsx9(Fragment3, { children: /* @__PURE__ */ jsx9(
        DeleteWorkspaceDialog,
        {
          workspace: selectedWs,
          open: deleteWsDialog,
          onOpenChange: (open) => {
            setDeleteWsDialog(open);
            if (!open) {
              setSelectedWs(null);
            }
          },
          onDelete: (ws) => {
            deleteWorkspaceMutation.mutate(ws.wsId);
          },
          isPending: deleteWorkspaceMutation.isPending
        }
      ) })
    ] });
  }
  return /* @__PURE__ */ jsx9("div", { className: cn("global-admin-view", className), children: /* @__PURE__ */ jsxs8("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsx9(Breadcrumb, { children: /* @__PURE__ */ jsxs8(BreadcrumbList, { children: [
      /* @__PURE__ */ jsx9(BreadcrumbItem, { children: /* @__PURE__ */ jsx9(
        BreadcrumbLink,
        {
          className: "cursor-pointer",
          onClick: () => onNavigate?.(),
          children: "Organizations"
        }
      ) }),
      /* @__PURE__ */ jsx9(BreadcrumbSeparator, {}),
      /* @__PURE__ */ jsx9(BreadcrumbItem, { children: /* @__PURE__ */ jsx9(
        BreadcrumbLink,
        {
          className: "cursor-pointer",
          onClick: () => onNavigate?.(selectedOrganizationId),
          children: currentOrganization?.name || selectedOrganizationId
        }
      ) }),
      /* @__PURE__ */ jsx9(BreadcrumbSeparator, {}),
      /* @__PURE__ */ jsx9(BreadcrumbItem, { children: /* @__PURE__ */ jsx9(BreadcrumbPage, { children: currentWorkspace?.name || workspace?.name || selectedWorkspaceId }) })
    ] }) }),
    /* @__PURE__ */ jsxs8("div", { children: [
      /* @__PURE__ */ jsx9("h2", { className: "text-2xl font-bold", children: "Workspace Management" }),
      /* @__PURE__ */ jsx9("p", { className: "text-muted-foreground", children: "Full administrative access to manage this workspace" })
    ] }),
    workspace && /* @__PURE__ */ jsxs8(Card3, { children: [
      /* @__PURE__ */ jsx9(CardHeader3, { children: /* @__PURE__ */ jsxs8("div", { className: "flex items-center justify-between", children: [
        /* @__PURE__ */ jsxs8("div", { children: [
          /* @__PURE__ */ jsxs8(CardTitle3, { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx9(FolderOpen2, { className: "h-5 w-5" }),
            workspace.name
          ] }),
          /* @__PURE__ */ jsx9(CardDescription, { children: workspace.description || "No description" })
        ] }),
        /* @__PURE__ */ jsx9(
          WorkspaceActionsDropdown,
          {
            workspace,
            onEdit: () => setEditWsDialog(true),
            onDelete: () => setDeleteWsDialog(true)
          }
        )
      ] }) }),
      /* @__PURE__ */ jsx9(CardContent3, { children: /* @__PURE__ */ jsx9("div", { className: "grid gap-4", children: /* @__PURE__ */ jsxs8("div", { className: "flex items-center justify-between", children: [
        /* @__PURE__ */ jsxs8("div", { children: [
          /* @__PURE__ */ jsx9("p", { className: "font-medium", children: "Workspace Actions" }),
          /* @__PURE__ */ jsx9("p", { className: "text-sm text-muted-foreground", children: "Manage all aspects of this workspace" })
        ] }),
        /* @__PURE__ */ jsx9("div", { className: "space-x-2" })
      ] }) }) })
    ] })
  ] }) });
};

// src/components/views/WorkspaceAdminView.tsx
import { useState as useState9, useMemo as useMemo4, useEffect as useEffect4 } from "react";
import { useQuery as useQuery4, useQueries } from "@tanstack/react-query";
import { Card as Card4, CardContent as CardContent4, CardDescription as CardDescription2, CardHeader as CardHeader4, CardTitle as CardTitle4 } from "@umd-dit-ai-workspace/shared-ui";

// src/components/workspaces/WorkspaceList.tsx
import {
  Table as Table2,
  TableBody as TableBody2,
  TableCell as TableCell2,
  TableHead as TableHead2,
  TableHeader as TableHeader2,
  TableRow as TableRow2
} from "@umd-dit-ai-workspace/shared-ui";
import { FolderOpen as FolderOpen3 } from "lucide-react";
import { jsx as jsx10, jsxs as jsxs9 } from "react/jsx-runtime";
var WorkspaceList = ({
  workspaces,
  onEdit,
  onDelete,
  showActions = true,
  allowDelete = true,
  allowEdit = true
}) => {
  if (workspaces.length === 0) {
    return /* @__PURE__ */ jsx10("div", { className: "text-center py-8 text-muted-foreground", children: "No workspaces found." });
  }
  return /* @__PURE__ */ jsx10("div", { className: "rounded-md border", children: /* @__PURE__ */ jsxs9(Table2, { children: [
    /* @__PURE__ */ jsx10(TableHeader2, { children: /* @__PURE__ */ jsxs9(TableRow2, { children: [
      /* @__PURE__ */ jsx10(TableHead2, { className: "text-xs", children: "Name" }),
      /* @__PURE__ */ jsx10(TableHead2, { className: "text-xs", children: "Data Source" }),
      /* @__PURE__ */ jsx10(TableHead2, { className: "text-xs", children: "Organization" }),
      showActions && /* @__PURE__ */ jsx10(TableHead2, { className: "text-xs w-[100px]", children: "Actions" })
    ] }) }),
    /* @__PURE__ */ jsx10(TableBody2, { children: workspaces.map((ws) => /* @__PURE__ */ jsxs9(TableRow2, { children: [
      /* @__PURE__ */ jsx10(TableCell2, { className: "font-medium", children: /* @__PURE__ */ jsxs9("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx10(FolderOpen3, { className: "h-4 w-4 text-muted-foreground" }),
        /* @__PURE__ */ jsxs9("div", { children: [
          /* @__PURE__ */ jsx10("div", { children: ws.name || ws.wsId }),
          /* @__PURE__ */ jsx10("div", { className: "text-xs text-muted-foreground", children: ws.wsId })
        ] })
      ] }) }),
      /* @__PURE__ */ jsx10(TableCell2, { children: ws.dataSource || "-" }),
      /* @__PURE__ */ jsx10(TableCell2, { className: "text-sm text-muted-foreground", children: ws.orgName || ws.orgId }),
      showActions && /* @__PURE__ */ jsx10(TableCell2, { children: /* @__PURE__ */ jsx10(
        WorkspaceActionsDropdown,
        {
          workspace: ws,
          onEdit: allowEdit ? onEdit : void 0,
          onDelete: allowDelete ? onDelete : void 0
        }
      ) })
    ] }, ws.wsId)) })
  ] }) });
};

// src/components/workspaces/WorkspaceSearchBar.tsx
import { Search as Search2 } from "lucide-react";
import { jsx as jsx11, jsxs as jsxs10 } from "react/jsx-runtime";
var WorkspaceSearchBar = ({
  value,
  onChange,
  placeholder = "Search workspaces..."
}) => {
  return /* @__PURE__ */ jsx11("div", { className: "w-full max-w-md", children: /* @__PURE__ */ jsxs10("div", { className: "flex items-center gap-2 px-3 py-1.5 text-sm border rounded-md bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2", children: [
    /* @__PURE__ */ jsx11(Search2, { className: "h-3.5 w-3.5 text-muted-foreground shrink-0" }),
    /* @__PURE__ */ jsx11(
      "input",
      {
        type: "search",
        placeholder,
        value,
        onChange: (e) => onChange(e.target.value),
        className: "flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
      }
    )
  ] }) });
};

// src/components/views/WorkspaceAdminView.tsx
import { cn as cn2 } from "@umd-dit-ai-workspace/shared-ui";
import { Shield as Shield3 } from "lucide-react";
import { jsx as jsx12, jsxs as jsxs11 } from "react/jsx-runtime";
var WorkspaceAdminView = ({
  selectedOrganizationId,
  selectedWorkspaceId,
  className,
  onWorkspaceSelect,
  debug = false
}) => {
  const [searchQuery, setSearchQuery] = useState9("");
  const workspaceApi = useWorkspaceApi();
  const organizationApi = useOrganizationApi();
  const { data: organizations = [] } = useQuery4({
    queryKey: ["organizations"],
    queryFn: () => organizationApi.listOrganizations()
  });
  const workspaceQueries = useQueries({
    queries: organizations.map((org) => ({
      queryKey: ["workspaces", org.orgId],
      queryFn: () => workspaceApi.fetchOrgWorkspaces(org.orgId),
      staleTime: 5 * 60 * 1e3,
      gcTime: 10 * 60 * 1e3
    }))
  });
  const isLoading = workspaceQueries.some((query) => query.isLoading);
  const allWorkspaces = useMemo4(() => {
    return workspaceQueries.filter((query) => query.data).flatMap((query) => query.data);
  }, [workspaceQueries]);
  const filteredWorkspaces = useMemo4(() => {
    if (!allWorkspaces)
      return [];
    if (!searchQuery)
      return allWorkspaces;
    const query = searchQuery.toLowerCase();
    return allWorkspaces.filter(
      (ws) => ws.name?.toLowerCase().includes(query) || ws.wsId.toLowerCase().includes(query) || ws.dataSource?.toLowerCase().includes(query)
    );
  }, [allWorkspaces, searchQuery]);
  useEffect4(() => {
    if (!selectedWorkspaceId && allWorkspaces && allWorkspaces.length > 0 && onWorkspaceSelect) {
      const firstWorkspace = allWorkspaces[0];
      logger.debug(debug, "Auto-selecting first admin workspace:", firstWorkspace);
      onWorkspaceSelect(firstWorkspace.orgId, firstWorkspace.wsId);
    }
  }, [selectedWorkspaceId, allWorkspaces, onWorkspaceSelect, debug]);
  const currentWorkspace = allWorkspaces?.find(
    (ws) => ws.orgId === selectedOrganizationId && ws.wsId === selectedWorkspaceId
  );
  if (isLoading) {
    return /* @__PURE__ */ jsx12("div", { className: "flex items-center justify-center h-64", children: /* @__PURE__ */ jsx12("div", { className: "text-muted-foreground", children: "Loading workspaces..." }) });
  }
  if (!allWorkspaces || allWorkspaces.length === 0) {
    return /* @__PURE__ */ jsx12("div", { className: cn2("workspace-admin-view", className), children: /* @__PURE__ */ jsxs11("div", { className: "flex flex-col items-center justify-center h-64 text-center", children: [
      /* @__PURE__ */ jsx12(Shield3, { className: "h-12 w-12 mb-4 text-muted-foreground opacity-50" }),
      /* @__PURE__ */ jsx12("h3", { className: "text-lg font-semibold mb-2", children: "No Admin Access" }),
      /* @__PURE__ */ jsx12("p", { className: "text-sm text-muted-foreground max-w-md", children: "You don't have admin privileges for any workspaces. Contact your system administrator if you believe you should have access." })
    ] }) });
  }
  return /* @__PURE__ */ jsx12("div", { className: cn2("workspace-admin-view", className), children: /* @__PURE__ */ jsxs11("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsxs11("div", { children: [
      /* @__PURE__ */ jsx12("h2", { className: "text-2xl font-bold", children: "Workspace Administration" }),
      /* @__PURE__ */ jsx12("p", { className: "text-muted-foreground", children: "Manage members in workspaces where you have admin privileges" })
    ] }),
    currentWorkspace && /* @__PURE__ */ jsx12(Card4, { children: /* @__PURE__ */ jsxs11(CardHeader4, { children: [
      /* @__PURE__ */ jsx12(CardTitle4, { children: "Current Workspace" }),
      /* @__PURE__ */ jsxs11(CardDescription2, { children: [
        currentWorkspace.name,
        " \u2022 ",
        currentWorkspace.dataSource
      ] })
    ] }) }),
    /* @__PURE__ */ jsx12(
      WorkspaceSearchBar,
      {
        value: searchQuery,
        onChange: setSearchQuery,
        placeholder: "Search your admin workspaces..."
      }
    ),
    /* @__PURE__ */ jsxs11("div", { children: [
      /* @__PURE__ */ jsx12("h3", { className: "text-lg font-semibold mb-4", children: "Your Admin Workspaces" }),
      filteredWorkspaces.length === 0 && searchQuery ? /* @__PURE__ */ jsx12("div", { className: "text-center py-8 text-muted-foreground", children: "No workspaces found matching your search." }) : /* @__PURE__ */ jsx12(
        WorkspaceList,
        {
          workspaces: filteredWorkspaces,
          showActions: true,
          allowDelete: false,
          allowEdit: false
        }
      )
    ] }),
    /* @__PURE__ */ jsxs11(Card4, { className: "border-warning", children: [
      /* @__PURE__ */ jsx12(CardHeader4, { children: /* @__PURE__ */ jsx12(CardTitle4, { className: "text-sm", children: "Workspace Admin Permissions" }) }),
      /* @__PURE__ */ jsx12(CardContent4, { children: /* @__PURE__ */ jsx12("p", { className: "text-sm text-muted-foreground", children: "As a workspace admin, you have access to your workspace data and settings. Workspace membership is managed through Grouper. Contact your global administrator to create new workspaces or manage organization settings." }) })
    ] })
  ] }) });
};

// src/components/views/WorkspaceUserView.tsx
import { useQuery as useQuery5 } from "@tanstack/react-query";
import { Card as Card5, CardContent as CardContent5, CardDescription as CardDescription3, CardHeader as CardHeader5, CardTitle as CardTitle5 } from "@umd-dit-ai-workspace/shared-ui";
import { cn as cn3 } from "@umd-dit-ai-workspace/shared-ui";
import { Building2 as Building23, Settings, Users as Users3 } from "lucide-react";
import { jsx as jsx13, jsxs as jsxs12 } from "react/jsx-runtime";
var WorkspaceUserView = ({
  selectedOrganizationId,
  selectedWorkspaceId,
  className
}) => {
  const workspaceApi = useWorkspaceApi();
  const organizationApi = useOrganizationApi();
  const { data: workspace, isLoading: workspaceLoading } = useQuery5({
    queryKey: ["workspace", selectedWorkspaceId],
    queryFn: () => workspaceApi.getWorkspace(selectedOrganizationId, selectedWorkspaceId),
    enabled: !!selectedWorkspaceId && !!selectedOrganizationId
  });
  const { data: organization, isLoading: orgLoading } = useQuery5({
    queryKey: ["organization", selectedOrganizationId],
    queryFn: () => organizationApi.getOrganization(selectedOrganizationId),
    enabled: !!selectedOrganizationId
  });
  if (workspaceLoading || orgLoading) {
    return /* @__PURE__ */ jsx13("div", { className: "flex items-center justify-center h-64", children: /* @__PURE__ */ jsx13("div", { className: "text-muted-foreground", children: "Loading workspace data..." }) });
  }
  if (!workspace || !organization) {
    return /* @__PURE__ */ jsx13("div", { className: "flex items-center justify-center h-64", children: /* @__PURE__ */ jsx13("div", { className: "text-destructive", children: "Unable to load workspace information" }) });
  }
  return /* @__PURE__ */ jsx13("div", { className: cn3("workspace-user-view", className), children: /* @__PURE__ */ jsxs12("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsxs12("div", { children: [
      /* @__PURE__ */ jsx13("h2", { className: "text-2xl font-bold", children: "Workspace Overview" }),
      /* @__PURE__ */ jsx13("p", { className: "text-muted-foreground", children: "View workspace and organization information" })
    ] }),
    /* @__PURE__ */ jsxs12("div", { className: "grid gap-4 md:grid-cols-2", children: [
      /* @__PURE__ */ jsxs12(Card5, { children: [
        /* @__PURE__ */ jsxs12(CardHeader5, { children: [
          /* @__PURE__ */ jsxs12(CardTitle5, { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx13(Building23, { className: "h-5 w-5" }),
            "Organization"
          ] }),
          /* @__PURE__ */ jsx13(CardDescription3, { children: "Your organization details" })
        ] }),
        /* @__PURE__ */ jsx13(CardContent5, { children: /* @__PURE__ */ jsxs12("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsxs12("div", { children: [
            /* @__PURE__ */ jsx13("span", { className: "font-medium", children: "Name:" }),
            " ",
            organization.name
          ] }),
          organization.description && /* @__PURE__ */ jsxs12("div", { children: [
            /* @__PURE__ */ jsx13("span", { className: "font-medium", children: "Description:" }),
            " ",
            organization.description
          ] })
        ] }) })
      ] }),
      /* @__PURE__ */ jsxs12(Card5, { children: [
        /* @__PURE__ */ jsxs12(CardHeader5, { children: [
          /* @__PURE__ */ jsxs12(CardTitle5, { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx13(Settings, { className: "h-5 w-5" }),
            "Workspace"
          ] }),
          /* @__PURE__ */ jsx13(CardDescription3, { children: "Your current workspace" })
        ] }),
        /* @__PURE__ */ jsx13(CardContent5, { children: /* @__PURE__ */ jsxs12("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsxs12("div", { children: [
            /* @__PURE__ */ jsx13("span", { className: "font-medium", children: "Name:" }),
            " ",
            workspace.name
          ] }),
          workspace.description && /* @__PURE__ */ jsxs12("div", { children: [
            /* @__PURE__ */ jsx13("span", { className: "font-medium", children: "Description:" }),
            " ",
            workspace.description
          ] }),
          /* @__PURE__ */ jsxs12("div", { children: [
            /* @__PURE__ */ jsx13("span", { className: "font-medium", children: "Data Source:" }),
            " ",
            workspace.dataSource
          ] })
        ] }) })
      ] })
    ] }),
    /* @__PURE__ */ jsxs12(Card5, { children: [
      /* @__PURE__ */ jsxs12(CardHeader5, { children: [
        /* @__PURE__ */ jsxs12(CardTitle5, { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx13(Users3, { className: "h-5 w-5" }),
          "Members"
        ] }),
        /* @__PURE__ */ jsx13(CardDescription3, { children: "Workspace membership managed via Grouper" })
      ] }),
      /* @__PURE__ */ jsx13(CardContent5, { children: /* @__PURE__ */ jsx13("div", { children: /* @__PURE__ */ jsx13("p", { className: "text-sm text-muted-foreground", children: "Workspace membership is managed through Grouper entitlements. Contact your administrator to request access changes." }) }) })
    ] }),
    /* @__PURE__ */ jsxs12(Card5, { className: "border-muted", children: [
      /* @__PURE__ */ jsx13(CardHeader5, { children: /* @__PURE__ */ jsx13(CardTitle5, { children: "Your Access" }) }),
      /* @__PURE__ */ jsx13(CardContent5, { children: /* @__PURE__ */ jsx13("p", { className: "text-sm text-muted-foreground", children: "You have read-only access to this workspace. You can view workspace information and see who has access, but you cannot make changes. Contact your workspace administrator if you need additional permissions." }) })
    ] })
  ] }) });
};

// src/WorkspacesComponent.tsx
import { jsx as jsx14, jsxs as jsxs13 } from "react/jsx-runtime";
var queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false
    }
  }
});
var WorkspacesComponent = ({
  app_id,
  viewType = "user",
  selectedOrganizationId,
  selectedWorkspaceId,
  debug = false,
  onOrganizationSelect,
  onWorkspaceSelect
}) => {
  console.log(`[WorkspacesComponent] Using global API instance for app: ${app_id}`);
  logger.debug(debug, "Initializing with:", {
    app_id,
    viewType
  });
  if (!app_id) {
    throw new Error("Application ID is required. Please provide app_id prop.");
  }
  const handleNavigate = React6.useCallback((targetOrganizationId, targetWorkspaceId) => {
    logger.debug(debug, "Breadcrumb navigation:", { targetOrganizationId, targetWorkspaceId });
    if (!targetOrganizationId) {
      onOrganizationSelect?.("");
    } else if (!targetWorkspaceId) {
      onOrganizationSelect?.(targetOrganizationId);
    } else {
      onWorkspaceSelect?.(targetOrganizationId, targetWorkspaceId);
    }
  }, [debug, onOrganizationSelect, onWorkspaceSelect]);
  return /* @__PURE__ */ jsx14(QueryClientProvider, { client: queryClient, children: /* @__PURE__ */ jsx14(AppConfigProvider, { app_id, debug, children: /* @__PURE__ */ jsxs13("div", { className: "workspaces-component", children: [
    viewType === "global-admin" && /* @__PURE__ */ jsx14(
      GlobalAdminView,
      {
        selectedOrganizationId,
        selectedWorkspaceId,
        onNavigate: handleNavigate
      }
    ),
    viewType === "workspace-admin" && /* @__PURE__ */ jsx14(
      WorkspaceAdminView,
      {
        app_id,
        selectedOrganizationId,
        selectedWorkspaceId,
        onWorkspaceSelect,
        debug
      }
    ),
    viewType === "user" && (selectedWorkspaceId ? /* @__PURE__ */ jsx14(
      WorkspaceUserView,
      {
        app_id,
        selectedOrganizationId,
        selectedWorkspaceId
      }
    ) : /* @__PURE__ */ jsx14("div", { className: "text-center py-8", children: "Please select a workspace to view" }))
  ] }) }) });
};

// src/WorkspaceSelector.tsx
import { useState as useState10 } from "react";
import { cn as cn4 } from "@umd-dit-ai-workspace/shared-ui";

// src/components/WorkspaceSwitcher.tsx
import {
  Button as Button9,
  DropdownMenu as DropdownMenu3,
  DropdownMenuContent as DropdownMenuContent3,
  DropdownMenuItem as DropdownMenuItem3,
  DropdownMenuTrigger as DropdownMenuTrigger3
} from "@umd-dit-ai-workspace/shared-ui";
import { ChevronDown, FolderOpen as FolderOpen4 } from "lucide-react";
import { jsx as jsx15, jsxs as jsxs14 } from "react/jsx-runtime";
function WorkspaceSwitcher({
  accessibleWorkspaces = [],
  selectedOrganizationId,
  selectedWorkspaceId,
  selectWorkspace,
  isLoading = false
}) {
  const currentWorkspace = accessibleWorkspaces.find(
    (ws) => ws.orgId === selectedOrganizationId && ws.wsId === selectedWorkspaceId
  );
  const handleWorkspaceSelect = (workspace) => {
    if (workspace.orgId !== selectedOrganizationId || workspace.wsId !== selectedWorkspaceId) {
      selectWorkspace(workspace.orgId, workspace.wsId);
    }
  };
  const formatOrgName = (orgId) => {
    const cleanedId = orgId.replace(/^ORG#/, "");
    return cleanedId.replace(/[-_]/g, " ").split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
  };
  return /* @__PURE__ */ jsxs14(DropdownMenu3, { children: [
    /* @__PURE__ */ jsx15(DropdownMenuTrigger3, { asChild: true, children: /* @__PURE__ */ jsxs14(Button9, { variant: "outline", className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx15(FolderOpen4, { className: "h-4 w-4" }),
      /* @__PURE__ */ jsx15("span", { className: "max-w-[150px] truncate", children: isLoading ? "Loading..." : currentWorkspace?.name || "Select Workspace" }),
      /* @__PURE__ */ jsx15(ChevronDown, { className: "h-4 w-4 opacity-50" })
    ] }) }),
    /* @__PURE__ */ jsx15(DropdownMenuContent3, { align: "end", className: "w-[240px]", children: isLoading ? /* @__PURE__ */ jsx15(DropdownMenuItem3, { disabled: true, children: "Loading workspaces..." }) : accessibleWorkspaces.length > 0 ? accessibleWorkspaces.map((workspace) => /* @__PURE__ */ jsx15(
      DropdownMenuItem3,
      {
        onClick: () => handleWorkspaceSelect(workspace),
        className: workspace.orgId === selectedOrganizationId && workspace.wsId === selectedWorkspaceId ? "bg-accent" : "",
        children: /* @__PURE__ */ jsxs14("div", { className: "flex flex-col w-full", children: [
          /* @__PURE__ */ jsx15("span", { className: "font-medium", children: workspace.name || `Workspace ${workspace.wsId}` }),
          /* @__PURE__ */ jsx15("span", { className: "text-xs text-muted-foreground", children: workspace.orgName || formatOrgName(workspace.orgId) })
        ] })
      },
      `${workspace.orgId}-${workspace.wsId}`
    )) : /* @__PURE__ */ jsx15(DropdownMenuItem3, { disabled: true, className: "opacity-50 cursor-not-allowed", children: /* @__PURE__ */ jsxs14("div", { className: "flex flex-col w-full", children: [
      /* @__PURE__ */ jsx15("span", { className: "font-medium", children: "No workspaces available" }),
      /* @__PURE__ */ jsx15("span", { className: "text-xs text-muted-foreground", children: "Please contact an administrator" })
    ] }) }) })
  ] });
}

// src/hooks/use-workspaces.ts
import { useQuery as useQuery6, useQueryClient as useQueryClient4 } from "@tanstack/react-query";
var workspacesQueryKey = (orgId) => ["workspaces", orgId];
function useOrgWorkspaces(orgId) {
  const workspaceApi = useWorkspaceApi();
  return useQuery6({
    queryKey: orgId ? workspacesQueryKey(orgId) : ["workspaces", "none"],
    queryFn: () => {
      if (!orgId) {
        throw new Error("Organization ID is required");
      }
      return workspaceApi.fetchOrgWorkspaces(orgId);
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1e3,
    // 5 minutes
    gcTime: 10 * 60 * 1e3
    // 10 minutes
  });
}
function usePrefetchOrgWorkspaces() {
  const queryClient2 = useQueryClient4();
  const workspaceApi = useWorkspaceApi();
  return (orgId) => {
    return queryClient2.prefetchQuery({
      queryKey: ["workspaces", orgId],
      queryFn: () => workspaceApi.fetchOrgWorkspaces(orgId),
      staleTime: 5 * 60 * 1e3
    });
  };
}
function useOrgWorkspacesCache(orgId) {
  const queryClient2 = useQueryClient4();
  return queryClient2.getQueryData(["workspaces", orgId]);
}

// src/WorkspaceSelector.tsx
import { jsx as jsx16 } from "react/jsx-runtime";
var WorkspaceSelectorInternal = ({
  selectedOrganizationId,
  selectedWorkspaceId,
  onWorkspaceSelect,
  refreshOnSelect = false,
  className
}) => {
  const { data: accessibleWorkspaces = [], isLoading } = useOrgWorkspaces(selectedOrganizationId);
  const [internalSelection, setInternalSelection] = useState10({
    orgId: selectedOrganizationId || "",
    wsId: selectedWorkspaceId || ""
  });
  const handleWorkspaceSelect = (orgId, wsId) => {
    if (orgId !== selectedOrganizationId || wsId !== selectedWorkspaceId) {
      if (onWorkspaceSelect) {
        onWorkspaceSelect(orgId, wsId);
      } else {
        setInternalSelection({ orgId, wsId });
      }
      if (refreshOnSelect) {
        window.location.href = window.location.pathname.split("/").slice(0, 2).join("/") || "/";
      }
    }
  };
  const currentOrgId = selectedOrganizationId || internalSelection.orgId;
  const currentWsId = selectedWorkspaceId || internalSelection.wsId;
  return /* @__PURE__ */ jsx16("div", { className: cn4("workspace-selector-component", className), children: /* @__PURE__ */ jsx16(
    WorkspaceSwitcher,
    {
      accessibleWorkspaces,
      selectedOrganizationId: currentOrgId,
      selectedWorkspaceId: currentWsId,
      selectWorkspace: handleWorkspaceSelect,
      isLoading
    }
  ) });
};
var WorkspaceSelector = ({
  app_id,
  debug = false,
  ...props
}) => {
  return /* @__PURE__ */ jsx16(AppConfigProvider, { app_id, debug, children: /* @__PURE__ */ jsx16(WorkspaceSelectorInternal, { ...props }) });
};

// src/OrganizationSelector.tsx
import { useState as useState11 } from "react";
import { cn as cn5 } from "@umd-dit-ai-workspace/shared-ui";

// src/components/OrganizationDropdown.tsx
import { ChevronDown as ChevronDown2, Building2 as Building24 } from "lucide-react";
import {
  Button as Button10,
  DropdownMenu as DropdownMenu4,
  DropdownMenuContent as DropdownMenuContent4,
  DropdownMenuItem as DropdownMenuItem4,
  DropdownMenuTrigger as DropdownMenuTrigger4
} from "@umd-dit-ai-workspace/shared-ui";
import { jsx as jsx17, jsxs as jsxs15 } from "react/jsx-runtime";
var OrganizationDropdown = ({
  accessibleOrganizations,
  selectedOrganizationId,
  selectOrganization,
  disabled = false
}) => {
  const selectedOrg = accessibleOrganizations.find(
    (org) => org.orgId === selectedOrganizationId
  );
  if (accessibleOrganizations.length === 0) {
    return /* @__PURE__ */ jsx17(
      Button10,
      {
        variant: "outline",
        className: "w-[240px] justify-between",
        disabled: true,
        children: /* @__PURE__ */ jsxs15("span", { className: "flex items-center gap-2 min-w-0", children: [
          /* @__PURE__ */ jsx17(Building24, { className: "h-4 w-4 flex-shrink-0" }),
          /* @__PURE__ */ jsx17("span", { className: "truncate", children: "No organizations available" })
        ] })
      }
    );
  }
  if (accessibleOrganizations.length === 1 && selectedOrg) {
    return /* @__PURE__ */ jsx17(
      Button10,
      {
        variant: "outline",
        className: "w-[240px] justify-between",
        disabled: true,
        children: /* @__PURE__ */ jsxs15("span", { className: "flex items-center gap-2 min-w-0", children: [
          /* @__PURE__ */ jsx17(Building24, { className: "h-4 w-4 flex-shrink-0" }),
          /* @__PURE__ */ jsx17("span", { className: "truncate", children: selectedOrg.name })
        ] })
      }
    );
  }
  return /* @__PURE__ */ jsxs15(DropdownMenu4, { children: [
    /* @__PURE__ */ jsx17(DropdownMenuTrigger4, { asChild: true, children: /* @__PURE__ */ jsxs15(
      Button10,
      {
        variant: "outline",
        className: "w-[240px] justify-between",
        disabled,
        children: [
          /* @__PURE__ */ jsxs15("span", { className: "flex items-center gap-2 min-w-0", children: [
            /* @__PURE__ */ jsx17(Building24, { className: "h-4 w-4 flex-shrink-0" }),
            /* @__PURE__ */ jsx17("span", { className: "truncate", children: selectedOrg ? selectedOrg.name : "Select Organization" })
          ] }),
          /* @__PURE__ */ jsx17(ChevronDown2, { className: "h-4 w-4 shrink-0 opacity-50" })
        ]
      }
    ) }),
    /* @__PURE__ */ jsx17(DropdownMenuContent4, { className: "w-[240px]", children: accessibleOrganizations.map((org) => /* @__PURE__ */ jsxs15(
      DropdownMenuItem4,
      {
        onClick: () => selectOrganization(org.orgId),
        className: "cursor-pointer",
        children: [
          /* @__PURE__ */ jsx17(Building24, { className: "mr-2 h-4 w-4" }),
          /* @__PURE__ */ jsx17("span", { className: "flex-1", children: org.name }),
          org.orgId === selectedOrganizationId && /* @__PURE__ */ jsx17("div", { className: "ml-auto h-2 w-2 rounded-full bg-primary" })
        ]
      },
      org.orgId
    )) })
  ] });
};

// src/hooks/use-organizations.ts
import { useQuery as useQuery7, useQueryClient as useQueryClient5 } from "@tanstack/react-query";
var ORGANIZATIONS_QUERY_KEY = ["organizations"];
function useOrganizations() {
  const organizationApi = useOrganizationApi();
  return useQuery7({
    queryKey: ORGANIZATIONS_QUERY_KEY,
    queryFn: () => organizationApi.listOrganizations(),
    staleTime: 5 * 60 * 1e3,
    // 5 minutes
    gcTime: 10 * 60 * 1e3
    // 10 minutes (formerly cacheTime)
  });
}
function usePrefetchOrganizations() {
  const queryClient2 = useQueryClient5();
  return () => {
    return queryClient2.prefetchQuery({
      queryKey: ORGANIZATIONS_QUERY_KEY,
      queryFn: fetchOrganizations,
      staleTime: 5 * 60 * 1e3
    });
  };
}
function useOrganizationsCache() {
  const queryClient2 = useQueryClient5();
  return queryClient2.getQueryData(ORGANIZATIONS_QUERY_KEY);
}

// src/OrganizationSelector.tsx
import { jsx as jsx18 } from "react/jsx-runtime";
var OrganizationSelectorInternal = ({
  selectedOrganizationId,
  onOrganizationSelect,
  className
}) => {
  const { data: organizations = [] } = useOrganizations();
  const [internalOrgId, setInternalOrgId] = useState11(selectedOrganizationId || "");
  const handleOrganizationSelect = (orgId) => {
    if (onOrganizationSelect) {
      onOrganizationSelect(orgId);
    } else {
      setInternalOrgId(orgId);
    }
  };
  const currentOrgId = selectedOrganizationId || internalOrgId;
  return /* @__PURE__ */ jsx18("div", { className: cn5("organization-selector-component", className), children: /* @__PURE__ */ jsx18(
    OrganizationDropdown,
    {
      accessibleOrganizations: organizations,
      selectedOrganizationId: currentOrgId,
      selectOrganization: handleOrganizationSelect
    }
  ) });
};
var OrganizationSelector = ({
  app_id,
  debug = false,
  ...props
}) => {
  return /* @__PURE__ */ jsx18(AppConfigProvider, { app_id, debug, children: /* @__PURE__ */ jsx18(OrganizationSelectorInternal, { ...props }) });
};

// src/context/AuthContext.tsx
import { createContext as createContext2, useContext as useContext2, useState as useState12, useEffect as useEffect5, useLayoutEffect, useCallback } from "react";
import { useQueryClient as useQueryClient7 } from "@tanstack/react-query";

// src/api/application-api.ts
var applicationApi = {
  /**
   * Get application configuration including datasources
   * @param app_id - Application identifier
   * @returns ApplicationResponse with datasources
   * @throws 404 if app is not bootstrapped
   */
  getConfig: async (app_id) => {
    const apiInstance = getWorkspacesApi();
    const response = await apiInstance.get(`/app/${app_id}/config`);
    return response.data;
  }
};

// src/api/llm-config-api.ts
var useLLMConfigApi = () => {
  const logger2 = useLogger();
  const apiInstance = getWorkspacesApi();
  return {
    /**
     * List all LLM configurations
     * @param params - Optional filters (provider, active_only)
     * @returns Array of LLM configurations
     */
    listLLMConfigs: async (params) => {
      logger2.debug("Fetching LLM configurations", params);
      const queryParams = new URLSearchParams();
      if (params?.provider) {
        queryParams.append("provider", params.provider);
      }
      if (params?.active_only !== void 0) {
        queryParams.append("active_only", String(params.active_only));
      }
      const url = `/admin/llm-configs${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await apiInstance.get(url);
      return response.data;
    },
    /**
     * Get a specific LLM configuration by model_name
     * @param model_name - The unique identifier for this model configuration
     * @returns LLM configuration
     */
    getLLMConfig: async (model_name) => {
      logger2.debug("Fetching LLM config:", model_name);
      const response = await apiInstance.get(`/admin/llm-configs/${encodeURIComponent(model_name)}`);
      return response.data;
    },
    /**
     * Create a new LLM configuration
     * @param data - LLM configuration data
     * @returns Created LLM configuration
     */
    createLLMConfig: async (data) => {
      logger2.debug("Creating LLM config:", data.model_id);
      const response = await apiInstance.post("/admin/llm-configs", data);
      return response.data;
    },
    /**
     * Update an existing LLM configuration
     * @param model_name - The unique identifier for this model configuration
     * @param data - Updated configuration data
     * @returns Updated LLM configuration
     */
    updateLLMConfig: async (model_name, data) => {
      logger2.debug("Updating LLM config:", model_name, data);
      const response = await apiInstance.put(`/admin/llm-configs/${encodeURIComponent(model_name)}`, data);
      return response.data;
    },
    /**
     * Delete an LLM configuration
     * @param model_name - The unique identifier for this model configuration
     */
    deleteLLMConfig: async (model_name) => {
      logger2.debug("Deleting LLM config:", model_name);
      await apiInstance.delete(`/admin/llm-configs/${encodeURIComponent(model_name)}`);
    },
    /**
     * Export all LLM configurations to CSV
     * @returns Blob containing CSV file
     */
    exportToCSV: async () => {
      logger2.debug("Exporting LLM configs to CSV");
      const response = await apiInstance.get("/admin/llm-configs/export/csv", {
        responseType: "blob"
      });
      return response.data;
    },
    /**
     * Import LLM configurations from CSV
     * @param file - CSV file to import
     * @param merge_existing - If true, update existing records. If false, skip existing records.
     * @returns Import results with counts
     */
    importFromCSV: async (file, merge_existing = false) => {
      logger2.debug("Importing LLM configs from CSV", { merge_existing });
      const formData = new FormData();
      formData.append("file", file);
      const response = await apiInstance.post(
        `/admin/llm-configs/import/csv?merge_existing=${merge_existing}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );
      return response.data;
    }
  };
};
var llmConfigApi = {
  /**
   * List all LLM configurations
   * @param params - Optional filters (provider, active_only)
   * @returns Array of LLM configurations
   */
  listLLMConfigs: async (params) => {
    const apiInstance = getWorkspacesApi();
    const queryParams = new URLSearchParams();
    if (params?.provider) {
      queryParams.append("provider", params.provider);
    }
    if (params?.active_only !== void 0) {
      queryParams.append("active_only", String(params.active_only));
    }
    const url = `/admin/llm-configs${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
    const response = await apiInstance.get(url);
    return response.data;
  },
  /**
   * Get a specific LLM configuration by model_name
   * @param model_name - The unique identifier for this model configuration
   * @returns LLM configuration
   */
  getLLMConfig: async (model_name) => {
    const apiInstance = getWorkspacesApi();
    const response = await apiInstance.get(`/admin/llm-configs/${encodeURIComponent(model_name)}`);
    return response.data;
  },
  /**
   * Create a new LLM configuration
   * @param data - LLM configuration data
   * @returns Created LLM configuration
   */
  createLLMConfig: async (data) => {
    const apiInstance = getWorkspacesApi();
    const response = await apiInstance.post("/admin/llm-configs", data);
    return response.data;
  },
  /**
   * Update an existing LLM configuration
   * @param model_name - The unique identifier for this model configuration
   * @param data - Updated configuration data
   * @returns Updated LLM configuration
   */
  updateLLMConfig: async (model_name, data) => {
    const apiInstance = getWorkspacesApi();
    const response = await apiInstance.put(`/admin/llm-configs/${encodeURIComponent(model_name)}`, data);
    return response.data;
  },
  /**
   * Delete an LLM configuration
   * @param model_name - The unique identifier for this model configuration
   */
  deleteLLMConfig: async (model_name) => {
    const apiInstance = getWorkspacesApi();
    await apiInstance.delete(`/admin/llm-configs/${encodeURIComponent(model_name)}`);
  },
  /**
   * Export all LLM configurations to CSV
   * @returns Blob containing CSV file
   */
  exportToCSV: async () => {
    const apiInstance = getWorkspacesApi();
    const response = await apiInstance.get("/admin/llm-configs/export/csv", {
      responseType: "blob"
    });
    return response.data;
  },
  /**
   * Import LLM configurations from CSV
   * @param file - CSV file to import
   * @param merge_existing - If true, update existing records. If false, skip existing records.
   * @returns Import results with counts
   */
  importFromCSV: async (file, merge_existing = false) => {
    const apiInstance = getWorkspacesApi();
    const formData = new FormData();
    formData.append("file", file);
    const response = await apiInstance.post(
      `/admin/llm-configs/import/csv?merge_existing=${merge_existing}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      }
    );
    return response.data;
  }
};

// src/lib/workspace-utils.ts
var DEFAULT_ORG_ID = "umd-org";
var DEFAULT_WS_ID = "umd-ogc-state-ws";
var STORAGE_KEY_SELECTED_WORKSPACE = "selected_workspace";
function getActiveWorkspace() {
  try {
    const savedSelection = localStorage.getItem(STORAGE_KEY_SELECTED_WORKSPACE);
    if (savedSelection) {
      return JSON.parse(savedSelection);
    }
    return null;
  } catch (error) {
    console.error("[WorkspaceUtils] Error retrieving active workspace:", error);
    return null;
  }
}
function saveActiveWorkspace(orgId, wsId) {
  try {
    localStorage.setItem(STORAGE_KEY_SELECTED_WORKSPACE, JSON.stringify({ orgId, wsId }));
    console.log(`[WorkspaceUtils] Saved active workspace: org=${orgId}, ws=${wsId}`);
  } catch (error) {
    console.error("[WorkspaceUtils] Error saving active workspace:", error);
  }
}
function clearActiveWorkspace() {
  localStorage.removeItem(STORAGE_KEY_SELECTED_WORKSPACE);
  console.log("[WorkspaceUtils] Cleared active workspace selection");
}
function getOrganizationId(explicitOrgId, userProfile) {
  if (explicitOrgId) {
    return explicitOrgId;
  }
  const activeWorkspace = getActiveWorkspace();
  if (activeWorkspace?.orgId) {
    return activeWorkspace.orgId;
  }
  if (userProfile?.orgId) {
    return userProfile.orgId;
  }
  return DEFAULT_ORG_ID;
}

// src/hooks/use-user-profile.ts
import { useQuery as useQuery8, useQueryClient as useQueryClient6 } from "@tanstack/react-query";
var USER_PROFILE_QUERY_KEY = ["user-profile"];
function useUserProfile(enabled = true) {
  return useQuery8({
    queryKey: USER_PROFILE_QUERY_KEY,
    queryFn: fetchUserProfile,
    enabled,
    staleTime: 5 * 60 * 1e3,
    // 5 minutes
    gcTime: 10 * 60 * 1e3,
    // 10 minutes
    refetchOnWindowFocus: true
    // Refetch to catch impersonation changes
  });
}
function usePrefetchUserProfile() {
  const queryClient2 = useQueryClient6();
  return () => {
    return queryClient2.prefetchQuery({
      queryKey: USER_PROFILE_QUERY_KEY,
      queryFn: fetchUserProfile,
      staleTime: 5 * 60 * 1e3
    });
  };
}
function useUserProfileCache() {
  const queryClient2 = useQueryClient6();
  return queryClient2.getQueryData(USER_PROFILE_QUERY_KEY);
}

// src/context/AuthContext.tsx
import { jsx as jsx19 } from "react/jsx-runtime";
var AuthContext = createContext2(void 0);
function AuthProvider({ children, appName }) {
  const queryClient2 = useQueryClient7();
  const [user, setUser] = useState12(null);
  const [isAuthenticated, setIsAuthenticated] = useState12(false);
  const [userId, setUserId] = useState12(null);
  const [isLoading, setIsLoading] = useState12(true);
  const [isAdmin, setIsAdmin] = useState12(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState12(null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState12(null);
  const [accessibleOrganizations, setAccessibleOrganizations] = useState12([]);
  const [accessibleWorkspaces, setAccessibleWorkspaces] = useState12([]);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState12(false);
  const [workspacesError, setWorkspacesError] = useState12(null);
  const [impersonatingUserId, setImpersonatingUserId] = useState12(null);
  const [actualUserId, setActualUserId] = useState12(null);
  const handleAuthentication = useCallback(async (authenticated, user2) => {
    console.log(`[AuthContext-${appName}] Handling authentication:`, authenticated);
    setIsAuthenticated(authenticated);
    setUser(user2);
    if (authenticated && user2) {
      let extractedUserId = null;
      if (user2.username) {
        extractedUserId = user2.username;
      } else if (user2.email) {
        extractedUserId = user2.email;
      }
      console.log(`[AuthContext-${appName}] Extracting userId from CAS:`, extractedUserId);
      if (extractedUserId) {
        console.log(`[AuthContext-${appName}] User ID set:`, extractedUserId);
        setUserId(extractedUserId);
        try {
          console.log(`[AuthContext-${appName}] Fetching user data...`);
          setLoadingWorkspaces(true);
          const userProfile = await queryClient2.fetchQuery({
            queryKey: USER_PROFILE_QUERY_KEY,
            queryFn: fetchUserProfile,
            staleTime: 5 * 60 * 1e3
            // 5 minutes
          });
          console.log(`[AuthContext-${appName}] User profile loaded:`, userProfile);
          if (userProfile.impersonatedBy) {
            console.log(`[AuthContext-${appName}] Being impersonated by admin:`, userProfile.impersonatedBy.userId);
            setImpersonatingUserId(userProfile.userId);
            setActualUserId(userProfile.impersonatedBy.userId);
            setIsAdmin(userProfile.isGlobalAdmin ?? false);
          } else {
            setImpersonatingUserId(null);
            setActualUserId(null);
            setIsAdmin(userProfile.isGlobalAdmin ?? false);
          }
          const organizations = await queryClient2.fetchQuery({
            queryKey: ORGANIZATIONS_QUERY_KEY,
            queryFn: fetchOrganizations,
            staleTime: 5 * 60 * 1e3
            // 5 minutes
          });
          console.log(`[AuthContext-${appName}] Organizations loaded:`, organizations.length);
          setAccessibleOrganizations(organizations);
          let autoSelectedOrgId = null;
          if (organizations.length === 1) {
            console.log(`[AuthContext-${appName}] Auto-selecting single available org:`, organizations[0].orgId);
            autoSelectedOrgId = organizations[0].orgId;
          } else if (organizations.length > 1) {
            console.log(`[AuthContext-${appName}] Auto-selecting first org from ${organizations.length} available:`, organizations[0].orgId);
            autoSelectedOrgId = organizations[0].orgId;
          }
          const savedWorkspace = getActiveWorkspace();
          let orgToLoad = autoSelectedOrgId;
          if (savedWorkspace?.orgId && savedWorkspace?.wsId) {
            orgToLoad = savedWorkspace.orgId;
            console.log(`[AuthContext-${appName}] Found saved workspace, will load org:`, orgToLoad);
          } else if (savedWorkspace) {
            console.log(`[AuthContext-${appName}] Clearing invalid saved workspace (missing orgId or wsId)`);
            clearActiveWorkspace();
          }
          if (orgToLoad) {
            console.log(`[AuthContext-${appName}] Loading workspaces for org:`, orgToLoad);
            const workspaces = await queryClient2.fetchQuery({
              queryKey: workspacesQueryKey(orgToLoad),
              queryFn: () => fetchOrgWorkspaces(orgToLoad),
              staleTime: 5 * 60 * 1e3
              // 5 minutes
            });
            console.log(`[AuthContext-${appName}] Workspaces loaded for org ${orgToLoad}:`, workspaces.length);
            setAccessibleWorkspaces(workspaces);
            setSelectedOrganizationId(orgToLoad);
            if (savedWorkspace && savedWorkspace.orgId === orgToLoad) {
              const workspaceExists = workspaces.some((ws) => ws.wsId === savedWorkspace.wsId);
              if (workspaceExists) {
                console.log(`[AuthContext-${appName}] Using saved workspace:`, savedWorkspace.wsId);
                setSelectedWorkspaceId(savedWorkspace.wsId);
              } else {
                console.log(`[AuthContext-${appName}] Saved workspace no longer exists, clearing`);
                clearActiveWorkspace();
                if (workspaces.length > 0) {
                  setSelectedWorkspaceId(workspaces[0].wsId);
                  saveActiveWorkspace(orgToLoad, workspaces[0].wsId);
                }
              }
            } else if (workspaces.length === 1) {
              console.log(`[AuthContext-${appName}] Auto-selecting single workspace:`, workspaces[0].wsId);
              setSelectedWorkspaceId(workspaces[0].wsId);
              saveActiveWorkspace(orgToLoad, workspaces[0].wsId);
            } else if (workspaces.length > 1) {
              console.log(`[AuthContext-${appName}] Auto-selecting first workspace:`, workspaces[0].wsId);
              setSelectedWorkspaceId(workspaces[0].wsId);
              saveActiveWorkspace(orgToLoad, workspaces[0].wsId);
            }
            if (workspaces.length === 0 && !userProfile.isGlobalAdmin) {
              console.log(`[AuthContext-${appName}] No accessible workspaces in org - access will be denied`);
            }
          } else if (organizations.length === 0 && !userProfile.isGlobalAdmin) {
            console.log(`[AuthContext-${appName}] No accessible organizations - access will be denied`);
          }
          setWorkspacesError(null);
        } catch (error) {
          console.error(`[AuthContext-${appName}] Error fetching user data:`, error);
          setWorkspacesError("Failed to load user data");
        } finally {
          setLoadingWorkspaces(false);
        }
      } else {
        console.error(`[AuthContext-${appName}] Could not extract userId from user object`);
        setWorkspacesError("Failed to extract user ID from authentication data");
      }
    }
    setIsLoading(false);
  }, [appName, queryClient2]);
  useEffect5(() => {
    console.log(`[AuthContext-${appName}] Checking for existing CAS session on mount`);
    try {
      const casUser = localStorage.getItem("cas_user");
      const casToken = localStorage.getItem("cas_jwt");
      if (casUser && casToken) {
        console.log(`[AuthContext-${appName}] Found existing CAS session, triggering auth flow`);
        const user2 = JSON.parse(casUser);
        handleAuthentication(true, user2);
        return;
      } else {
        console.log(`[AuthContext-${appName}] No existing CAS session found on mount`);
        setIsLoading(false);
      }
    } catch (error) {
      console.error(`[AuthContext-${appName}] Error checking CAS session:`, error);
      setIsLoading(false);
    }
  }, [appName, handleAuthentication]);
  useLayoutEffect(() => {
    console.log(`[AuthContext-${appName}] Setting up event listeners`);
    const handleAuthStateChange = async (event) => {
      console.log(`[AuthContext-${appName}] Event received:`, event.type);
      const customEvent = event;
      const { authenticated, user: user2 } = customEvent.detail;
      await handleAuthentication(authenticated, user2);
    };
    console.log(`[AuthContext-${appName}] Adding event listeners for auth-state-change and access-granted (CAS auth)`);
    document.addEventListener("auth-state-change", handleAuthStateChange);
    document.addEventListener("access-granted", handleAuthStateChange);
    return () => {
      document.removeEventListener("auth-state-change", handleAuthStateChange);
      document.removeEventListener("access-granted", handleAuthStateChange);
    };
  }, [appName, handleAuthentication]);
  const selectWorkspace = (orgId, wsId) => {
    console.log(`[AuthContext-${appName}] Selecting workspace:`, { orgId, wsId });
    setSelectedOrganizationId(orgId);
    setSelectedWorkspaceId(wsId);
    saveActiveWorkspace(orgId, wsId);
  };
  const selectOrganization = async (orgId) => {
    console.log(`[AuthContext-${appName}] Selecting organization:`, orgId);
    if (orgId !== selectedOrganizationId) {
      setSelectedOrganizationId(orgId);
      setSelectedWorkspaceId(null);
      setLoadingWorkspaces(true);
      if (selectedOrganizationId) {
        localStorage.removeItem(`activeWorkspace_${selectedOrganizationId}`);
      }
      try {
        console.log(`[AuthContext-${appName}] Loading workspaces for org:`, orgId);
        const workspaces = await queryClient2.fetchQuery({
          queryKey: workspacesQueryKey(orgId),
          queryFn: () => fetchOrgWorkspaces(orgId),
          staleTime: 5 * 60 * 1e3
          // 5 minutes
        });
        console.log(`[AuthContext-${appName}] Workspaces loaded for org ${orgId}:`, workspaces.length);
        setAccessibleWorkspaces(workspaces);
        if (workspaces.length === 1) {
          console.log(`[AuthContext-${appName}] Auto-selecting single workspace in org:`, workspaces[0]);
          setSelectedWorkspaceId(workspaces[0].wsId);
          saveActiveWorkspace(orgId, workspaces[0].wsId);
        } else if (workspaces.length > 1) {
          console.log(`[AuthContext-${appName}] Auto-selecting first workspace in org:`, workspaces[0]);
          setSelectedWorkspaceId(workspaces[0].wsId);
          saveActiveWorkspace(orgId, workspaces[0].wsId);
        } else {
          console.log(`[AuthContext-${appName}] No workspaces available in org ${orgId}`);
        }
      } catch (error) {
        console.error(`[AuthContext-${appName}] Error loading workspaces for org ${orgId}:`, error);
        setWorkspacesError(`Failed to load workspaces for organization`);
        setAccessibleWorkspaces([]);
      } finally {
        setLoadingWorkspaces(false);
      }
    }
  };
  const hasWorkspaceAccess = Boolean(selectedWorkspaceId) || isAdmin;
  const currentWorkspace = accessibleWorkspaces.find(
    (ws) => ws.orgId === selectedOrganizationId && ws.wsId === selectedWorkspaceId
  ) || null;
  const value = {
    isAuthenticated,
    isLoading,
    user,
    userId,
    isAdmin,
    selectedOrganizationId,
    selectedWorkspaceId,
    accessibleOrganizations,
    accessibleWorkspaces,
    currentWorkspace,
    selectWorkspace,
    selectOrganization,
    loadingWorkspaces,
    workspacesError,
    hasWorkspaceAccess,
    impersonatingUserId,
    actualUserId
  };
  return /* @__PURE__ */ jsx19(AuthContext.Provider, { value, children });
}
function useAuth() {
  const context = useContext2(AuthContext);
  if (context === void 0) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// src/components/AppHeader.tsx
import { useEffect as useEffect6, useState as useState14 } from "react";
import { Link, useLocation as useLocation2 } from "react-router-dom";
import { Menu } from "lucide-react";
import "@universityofmaryland/utilityheader";

// src/components/ProfileMenu.tsx
import {
  DropdownMenu as DropdownMenu5,
  DropdownMenuContent as DropdownMenuContent5,
  DropdownMenuItem as DropdownMenuItem6,
  DropdownMenuSeparator as DropdownMenuSeparator3,
  DropdownMenuTrigger as DropdownMenuTrigger5
} from "@umd-dit-ai-workspace/shared-ui";
import { useNavigate, useLocation } from "react-router-dom";

// src/components/ImpersonationMenuItem.tsx
import { useState as useState13 } from "react";
import {
  DropdownMenuItem as DropdownMenuItem5,
  Dialog as Dialog6,
  DialogContent as DialogContent6,
  DialogDescription as DialogDescription6,
  DialogHeader as DialogHeader6,
  DialogTitle as DialogTitle6,
  DialogFooter as DialogFooter4,
  Button as Button11,
  Label as Label4,
  Input as Input4
} from "@umd-dit-ai-workspace/shared-ui";
import { useQuery as useQuery9, useMutation as useMutation4, useQueryClient as useQueryClient8 } from "@tanstack/react-query";
import { UserCog, Shield as Shield4 } from "lucide-react";
import { Fragment as Fragment4, jsx as jsx20, jsxs as jsxs16 } from "react/jsx-runtime";
function ImpersonationMenuItemInner() {
  const { isAuthenticated, isAdmin } = useAuth();
  const queryClient2 = useQueryClient8();
  const usersApi = useUsersApi();
  const [impersonateDialogOpen, setImpersonateDialogOpen] = useState13(false);
  const [impersonateUserId, setImpersonateUserId] = useState13("");
  const { data: userProfile } = useUserProfile(isAuthenticated && isAdmin);
  const { data: allUsers = [] } = useQuery9({
    queryKey: ["users", "all"],
    queryFn: () => usersApi.getAllUsers(),
    enabled: isAdmin && impersonateDialogOpen
  });
  const impersonateMutation = useMutation4({
    mutationFn: (targetUserId) => usersApi.setUserImpersonation(targetUserId),
    onSuccess: () => {
      clearActiveWorkspace();
      queryClient2.invalidateQueries({ queryKey: USER_PROFILE_QUERY_KEY });
      window.location.reload();
    }
  });
  const isImpersonating = userProfile?.impersonatedBy != null;
  const handleStartImpersonation = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImpersonateDialogOpen(true);
    setImpersonateUserId("");
  };
  const handleImpersonate = () => {
    if (impersonateUserId.trim()) {
      impersonateMutation.mutate(impersonateUserId.trim());
      setImpersonateDialogOpen(false);
    }
  };
  if (isImpersonating) {
    return null;
  }
  return /* @__PURE__ */ jsxs16(Fragment4, { children: [
    /* @__PURE__ */ jsxs16(
      DropdownMenuItem5,
      {
        onSelect: (e) => {
          e.preventDefault();
          handleStartImpersonation(e);
        },
        children: [
          /* @__PURE__ */ jsx20(UserCog, { className: "mr-2 h-4 w-4" }),
          /* @__PURE__ */ jsx20("span", { children: "Impersonate User" })
        ]
      }
    ),
    /* @__PURE__ */ jsx20(Dialog6, { open: impersonateDialogOpen, onOpenChange: setImpersonateDialogOpen, children: /* @__PURE__ */ jsxs16(DialogContent6, { className: "max-w-md", children: [
      /* @__PURE__ */ jsxs16(DialogHeader6, { children: [
        /* @__PURE__ */ jsx20(DialogTitle6, { children: "Impersonate User" }),
        /* @__PURE__ */ jsx20(DialogDescription6, { children: "Enter the user ID to impersonate. You will see the application as that user would see it." })
      ] }),
      /* @__PURE__ */ jsxs16("div", { className: "space-y-4 py-4", children: [
        /* @__PURE__ */ jsxs16("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsx20(Label4, { htmlFor: "impersonate_user_id", children: "User ID" }),
          /* @__PURE__ */ jsx20(
            Input4,
            {
              id: "impersonate_user_id",
              value: impersonateUserId,
              onChange: (e) => setImpersonateUserId(e.target.value),
              placeholder: "Enter user ID...",
              autoComplete: "off"
            }
          ),
          /* @__PURE__ */ jsx20("p", { className: "text-xs text-muted-foreground", children: "The page will reload once impersonation is activated." })
        ] }),
        allUsers.length > 0 && /* @__PURE__ */ jsxs16("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsx20(Label4, { className: "text-xs", children: "Available Users:" }),
          /* @__PURE__ */ jsx20("div", { className: "max-h-40 overflow-y-auto border rounded-md", children: allUsers.map((u) => /* @__PURE__ */ jsxs16(
            "button",
            {
              onClick: () => setImpersonateUserId(u.userId),
              className: "w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center justify-between",
              children: [
                /* @__PURE__ */ jsx20("span", { children: u.userId }),
                u.isGlobalAdmin && /* @__PURE__ */ jsx20(Shield4, { className: "h-3 w-3 text-blue-600" })
              ]
            },
            u.userId
          )) })
        ] })
      ] }),
      /* @__PURE__ */ jsxs16(DialogFooter4, { children: [
        /* @__PURE__ */ jsx20(
          Button11,
          {
            type: "button",
            variant: "outline",
            onClick: () => setImpersonateDialogOpen(false),
            disabled: impersonateMutation.isPending,
            children: "Cancel"
          }
        ),
        /* @__PURE__ */ jsx20(
          Button11,
          {
            onClick: handleImpersonate,
            disabled: !impersonateUserId.trim() || impersonateMutation.isPending,
            children: impersonateMutation.isPending ? "Activating..." : "Start Impersonation"
          }
        )
      ] })
    ] }) })
  ] });
}
function ImpersonationMenuItem({ app_id = "default", debug = false }) {
  return /* @__PURE__ */ jsx20(AppConfigProvider, { app_id, debug, children: /* @__PURE__ */ jsx20(ImpersonationMenuItemInner, {}) });
}

// src/components/ProfileMenu.tsx
import { ChevronDown as ChevronDown3, Briefcase, DollarSign, Shield as Shield5, User, Home } from "lucide-react";
import { Fragment as Fragment5, jsx as jsx21, jsxs as jsxs17 } from "react/jsx-runtime";
function ProfileMenu({
  isAdmin,
  app_id,
  showDashboard = true
  // Default to true for backward compatibility
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userId, hasWorkspaceAccess, impersonatingUserId } = useAuth();
  const displayUserId = impersonatingUserId || userId;
  const userName = displayUserId || user?.name || user?.username || user?.email || "User";
  const showJobs = isAdmin || hasWorkspaceAccess;
  const hasVisibleItems = showJobs || isAdmin;
  if (!hasVisibleItems) {
    return /* @__PURE__ */ jsxs17("div", { className: "flex items-center gap-2 px-3 py-2", children: [
      /* @__PURE__ */ jsx21("div", { className: "h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center", children: /* @__PURE__ */ jsx21(User, { className: "h-4 w-4" }) }),
      /* @__PURE__ */ jsx21("span", { className: "text-sm font-medium text-gray-700", children: userName })
    ] });
  }
  return /* @__PURE__ */ jsxs17(DropdownMenu5, { children: [
    /* @__PURE__ */ jsx21(DropdownMenuTrigger5, { asChild: true, children: /* @__PURE__ */ jsxs17("button", { className: "flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500", children: [
      /* @__PURE__ */ jsx21("div", { className: "h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center", children: /* @__PURE__ */ jsx21(User, { className: "h-4 w-4" }) }),
      /* @__PURE__ */ jsx21("span", { className: "text-sm font-medium", children: userName }),
      /* @__PURE__ */ jsx21(ChevronDown3, { className: "h-4 w-4 text-gray-500" })
    ] }) }),
    /* @__PURE__ */ jsxs17(DropdownMenuContent5, { align: "end", className: "w-56", children: [
      showDashboard && location.pathname !== "/" && /* @__PURE__ */ jsxs17(DropdownMenuItem6, { onClick: () => navigate("/"), children: [
        /* @__PURE__ */ jsx21(Home, { className: "mr-2 h-4 w-4" }),
        /* @__PURE__ */ jsx21("span", { children: "Dashboard" })
      ] }),
      showJobs && /* @__PURE__ */ jsxs17(DropdownMenuItem6, { onClick: () => navigate("/jobs"), children: [
        /* @__PURE__ */ jsx21(Briefcase, { className: "mr-2 h-4 w-4" }),
        /* @__PURE__ */ jsx21("span", { children: "Jobs" })
      ] }),
      isAdmin && /* @__PURE__ */ jsxs17(Fragment5, { children: [
        /* @__PURE__ */ jsxs17(DropdownMenuItem6, { onClick: () => navigate("/costs"), children: [
          /* @__PURE__ */ jsx21(DollarSign, { className: "mr-2 h-4 w-4" }),
          /* @__PURE__ */ jsx21("span", { children: "Costs" })
        ] }),
        /* @__PURE__ */ jsxs17(DropdownMenuItem6, { onClick: () => navigate("/admin"), children: [
          /* @__PURE__ */ jsx21(Shield5, { className: "mr-2 h-4 w-4" }),
          /* @__PURE__ */ jsx21("span", { children: "Admin" })
        ] }),
        /* @__PURE__ */ jsx21(ImpersonationMenuItem, { app_id })
      ] }),
      /* @__PURE__ */ jsx21(DropdownMenuSeparator3, {})
    ] })
  ] });
}

// src/components/AppHeader.tsx
import { Fragment as Fragment6, jsx as jsx22, jsxs as jsxs18 } from "react/jsx-runtime";
var AppHeader = ({
  appId,
  navigationItems,
  debugMode = false,
  showDashboard = true
  // Default to true for backward compatibility
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState14(false);
  const [isMobile, setIsMobile] = useState14(false);
  const [isTwoRowLayout, setIsTwoRowLayout] = useState14(false);
  const currentLocation = useLocation2();
  const authState = useAuth();
  const {
    isAdmin = false,
    selectedOrganizationId,
    selectedWorkspaceId,
    isAuthenticated = false,
    accessibleOrganizations = []
  } = authState;
  const { selectOrganization, selectWorkspace } = authState;
  const showOrgSelector = isAdmin || accessibleOrganizations.length > 1;
  useEffect6(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 640);
      const threshold = appId === "secure-analysis" ? 1536 : 1235;
      setIsTwoRowLayout(window.innerWidth < threshold && window.innerWidth >= 640);
    };
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, [appId]);
  useEffect6(() => {
    const authElement = document.querySelector("umd-auth");
    if (authElement) {
      if (mobileMenuOpen) {
        authElement.classList.add("menu-open");
      } else {
        authElement.classList.remove("menu-open");
      }
    }
  }, [mobileMenuOpen]);
  const isActive = (path, activeWhen) => {
    const pathsToCheck = [path, ...activeWhen || []];
    return pathsToCheck.some((p) => currentLocation.pathname === p || currentLocation.pathname.startsWith(p)) ? "bg-gray-200" : "";
  };
  const filteredNavItems = navigationItems.filter(
    (item) => !item.adminOnly || item.adminOnly && isAdmin
  );
  const allNavItems = [
    ...filteredNavItems
  ].filter((item) => !item.adminOnly || item.adminOnly && isAdmin);
  const renderNavigation = () => /* @__PURE__ */ jsx22(Fragment6, { children: allNavItems.map((item, index) => /* @__PURE__ */ jsxs18(
    Link,
    {
      to: item.path,
      className: `inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${isActive(item.path, item.activeWhen)}`,
      onClick: () => setMobileMenuOpen(false),
      children: [
        item.icon && /* @__PURE__ */ jsx22("span", { className: "mr-1", children: item.icon }),
        item.label
      ]
    },
    index
  )) });
  const renderMobileNavigation = () => /* @__PURE__ */ jsx22(Fragment6, { children: allNavItems.map((item, index) => /* @__PURE__ */ jsxs18(
    Link,
    {
      to: item.path,
      className: `block px-3 py-2 text-base font-medium ${isActive(item.path, item.activeWhen)}`,
      onClick: () => setMobileMenuOpen(false),
      children: [
        item.icon && /* @__PURE__ */ jsx22("span", { className: "mr-1", children: item.icon }),
        item.label
      ]
    },
    index
  )) });
  return /* @__PURE__ */ jsxs18(AppConfigProvider, { app_id: appId, debug: debugMode, children: [
    /* @__PURE__ */ jsx22("umd-utility-header", { size: "100%", padding: "20px" }),
    /* @__PURE__ */ jsx22("nav", { className: "bg-white shadow-sm", children: /* @__PURE__ */ jsxs18("div", { className: "mx-auto px-4", children: [
      /* @__PURE__ */ jsx22("div", { className: "flex h-16 items-center justify-between", children: isMobile ? (
        // Mobile view
        /* @__PURE__ */ jsxs18(Fragment6, { children: [
          /* @__PURE__ */ jsx22(
            "button",
            {
              type: "button",
              className: "inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:bg-gray-100",
              onClick: () => setMobileMenuOpen(!mobileMenuOpen),
              "aria-expanded": mobileMenuOpen,
              "aria-label": "Toggle navigation menu",
              children: /* @__PURE__ */ jsx22(Menu, { className: "h-6 w-6", "aria-hidden": "true" })
            }
          ),
          /* @__PURE__ */ jsx22(
            ProfileMenu,
            {
              isAdmin,
              app_id: appId,
              showDashboard
            }
          )
        ] })
      ) : isTwoRowLayout ? (
        // Two-row layout
        /* @__PURE__ */ jsxs18(Fragment6, { children: [
          /* @__PURE__ */ jsxs18("div", { className: "flex items-center space-x-2", children: [
            isAuthenticated && showOrgSelector && /* @__PURE__ */ jsx22(
              OrganizationSelector,
              {
                app_id: appId,
                selectedOrganizationId: selectedOrganizationId || void 0,
                onOrganizationSelect: selectOrganization,
                debug: debugMode
              }
            ),
            isAuthenticated && /* @__PURE__ */ jsx22(
              WorkspaceSelector,
              {
                app_id: appId,
                selectedOrganizationId: selectedOrganizationId || void 0,
                selectedWorkspaceId: selectedWorkspaceId || void 0,
                onWorkspaceSelect: selectWorkspace,
                refreshOnSelect: false,
                debug: debugMode
              }
            )
          ] }),
          /* @__PURE__ */ jsx22("div", { className: "flex-shrink-0", children: /* @__PURE__ */ jsx22(
            ProfileMenu,
            {
              isAdmin,
              app_id: appId,
              showDashboard
            }
          ) })
        ] })
      ) : (
        // Full desktop view
        /* @__PURE__ */ jsxs18(Fragment6, { children: [
          /* @__PURE__ */ jsxs18("div", { className: "flex items-center space-x-8", children: [
            isAuthenticated && showOrgSelector && /* @__PURE__ */ jsx22(
              OrganizationSelector,
              {
                app_id: appId,
                selectedOrganizationId: selectedOrganizationId || void 0,
                onOrganizationSelect: selectOrganization,
                debug: debugMode
              }
            ),
            isAuthenticated && /* @__PURE__ */ jsx22(
              WorkspaceSelector,
              {
                app_id: appId,
                selectedOrganizationId: selectedOrganizationId || void 0,
                selectedWorkspaceId: selectedWorkspaceId || void 0,
                onWorkspaceSelect: selectWorkspace,
                refreshOnSelect: false,
                debug: debugMode
              }
            ),
            renderNavigation()
          ] }),
          /* @__PURE__ */ jsx22("div", { className: "flex-shrink-0", children: /* @__PURE__ */ jsx22(
            ProfileMenu,
            {
              isAdmin,
              app_id: appId,
              showDashboard
            }
          ) })
        ] })
      ) }),
      isTwoRowLayout && /* @__PURE__ */ jsx22("div", { className: "mx-auto px-4 py-2 flex items-center space-x-8 overflow-x-auto", children: renderNavigation() }),
      isMobile && mobileMenuOpen && /* @__PURE__ */ jsx22("div", { className: "pt-2 pb-3 space-y-1", children: renderMobileNavigation() })
    ] }) })
  ] });
};

// src/components/ImpersonationBanner.tsx
import { useMutation as useMutation5, useQueryClient as useQueryClient9 } from "@tanstack/react-query";
import { UserCog as UserCog2, X as X3 } from "lucide-react";
import { jsx as jsx23, jsxs as jsxs19 } from "react/jsx-runtime";
function ImpersonationBannerInner() {
  const { isAuthenticated } = useAuth();
  const queryClient2 = useQueryClient9();
  const usersApi = useUsersApi();
  const { data: userProfile } = useUserProfile(isAuthenticated);
  const impersonateMutation = useMutation5({
    mutationFn: (targetUserId) => usersApi.setUserImpersonation(targetUserId),
    onSuccess: () => {
      clearActiveWorkspace();
      queryClient2.invalidateQueries({ queryKey: USER_PROFILE_QUERY_KEY });
      window.location.reload();
    }
  });
  const isImpersonating = userProfile?.impersonatedBy != null;
  const handleStopImpersonation = () => {
    impersonateMutation.mutate(null);
  };
  if (!isImpersonating) {
    return null;
  }
  return /* @__PURE__ */ jsxs19("div", { className: "px-3 py-2 bg-amber-100 border border-amber-300 rounded-md flex items-center justify-between", children: [
    /* @__PURE__ */ jsxs19("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx23(UserCog2, { className: "h-4 w-4 text-amber-700" }),
      /* @__PURE__ */ jsxs19("span", { className: "text-sm font-medium text-amber-900", children: [
        "Viewing as: ",
        userProfile?.userId,
        " (by admin: ",
        userProfile?.impersonatedBy?.userId,
        ")"
      ] })
    ] }),
    /* @__PURE__ */ jsxs19(
      "button",
      {
        onClick: handleStopImpersonation,
        disabled: impersonateMutation.isPending,
        className: "px-2 py-1 text-xs bg-amber-200 hover:bg-amber-300 text-amber-900 rounded flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed",
        "aria-label": "Stop impersonation",
        children: [
          /* @__PURE__ */ jsx23(X3, { className: "h-3 w-3" }),
          impersonateMutation.isPending ? "Stopping..." : "Stop"
        ]
      }
    )
  ] });
}
function ImpersonationBanner({ app_id = "default", debug = false }) {
  return /* @__PURE__ */ jsx23(AppConfigProvider, { app_id, debug, children: /* @__PURE__ */ jsx23(ImpersonationBannerInner, {}) });
}

// src/components/users/UserManagement.tsx
import React11, { useState as useState18 } from "react";
import { useQuery as useQuery11, useMutation as useMutation7, useQueryClient as useQueryClient11 } from "@tanstack/react-query";
import { Button as Button15 } from "@umd-dit-ai-workspace/shared-ui";
import { Card as Card7, CardContent as CardContent7, CardDescription as CardDescription5, CardHeader as CardHeader7, CardTitle as CardTitle7 } from "@umd-dit-ai-workspace/shared-ui";
import {
  Table as Table3,
  TableBody as TableBody3,
  TableCell as TableCell3,
  TableHead as TableHead3,
  TableHeader as TableHeader3,
  TableRow as TableRow3
} from "@umd-dit-ai-workspace/shared-ui";
import { useToast as useToast5 } from "@umd-dit-ai-workspace/shared-ui";
import { cn as cn6 } from "@umd-dit-ai-workspace/shared-ui";
import { UserPlus, Search as Search3, Shield as Shield7, User as UserIcon4, Users as Users4, Trash2 as Trash26 } from "lucide-react";

// src/components/users/CreateUserDialog.tsx
import { useState as useState15, useEffect as useEffect7 } from "react";
import {
  Dialog as Dialog7,
  DialogContent as DialogContent7,
  DialogDescription as DialogDescription7,
  DialogHeader as DialogHeader7,
  DialogTitle as DialogTitle7,
  DialogFooter as DialogFooter5
} from "@umd-dit-ai-workspace/shared-ui";
import { Button as Button12 } from "@umd-dit-ai-workspace/shared-ui";
import { Label as Label5 } from "@umd-dit-ai-workspace/shared-ui";
import { Input as Input5 } from "@umd-dit-ai-workspace/shared-ui";
import { jsx as jsx24, jsxs as jsxs20 } from "react/jsx-runtime";
function CreateUserDialog({
  open,
  onOpenChange,
  onSave,
  isPending,
  defaultOrgId
}) {
  const [formData, setFormData] = useState15({
    userId: "",
    orgId: defaultOrgId || ""
  });
  useEffect7(() => {
    if (defaultOrgId) {
      setFormData((prev) => ({ ...prev, orgId: defaultOrgId }));
    }
  }, [defaultOrgId]);
  useEffect7(() => {
    if (open) {
      setFormData({
        userId: "",
        orgId: defaultOrgId || ""
      });
    }
  }, [open, defaultOrgId]);
  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.userId.trim()) {
      onSave({
        userId: formData.userId.trim(),
        orgId: formData.orgId.trim() || void 0
      });
    }
  };
  return /* @__PURE__ */ jsx24(Dialog7, { open, onOpenChange, children: /* @__PURE__ */ jsxs20(DialogContent7, { className: "max-w-md", children: [
    /* @__PURE__ */ jsxs20(DialogHeader7, { children: [
      /* @__PURE__ */ jsx24(DialogTitle7, { children: "Create User" }),
      /* @__PURE__ */ jsx24(DialogDescription7, { children: "Add a new user to the system. Users can be assigned to organizations and workspaces." })
    ] }),
    /* @__PURE__ */ jsxs20("form", { onSubmit: handleSubmit, children: [
      /* @__PURE__ */ jsxs20("div", { className: "space-y-4 py-4", children: [
        /* @__PURE__ */ jsxs20("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsx24(Label5, { htmlFor: "userId", children: "User ID" }),
          /* @__PURE__ */ jsx24(
            Input5,
            {
              id: "userId",
              value: formData.userId,
              onChange: (e) => setFormData((prev) => ({ ...prev, userId: e.target.value })),
              placeholder: "Enter user ID (e.g., jdoe, jane.doe)",
              required: true,
              autoFocus: true
            }
          ),
          /* @__PURE__ */ jsx24("p", { className: "text-xs text-muted-foreground", children: "The unique identifier for this user (e.g., username or email)" })
        ] }),
        !defaultOrgId && /* @__PURE__ */ jsxs20("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsx24(Label5, { htmlFor: "org_id", children: "Organization ID (Optional)" }),
          /* @__PURE__ */ jsx24(
            Input5,
            {
              id: "org_id",
              value: formData.orgId,
              onChange: (e) => setFormData((prev) => ({ ...prev, orgId: e.target.value })),
              placeholder: "Enter organization ID"
            }
          ),
          /* @__PURE__ */ jsx24("p", { className: "text-xs text-muted-foreground", children: "Leave empty to create a user without organization assignment" })
        ] }),
        defaultOrgId && /* @__PURE__ */ jsxs20("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsx24(Label5, { htmlFor: "org_id_display", children: "Organization" }),
          /* @__PURE__ */ jsx24(
            Input5,
            {
              id: "org_id_display",
              value: defaultOrgId,
              disabled: true,
              className: "bg-muted"
            }
          ),
          /* @__PURE__ */ jsx24("p", { className: "text-xs text-muted-foreground", children: "User will be created in this organization" })
        ] })
      ] }),
      /* @__PURE__ */ jsxs20(DialogFooter5, { children: [
        /* @__PURE__ */ jsx24(
          Button12,
          {
            type: "button",
            variant: "outline",
            onClick: () => onOpenChange(false),
            disabled: isPending,
            children: "Cancel"
          }
        ),
        /* @__PURE__ */ jsx24(Button12, { type: "submit", disabled: isPending, children: isPending ? "Creating..." : "Create User" })
      ] })
    ] })
  ] }) });
}

// src/components/users/EditUserDialog.tsx
import { useState as useState16, useEffect as useEffect8 } from "react";
import {
  Dialog as Dialog8,
  DialogContent as DialogContent8,
  DialogDescription as DialogDescription8,
  DialogHeader as DialogHeader8,
  DialogTitle as DialogTitle8,
  DialogFooter as DialogFooter6
} from "@umd-dit-ai-workspace/shared-ui";
import { Button as Button13 } from "@umd-dit-ai-workspace/shared-ui";
import { Label as Label6 } from "@umd-dit-ai-workspace/shared-ui";
import { Input as Input6 } from "@umd-dit-ai-workspace/shared-ui";
import { Switch } from "@umd-dit-ai-workspace/shared-ui";
import { jsx as jsx25, jsxs as jsxs21 } from "react/jsx-runtime";
function EditUserDialog({
  user,
  open,
  onOpenChange,
  onSave,
  isPending
}) {
  const [isGlobalAdmin, setIsGlobalAdmin] = useState16(user.isGlobalAdmin || false);
  useEffect8(() => {
    setIsGlobalAdmin(user.isGlobalAdmin || false);
  }, [user]);
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ isGlobalAdmin });
  };
  return /* @__PURE__ */ jsx25(Dialog8, { open, onOpenChange, children: /* @__PURE__ */ jsxs21(DialogContent8, { className: "max-w-md", children: [
    /* @__PURE__ */ jsxs21(DialogHeader8, { children: [
      /* @__PURE__ */ jsx25(DialogTitle8, { children: "Edit User" }),
      /* @__PURE__ */ jsx25(DialogDescription8, { children: "Update user permissions and settings." })
    ] }),
    /* @__PURE__ */ jsxs21("form", { onSubmit: handleSubmit, children: [
      /* @__PURE__ */ jsxs21("div", { className: "space-y-4 py-4", children: [
        /* @__PURE__ */ jsxs21("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsx25(Label6, { htmlFor: "user_id_display", children: "User ID" }),
          /* @__PURE__ */ jsx25(
            Input6,
            {
              id: "user_id_display",
              value: user.userId,
              disabled: true,
              className: "bg-muted"
            }
          ),
          /* @__PURE__ */ jsx25("p", { className: "text-xs text-muted-foreground", children: "User ID cannot be changed" })
        ] }),
        /* @__PURE__ */ jsxs21("div", { className: "flex items-center justify-between space-x-2 rounded-lg border p-4", children: [
          /* @__PURE__ */ jsxs21("div", { className: "flex-1 space-y-1", children: [
            /* @__PURE__ */ jsx25(Label6, { htmlFor: "is_global_admin", className: "font-medium", children: "Global Administrator" }),
            /* @__PURE__ */ jsx25("p", { className: "text-xs text-muted-foreground", children: "Grant this user full administrative access to all organizations and workspaces" })
          ] }),
          /* @__PURE__ */ jsx25(
            Switch,
            {
              id: "is_global_admin",
              checked: isGlobalAdmin,
              onCheckedChange: setIsGlobalAdmin
            }
          )
        ] })
      ] }),
      /* @__PURE__ */ jsxs21(DialogFooter6, { children: [
        /* @__PURE__ */ jsx25(
          Button13,
          {
            type: "button",
            variant: "outline",
            onClick: () => onOpenChange(false),
            disabled: isPending,
            children: "Cancel"
          }
        ),
        /* @__PURE__ */ jsx25(Button13, { type: "submit", disabled: isPending, children: isPending ? "Saving..." : "Save Changes" })
      ] })
    ] })
  ] }) });
}

// src/components/users/DeleteUserDialog.tsx
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@umd-dit-ai-workspace/shared-ui";
import { jsx as jsx26, jsxs as jsxs22 } from "react/jsx-runtime";
function DeleteUserDialog({
  user,
  open,
  onOpenChange,
  onDelete,
  isPending
}) {
  return /* @__PURE__ */ jsx26(AlertDialog, { open, onOpenChange, children: /* @__PURE__ */ jsxs22(AlertDialogContent, { children: [
    /* @__PURE__ */ jsxs22(AlertDialogHeader, { children: [
      /* @__PURE__ */ jsx26(AlertDialogTitle, { children: "Are you sure?" }),
      /* @__PURE__ */ jsxs22(AlertDialogDescription, { children: [
        "This will permanently delete the user ",
        /* @__PURE__ */ jsx26("strong", { children: user.userId }),
        " and remove all their access to organizations and workspaces. This action cannot be undone."
      ] })
    ] }),
    /* @__PURE__ */ jsxs22(AlertDialogFooter, { children: [
      /* @__PURE__ */ jsx26(AlertDialogCancel, { disabled: isPending, children: "Cancel" }),
      /* @__PURE__ */ jsx26(
        AlertDialogAction,
        {
          onClick: onDelete,
          disabled: isPending,
          className: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
          children: isPending ? "Deleting..." : "Delete User"
        }
      )
    ] })
  ] }) });
}

// src/components/users/UserMembershipsModal.tsx
import { useState as useState17, useMemo as useMemo5 } from "react";
import { useQuery as useQuery10, useMutation as useMutation6, useQueryClient as useQueryClient10 } from "@tanstack/react-query";
import {
  Dialog as Dialog9,
  DialogContent as DialogContent9,
  DialogDescription as DialogDescription9,
  DialogHeader as DialogHeader9,
  DialogTitle as DialogTitle9,
  Badge as Badge3,
  Button as Button14,
  Card as Card6,
  CardContent as CardContent6,
  CardDescription as CardDescription4,
  CardHeader as CardHeader6,
  CardTitle as CardTitle6,
  Select as Select3,
  SelectContent as SelectContent3,
  SelectItem as SelectItem3,
  SelectTrigger as SelectTrigger3,
  SelectValue as SelectValue3,
  Switch as Switch2,
  Label as Label7,
  useToast as useToast4,
  SearchableSelect as SearchableSelect3
} from "@umd-dit-ai-workspace/shared-ui";
import { Building2 as Building25, FolderOpen as FolderOpen5, Shield as Shield6, User as UserIcon3, Plus as Plus4, Trash2 as Trash25, Edit2 as Edit23, X as X4 } from "lucide-react";
import { jsx as jsx27, jsxs as jsxs23 } from "react/jsx-runtime";
var UserMembershipsModal = ({
  userId,
  open,
  onOpenChange
}) => {
  const usersApi = useUsersApi();
  const organizationApi = useOrganizationApi();
  const workspaceApi = useWorkspaceApi();
  const queryClient2 = useQueryClient10();
  const { toast } = useToast4();
  const [addingOrgMode, setAddingOrgMode] = useState17(false);
  const [addingWorkspaceMode, setAddingWorkspaceMode] = useState17(false);
  const [selectedOrgId, setSelectedOrgId] = useState17("");
  const [selectedWsId, setSelectedWsId] = useState17("");
  const [selectedOrgRole, setSelectedOrgRole] = useState17("member");
  const [selectedWsRole, setSelectedWsRole] = useState17("member");
  const [editingOrgId, setEditingOrgId] = useState17(null);
  const [editingWsId, setEditingWsId] = useState17(null);
  const [wsOrgFilter, setWsOrgFilter] = useState17("");
  const { data: userData, isLoading } = useQuery10({
    queryKey: ["user-profile", userId],
    queryFn: () => usersApi.getUser(userId),
    enabled: open
  });
  const { data: orgMemberships = [] } = useQuery10({
    queryKey: ["user-org-memberships", userId],
    queryFn: () => usersApi.getUserOrganizations(userId),
    enabled: open
  });
  const { data: allOrgs = [] } = useQuery10({
    queryKey: ["organizations"],
    queryFn: () => organizationApi.listOrganizations(),
    enabled: open
  });
  const workspaceMembershipsQueries = useQuery10({
    queryKey: ["user-workspace-memberships", userId, orgMemberships.map((om) => om.orgId).join(",")],
    queryFn: async () => {
      if (orgMemberships.length === 0)
        return [];
      const workspacesByOrg = await Promise.all(
        orgMemberships.map((om) => usersApi.getUserWorkspacesInOrg(userId, om.orgId))
      );
      return workspacesByOrg.flat();
    },
    enabled: open && orgMemberships.length > 0
  });
  const workspaceMemberships = useMemo5(
    () => workspaceMembershipsQueries.data || [],
    [workspaceMembershipsQueries.data]
  );
  const { data: availableWorkspacesForOrg = [] } = useQuery10({
    queryKey: ["available-workspaces-for-org", wsOrgFilter],
    queryFn: async () => {
      if (!wsOrgFilter)
        return [];
      return workspaceApi.adminListWorkspaces(wsOrgFilter);
    },
    enabled: open && !!wsOrgFilter
  });
  const groupedWorkspaces = useMemo5(() => {
    return workspaceMemberships.reduce((acc, ws) => {
      if (!acc[ws.orgId]) {
        acc[ws.orgId] = [];
      }
      acc[ws.orgId].push(ws);
      return acc;
    }, {});
  }, [workspaceMemberships]);
  const availableOrgOptions = useMemo5(() => {
    const memberOrgIds = new Set(orgMemberships.map((m) => m.orgId));
    return allOrgs.filter((org) => !memberOrgIds.has(org.orgId)).map((org) => ({
      value: org.orgId,
      label: `${org.name} (${org.orgId})`
    }));
  }, [allOrgs, orgMemberships]);
  const availableWorkspaceOptions = useMemo5(() => {
    const memberWsKeys = new Set(workspaceMemberships.map((m) => `${m.orgId}:${m.wsId}`));
    const filtered = availableWorkspacesForOrg.filter((ws) => !memberWsKeys.has(`${ws.orgId}:${ws.wsId}`));
    return filtered.map((ws) => ({
      value: `${ws.orgId}:${ws.wsId}`,
      label: `${ws.name} (${ws.orgId}/${ws.wsId})`
    }));
  }, [availableWorkspacesForOrg, workspaceMemberships]);
  const orgFilterOptions = useMemo5(() => {
    const userOrgIds = new Set(orgMemberships.map((m) => m.orgId));
    const userOrgs = allOrgs.filter((org) => userOrgIds.has(org.orgId));
    return userOrgs.map((org) => ({
      value: org.orgId,
      label: `${org.name} (${org.orgId})`
    }));
  }, [allOrgs, orgMemberships]);
  const getRoleBadgeVariant = (role) => {
    return role === "admin" ? "default" : "secondary";
  };
  const addOrgMutation = useMutation6({
    mutationFn: ({ orgId, role }) => usersApi.addUserToOrg(orgId, userId, role),
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["user-org-memberships", userId] });
      queryClient2.invalidateQueries({ queryKey: ["user-workspace-memberships", userId] });
      toast({ title: "Success", description: "Organization membership added" });
      setAddingOrgMode(false);
      setSelectedOrgId("");
      setSelectedOrgRole("member");
    },
    onError: (error) => {
      toast({ title: "Error", description: error instanceof Error ? error.message : "Failed to add membership", variant: "destructive" });
    }
  });
  const removeOrgMutation = useMutation6({
    mutationFn: (orgId) => usersApi.removeUserFromOrg(orgId, userId),
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["user-org-memberships", userId] });
      queryClient2.invalidateQueries({ queryKey: ["user-workspace-memberships", userId] });
      toast({ title: "Success", description: "Organization membership removed" });
    },
    onError: (error) => {
      toast({ title: "Error", description: error instanceof Error ? error.message : "Failed to remove membership", variant: "destructive" });
    }
  });
  const updateOrgRoleMutation = useMutation6({
    mutationFn: ({ orgId, role }) => usersApi.updateUserOrgRole(orgId, userId, role),
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["user-org-memberships", userId] });
      toast({ title: "Success", description: "Organization role updated" });
      setEditingOrgId(null);
    },
    onError: (error) => {
      toast({ title: "Error", description: error instanceof Error ? error.message : "Failed to update role", variant: "destructive" });
    }
  });
  const addWorkspaceMutation = useMutation6({
    mutationFn: ({ orgId, wsId, role }) => usersApi.addUserToWorkspace(orgId, wsId, userId, role),
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["user-workspace-memberships", userId] });
      toast({ title: "Success", description: "Workspace membership added" });
      setAddingWorkspaceMode(false);
      setSelectedWsId("");
      setSelectedWsRole("member");
      setWsOrgFilter("");
    },
    onError: (error) => {
      toast({ title: "Error", description: error instanceof Error ? error.message : "Failed to add membership", variant: "destructive" });
    }
  });
  const removeWorkspaceMutation = useMutation6({
    mutationFn: ({ orgId, wsId }) => usersApi.removeUserFromWorkspace(orgId, wsId, userId),
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["user-workspace-memberships", userId] });
      toast({ title: "Success", description: "Workspace membership removed" });
    },
    onError: (error) => {
      toast({ title: "Error", description: error instanceof Error ? error.message : "Failed to remove membership", variant: "destructive" });
    }
  });
  const updateWorkspaceRoleMutation = useMutation6({
    mutationFn: ({ orgId, wsId, role }) => usersApi.updateUserWorkspaceRole(orgId, wsId, userId, role),
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["user-workspace-memberships", userId] });
      toast({ title: "Success", description: "Workspace role updated" });
      setEditingWsId(null);
    },
    onError: (error) => {
      toast({ title: "Error", description: error instanceof Error ? error.message : "Failed to update role", variant: "destructive" });
    }
  });
  const updateGlobalAdminMutation = useMutation6({
    mutationFn: (isGlobalAdmin) => usersApi.updateUser({ userId, isGlobalAdmin }),
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["user-profile", userId] });
      queryClient2.invalidateQueries({ queryKey: ["users"] });
      toast({ title: "Success", description: "Global admin status updated" });
    },
    onError: (error) => {
      toast({ title: "Error", description: error instanceof Error ? error.message : "Failed to update global admin status", variant: "destructive" });
    }
  });
  return /* @__PURE__ */ jsx27(Dialog9, { open, onOpenChange, children: /* @__PURE__ */ jsxs23(DialogContent9, { className: "max-w-3xl max-h-[85vh] overflow-y-auto", children: [
    /* @__PURE__ */ jsxs23(DialogHeader9, { children: [
      /* @__PURE__ */ jsxs23(DialogTitle9, { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx27(UserIcon3, { className: "h-5 w-5" }),
        "User Memberships"
      ] }),
      /* @__PURE__ */ jsx27(DialogDescription9, { children: userId })
    ] }),
    isLoading ? /* @__PURE__ */ jsx27("div", { className: "text-center py-8", children: "Loading memberships..." }) : /* @__PURE__ */ jsxs23("div", { className: "space-y-6", children: [
      /* @__PURE__ */ jsxs23(Card6, { children: [
        /* @__PURE__ */ jsxs23(CardHeader6, { children: [
          /* @__PURE__ */ jsxs23(CardTitle6, { className: "flex items-center gap-2 text-lg", children: [
            /* @__PURE__ */ jsx27(Shield6, { className: "h-4 w-4" }),
            "Global Admin Status"
          ] }),
          /* @__PURE__ */ jsx27(CardDescription4, { children: "Global admins have full access to all organizations and workspaces" })
        ] }),
        /* @__PURE__ */ jsx27(CardContent6, { children: /* @__PURE__ */ jsxs23("div", { className: "flex items-center justify-between", children: [
          /* @__PURE__ */ jsxs23("div", { className: "flex items-center space-x-2", children: [
            /* @__PURE__ */ jsx27(
              Switch2,
              {
                id: "global-admin",
                checked: userData?.isGlobalAdmin || false,
                onCheckedChange: (checked) => updateGlobalAdminMutation.mutate(checked),
                disabled: updateGlobalAdminMutation.isPending
              }
            ),
            /* @__PURE__ */ jsx27(Label7, { htmlFor: "global-admin", className: "cursor-pointer", children: userData?.isGlobalAdmin ? "Global Admin" : "Regular User" })
          ] }),
          userData?.isGlobalAdmin && /* @__PURE__ */ jsxs23(Badge3, { variant: "default", children: [
            /* @__PURE__ */ jsx27(Shield6, { className: "h-3 w-3 mr-1" }),
            "Active"
          ] })
        ] }) })
      ] }),
      /* @__PURE__ */ jsxs23(Card6, { children: [
        /* @__PURE__ */ jsx27(CardHeader6, { children: /* @__PURE__ */ jsxs23("div", { className: "flex items-center justify-between", children: [
          /* @__PURE__ */ jsxs23("div", { children: [
            /* @__PURE__ */ jsxs23(CardTitle6, { className: "flex items-center gap-2 text-lg", children: [
              /* @__PURE__ */ jsx27(Building25, { className: "h-4 w-4" }),
              "Organizations"
            ] }),
            /* @__PURE__ */ jsxs23(CardDescription4, { children: [
              orgMemberships.length,
              " organization",
              orgMemberships.length !== 1 ? "s" : ""
            ] })
          ] }),
          !addingOrgMode && /* @__PURE__ */ jsxs23(Button14, { size: "sm", onClick: () => setAddingOrgMode(true), children: [
            /* @__PURE__ */ jsx27(Plus4, { className: "h-4 w-4 mr-1" }),
            "Add"
          ] })
        ] }) }),
        /* @__PURE__ */ jsxs23(CardContent6, { children: [
          addingOrgMode && /* @__PURE__ */ jsxs23("div", { className: "mb-4 p-4 border rounded-md bg-accent/10 space-y-3", children: [
            /* @__PURE__ */ jsxs23("div", { className: "flex items-center justify-between", children: [
              /* @__PURE__ */ jsx27("h4", { className: "text-sm font-semibold", children: "Add Organization Membership" }),
              /* @__PURE__ */ jsx27(Button14, { size: "sm", variant: "ghost", onClick: () => {
                setAddingOrgMode(false);
                setSelectedOrgId("");
              }, children: /* @__PURE__ */ jsx27(X4, { className: "h-4 w-4" }) })
            ] }),
            /* @__PURE__ */ jsxs23("div", { className: "space-y-2", children: [
              /* @__PURE__ */ jsx27(
                SearchableSelect3,
                {
                  options: availableOrgOptions,
                  value: selectedOrgId,
                  onValueChange: setSelectedOrgId,
                  placeholder: "Select organization",
                  searchPlaceholder: "Search organizations...",
                  emptyMessage: "No organizations available"
                }
              ),
              /* @__PURE__ */ jsxs23(Select3, { value: selectedOrgRole, onValueChange: setSelectedOrgRole, children: [
                /* @__PURE__ */ jsx27(SelectTrigger3, { children: /* @__PURE__ */ jsx27(SelectValue3, { placeholder: "Select role" }) }),
                /* @__PURE__ */ jsxs23(SelectContent3, { children: [
                  /* @__PURE__ */ jsx27(SelectItem3, { value: "member", children: "Member" }),
                  /* @__PURE__ */ jsx27(SelectItem3, { value: "admin", children: "Admin" })
                ] })
              ] }),
              /* @__PURE__ */ jsx27(
                Button14,
                {
                  onClick: () => addOrgMutation.mutate({ orgId: selectedOrgId, role: selectedOrgRole }),
                  disabled: !selectedOrgId || addOrgMutation.isPending,
                  className: "w-full",
                  children: addOrgMutation.isPending ? "Adding..." : "Add Organization"
                }
              )
            ] })
          ] }),
          orgMemberships.length === 0 ? /* @__PURE__ */ jsx27("p", { className: "text-sm text-muted-foreground", children: "No organization memberships" }) : /* @__PURE__ */ jsx27("div", { className: "space-y-2", children: orgMemberships.map((org) => /* @__PURE__ */ jsxs23(
            "div",
            {
              className: "flex items-center justify-between p-3 rounded-md border bg-card",
              children: [
                /* @__PURE__ */ jsxs23("div", { className: "flex items-center gap-2 flex-1", children: [
                  /* @__PURE__ */ jsx27(Building25, { className: "h-4 w-4 text-muted-foreground" }),
                  /* @__PURE__ */ jsx27("span", { className: "font-medium", children: org.orgId })
                ] }),
                editingOrgId === org.orgId ? /* @__PURE__ */ jsxs23("div", { className: "flex items-center gap-2", children: [
                  /* @__PURE__ */ jsxs23(
                    Select3,
                    {
                      value: org.role,
                      onValueChange: (role) => updateOrgRoleMutation.mutate({ orgId: org.orgId, role }),
                      children: [
                        /* @__PURE__ */ jsx27(SelectTrigger3, { className: "w-32", children: /* @__PURE__ */ jsx27(SelectValue3, {}) }),
                        /* @__PURE__ */ jsxs23(SelectContent3, { children: [
                          /* @__PURE__ */ jsx27(SelectItem3, { value: "member", children: "Member" }),
                          /* @__PURE__ */ jsx27(SelectItem3, { value: "admin", children: "Admin" })
                        ] })
                      ]
                    }
                  ),
                  /* @__PURE__ */ jsx27(Button14, { size: "sm", variant: "ghost", onClick: () => setEditingOrgId(null), children: /* @__PURE__ */ jsx27(X4, { className: "h-4 w-4" }) })
                ] }) : /* @__PURE__ */ jsxs23("div", { className: "flex items-center gap-2", children: [
                  /* @__PURE__ */ jsxs23(Badge3, { variant: getRoleBadgeVariant(org.role), children: [
                    org.role === "admin" && /* @__PURE__ */ jsx27(Shield6, { className: "h-3 w-3 mr-1" }),
                    org.role
                  ] }),
                  /* @__PURE__ */ jsx27(Button14, { size: "sm", variant: "ghost", onClick: () => setEditingOrgId(org.orgId), children: /* @__PURE__ */ jsx27(Edit23, { className: "h-3 w-3" }) }),
                  /* @__PURE__ */ jsx27(
                    Button14,
                    {
                      size: "sm",
                      variant: "ghost",
                      onClick: () => removeOrgMutation.mutate(org.orgId),
                      disabled: removeOrgMutation.isPending,
                      children: /* @__PURE__ */ jsx27(Trash25, { className: "h-3 w-3 text-destructive" })
                    }
                  )
                ] })
              ]
            },
            org.orgId
          )) })
        ] })
      ] }),
      /* @__PURE__ */ jsxs23(Card6, { children: [
        /* @__PURE__ */ jsx27(CardHeader6, { children: /* @__PURE__ */ jsxs23("div", { className: "flex items-center justify-between", children: [
          /* @__PURE__ */ jsxs23("div", { children: [
            /* @__PURE__ */ jsxs23(CardTitle6, { className: "flex items-center gap-2 text-lg", children: [
              /* @__PURE__ */ jsx27(FolderOpen5, { className: "h-4 w-4" }),
              "Workspaces"
            ] }),
            /* @__PURE__ */ jsxs23(CardDescription4, { children: [
              workspaceMemberships.length,
              " workspace",
              workspaceMemberships.length !== 1 ? "s" : ""
            ] })
          ] }),
          !addingWorkspaceMode && /* @__PURE__ */ jsxs23(Button14, { size: "sm", onClick: () => setAddingWorkspaceMode(true), children: [
            /* @__PURE__ */ jsx27(Plus4, { className: "h-4 w-4 mr-1" }),
            "Add"
          ] })
        ] }) }),
        /* @__PURE__ */ jsxs23(CardContent6, { children: [
          addingWorkspaceMode && /* @__PURE__ */ jsxs23("div", { className: "mb-4 p-4 border rounded-md bg-accent/10 space-y-3", children: [
            /* @__PURE__ */ jsxs23("div", { className: "flex items-center justify-between", children: [
              /* @__PURE__ */ jsx27("h4", { className: "text-sm font-semibold", children: "Add Workspace Membership" }),
              /* @__PURE__ */ jsx27(Button14, { size: "sm", variant: "ghost", onClick: () => {
                setAddingWorkspaceMode(false);
                setSelectedWsId("");
                setWsOrgFilter("");
              }, children: /* @__PURE__ */ jsx27(X4, { className: "h-4 w-4" }) })
            ] }),
            /* @__PURE__ */ jsxs23("div", { className: "space-y-2", children: [
              /* @__PURE__ */ jsxs23("div", { className: "space-y-1", children: [
                /* @__PURE__ */ jsx27(Label7, { htmlFor: "org-filter", className: "text-xs text-muted-foreground", children: "Organization *" }),
                /* @__PURE__ */ jsx27(
                  SearchableSelect3,
                  {
                    options: orgFilterOptions,
                    value: wsOrgFilter,
                    onValueChange: setWsOrgFilter,
                    placeholder: "Select organization first",
                    searchPlaceholder: "Search organizations...",
                    emptyMessage: "User must be member of an organization first"
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs23("div", { className: "space-y-1", children: [
                /* @__PURE__ */ jsx27(Label7, { htmlFor: "workspace-select", className: "text-xs text-muted-foreground", children: "Workspace *" }),
                /* @__PURE__ */ jsx27(
                  SearchableSelect3,
                  {
                    options: availableWorkspaceOptions,
                    value: selectedWsId,
                    onValueChange: setSelectedWsId,
                    placeholder: wsOrgFilter ? "Select workspace" : "Select organization first",
                    searchPlaceholder: "Search workspaces...",
                    emptyMessage: wsOrgFilter ? "No workspaces available" : "Select an organization first",
                    disabled: !wsOrgFilter
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs23(Select3, { value: selectedWsRole, onValueChange: setSelectedWsRole, children: [
                /* @__PURE__ */ jsx27(SelectTrigger3, { children: /* @__PURE__ */ jsx27(SelectValue3, { placeholder: "Select role" }) }),
                /* @__PURE__ */ jsxs23(SelectContent3, { children: [
                  /* @__PURE__ */ jsx27(SelectItem3, { value: "member", children: "Member" }),
                  /* @__PURE__ */ jsx27(SelectItem3, { value: "admin", children: "Admin" })
                ] })
              ] }),
              /* @__PURE__ */ jsx27(
                Button14,
                {
                  onClick: () => {
                    const [orgId, wsId] = selectedWsId.split(":");
                    addWorkspaceMutation.mutate({ orgId, wsId, role: selectedWsRole });
                  },
                  disabled: !selectedWsId || addWorkspaceMutation.isPending,
                  className: "w-full",
                  children: addWorkspaceMutation.isPending ? "Adding..." : "Add Workspace"
                }
              )
            ] })
          ] }),
          workspaceMemberships.length === 0 ? /* @__PURE__ */ jsx27("p", { className: "text-sm text-muted-foreground", children: "No workspace memberships" }) : /* @__PURE__ */ jsx27("div", { className: "space-y-4", children: Object.entries(groupedWorkspaces).map(([org_id, workspaces]) => /* @__PURE__ */ jsxs23("div", { className: "space-y-2", children: [
            /* @__PURE__ */ jsxs23("div", { className: "flex items-center gap-2 text-sm font-semibold text-muted-foreground", children: [
              /* @__PURE__ */ jsx27(Building25, { className: "h-3 w-3" }),
              org_id
            ] }),
            /* @__PURE__ */ jsx27("div", { className: "space-y-1 ml-5", children: workspaces.map((ws) => {
              const wsKey = `${ws.orgId}:${ws.wsId}`;
              return /* @__PURE__ */ jsxs23(
                "div",
                {
                  className: "flex items-center justify-between p-2 rounded-md border bg-card",
                  children: [
                    /* @__PURE__ */ jsxs23("div", { className: "flex items-center gap-2 flex-1", children: [
                      /* @__PURE__ */ jsx27(FolderOpen5, { className: "h-3 w-3 text-muted-foreground" }),
                      /* @__PURE__ */ jsx27("span", { className: "text-sm", children: ws.wsId })
                    ] }),
                    editingWsId === wsKey ? /* @__PURE__ */ jsxs23("div", { className: "flex items-center gap-2", children: [
                      /* @__PURE__ */ jsxs23(
                        Select3,
                        {
                          value: ws.role,
                          onValueChange: (role) => updateWorkspaceRoleMutation.mutate({
                            orgId: ws.orgId,
                            wsId: ws.wsId,
                            role
                          }),
                          children: [
                            /* @__PURE__ */ jsx27(SelectTrigger3, { className: "w-28 h-7 text-xs", children: /* @__PURE__ */ jsx27(SelectValue3, {}) }),
                            /* @__PURE__ */ jsxs23(SelectContent3, { children: [
                              /* @__PURE__ */ jsx27(SelectItem3, { value: "member", children: "Member" }),
                              /* @__PURE__ */ jsx27(SelectItem3, { value: "admin", children: "Admin" })
                            ] })
                          ]
                        }
                      ),
                      /* @__PURE__ */ jsx27(Button14, { size: "sm", variant: "ghost", onClick: () => setEditingWsId(null), className: "h-7 w-7 p-0", children: /* @__PURE__ */ jsx27(X4, { className: "h-3 w-3" }) })
                    ] }) : /* @__PURE__ */ jsxs23("div", { className: "flex items-center gap-2", children: [
                      /* @__PURE__ */ jsxs23(Badge3, { variant: getRoleBadgeVariant(ws.role), className: "text-xs", children: [
                        ws.role === "admin" && /* @__PURE__ */ jsx27(Shield6, { className: "h-2 w-2 mr-1" }),
                        ws.role
                      ] }),
                      /* @__PURE__ */ jsx27(Button14, { size: "sm", variant: "ghost", onClick: () => setEditingWsId(wsKey), className: "h-7 w-7 p-0", children: /* @__PURE__ */ jsx27(Edit23, { className: "h-3 w-3" }) }),
                      /* @__PURE__ */ jsx27(
                        Button14,
                        {
                          size: "sm",
                          variant: "ghost",
                          onClick: () => removeWorkspaceMutation.mutate({ orgId: ws.orgId, wsId: ws.wsId }),
                          disabled: removeWorkspaceMutation.isPending,
                          className: "h-7 w-7 p-0",
                          children: /* @__PURE__ */ jsx27(Trash25, { className: "h-3 w-3 text-destructive" })
                        }
                      )
                    ] })
                  ]
                },
                wsKey
              );
            }) })
          ] }, org_id)) })
        ] })
      ] })
    ] })
  ] }) });
};

// src/components/users/UserManagement.tsx
import { Fragment as Fragment7, jsx as jsx28, jsxs as jsxs24 } from "react/jsx-runtime";
var UserManagementInner = ({
  org_id,
  className
}) => {
  const [searchQuery, setSearchQuery] = useState18("");
  const [createDialogOpen, setCreateDialogOpen] = useState18(false);
  const [editDialogOpen, setEditDialogOpen] = useState18(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState18(false);
  const [membershipsDialogOpen, setMembershipsDialogOpen] = useState18(false);
  const [selectedUser, setSelectedUser] = useState18(null);
  const queryClient2 = useQueryClient11();
  const { toast } = useToast5();
  const usersApi = useUsersApi();
  const { data: users, isLoading } = useQuery11({
    queryKey: org_id ? ["users", org_id] : ["users", "all"],
    queryFn: () => org_id ? usersApi.getOrgUsers(org_id) : usersApi.getAllUsers()
  });
  const createUserMutation = useMutation7({
    mutationFn: ({ userId }) => usersApi.createUser(userId),
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["users"] });
      toast({
        title: "User created",
        description: "The user has been created successfully."
      });
      setCreateDialogOpen(false);
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to create user";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });
  const updateUserMutation = useMutation7({
    mutationFn: (data) => usersApi.updateUser(data),
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["users"] });
      toast({
        title: "User updated",
        description: "The user has been updated successfully."
      });
      setEditDialogOpen(false);
      setSelectedUser(null);
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to update user";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });
  const deleteUserMutation = useMutation7({
    mutationFn: (userId) => usersApi.deleteUser(userId),
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["users"] });
      toast({
        title: "User deleted",
        description: "The user has been deleted successfully."
      });
      setDeleteDialogOpen(false);
      setSelectedUser(null);
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to delete user";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });
  const filteredUsers = React11.useMemo(() => {
    if (!users)
      return [];
    if (!searchQuery)
      return users;
    const query = searchQuery.toLowerCase();
    return users.filter(
      (user) => user.userId.toLowerCase().includes(query)
    );
  }, [users, searchQuery]);
  return /* @__PURE__ */ jsxs24("div", { className: cn6("user-management", className), children: [
    /* @__PURE__ */ jsxs24("div", { className: "space-y-6", children: [
      /* @__PURE__ */ jsxs24("div", { className: "flex items-center justify-between", children: [
        /* @__PURE__ */ jsxs24("div", { children: [
          /* @__PURE__ */ jsx28("h2", { className: "text-2xl font-bold", children: "User Management" }),
          /* @__PURE__ */ jsx28("p", { className: "text-muted-foreground", children: org_id ? `Manage users in this organization` : "Manage all users in the system" })
        ] }),
        /* @__PURE__ */ jsxs24(Button15, { onClick: () => setCreateDialogOpen(true), children: [
          /* @__PURE__ */ jsx28(UserPlus, { className: "mr-2 h-4 w-4" }),
          "Create User"
        ] })
      ] }),
      /* @__PURE__ */ jsx28("div", { className: "w-full max-w-md", children: /* @__PURE__ */ jsxs24("div", { className: "flex items-center gap-2 px-3 py-1.5 text-sm border rounded-md bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2", children: [
        /* @__PURE__ */ jsx28(Search3, { className: "h-3.5 w-3.5 text-muted-foreground shrink-0" }),
        /* @__PURE__ */ jsx28(
          "input",
          {
            type: "search",
            placeholder: "Search users...",
            value: searchQuery,
            onChange: (e) => setSearchQuery(e.target.value),
            className: "flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
          }
        )
      ] }) }),
      isLoading ? /* @__PURE__ */ jsx28("div", { className: "text-center py-8", children: "Loading users..." }) : filteredUsers.length === 0 ? /* @__PURE__ */ jsx28("div", { className: "text-center py-8 text-muted-foreground", children: searchQuery ? "No users found matching your search." : "No users found." }) : /* @__PURE__ */ jsxs24(Card7, { children: [
        /* @__PURE__ */ jsxs24(CardHeader7, { children: [
          /* @__PURE__ */ jsx28(CardTitle7, { children: "Users" }),
          /* @__PURE__ */ jsxs24(CardDescription5, { children: [
            filteredUsers.length,
            " user",
            filteredUsers.length !== 1 ? "s" : "",
            " found"
          ] })
        ] }),
        /* @__PURE__ */ jsx28(CardContent7, { children: /* @__PURE__ */ jsx28("div", { className: "rounded-md border", children: /* @__PURE__ */ jsxs24(Table3, { children: [
          /* @__PURE__ */ jsx28(TableHeader3, { children: /* @__PURE__ */ jsxs24(TableRow3, { children: [
            /* @__PURE__ */ jsx28(TableHead3, { className: "text-xs", children: "User" }),
            /* @__PURE__ */ jsx28(TableHead3, { className: "text-xs", children: "Global Admin" }),
            /* @__PURE__ */ jsx28(TableHead3, { className: "text-xs", children: "Memberships" }),
            /* @__PURE__ */ jsx28(TableHead3, { className: "text-xs w-[100px]", children: "Delete" })
          ] }) }),
          /* @__PURE__ */ jsx28(TableBody3, { children: filteredUsers.map((user) => /* @__PURE__ */ jsxs24(TableRow3, { children: [
            /* @__PURE__ */ jsx28(TableCell3, { className: "font-medium", children: /* @__PURE__ */ jsxs24("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx28(UserIcon4, { className: "h-4 w-4 text-muted-foreground" }),
              /* @__PURE__ */ jsx28("div", { children: /* @__PURE__ */ jsx28("div", { children: user.userId }) })
            ] }) }),
            /* @__PURE__ */ jsx28(TableCell3, { children: user.isGlobalAdmin ? /* @__PURE__ */ jsxs24("div", { className: "flex items-center gap-1", children: [
              /* @__PURE__ */ jsx28(Shield7, { className: "h-3 w-3 text-blue-500" }),
              /* @__PURE__ */ jsx28("span", { className: "text-xs text-blue-500", children: "Yes" })
            ] }) : /* @__PURE__ */ jsx28("span", { className: "text-xs text-muted-foreground", children: "No" }) }),
            /* @__PURE__ */ jsx28(TableCell3, { children: /* @__PURE__ */ jsxs24(
              Button15,
              {
                variant: "ghost",
                size: "sm",
                onClick: () => {
                  setSelectedUser(user);
                  setMembershipsDialogOpen(true);
                },
                children: [
                  /* @__PURE__ */ jsx28(Users4, { className: "h-4 w-4 mr-1" }),
                  "View & Edit"
                ]
              }
            ) }),
            /* @__PURE__ */ jsx28(TableCell3, { children: /* @__PURE__ */ jsx28(
              Button15,
              {
                variant: "ghost",
                size: "sm",
                onClick: () => {
                  setSelectedUser(user);
                  setDeleteDialogOpen(true);
                },
                children: /* @__PURE__ */ jsx28(Trash26, { className: "h-4 w-4 text-destructive" })
              }
            ) })
          ] }, user.userId)) })
        ] }) }) })
      ] })
    ] }),
    /* @__PURE__ */ jsx28(
      CreateUserDialog,
      {
        open: createDialogOpen,
        onOpenChange: setCreateDialogOpen,
        onSave: (data) => {
          createUserMutation.mutate({ userId: data.userId });
        },
        isPending: createUserMutation.isPending,
        defaultOrgId: org_id
      }
    ),
    selectedUser && /* @__PURE__ */ jsxs24(Fragment7, { children: [
      /* @__PURE__ */ jsx28(
        EditUserDialog,
        {
          user: selectedUser,
          open: editDialogOpen,
          onOpenChange: (open) => {
            setEditDialogOpen(open);
            if (!open)
              setSelectedUser(null);
          },
          onSave: (data) => {
            updateUserMutation.mutate({ userId: selectedUser.userId, ...data });
          },
          isPending: updateUserMutation.isPending
        }
      ),
      /* @__PURE__ */ jsx28(
        DeleteUserDialog,
        {
          user: selectedUser,
          open: deleteDialogOpen,
          onOpenChange: (open) => {
            setDeleteDialogOpen(open);
            if (!open)
              setSelectedUser(null);
          },
          onDelete: () => {
            deleteUserMutation.mutate(selectedUser.userId);
          },
          isPending: deleteUserMutation.isPending
        }
      ),
      /* @__PURE__ */ jsx28(
        UserMembershipsModal,
        {
          userId: selectedUser.userId,
          open: membershipsDialogOpen,
          onOpenChange: (open) => {
            setMembershipsDialogOpen(open);
            if (!open)
              setSelectedUser(null);
          }
        }
      )
    ] })
  ] });
};
var UserManagement = ({
  app_id,
  org_id,
  className,
  showOrgFilter = false,
  debug = false
}) => {
  return /* @__PURE__ */ jsx28(AppConfigProvider, { app_id, debug, children: /* @__PURE__ */ jsx28(
    UserManagementInner,
    {
      org_id,
      className,
      showOrgFilter
    }
  ) });
};

// src/components/users/UserActionsDropdown.tsx
import {
  DropdownMenu as DropdownMenu6,
  DropdownMenuContent as DropdownMenuContent6,
  DropdownMenuItem as DropdownMenuItem7,
  DropdownMenuTrigger as DropdownMenuTrigger6
} from "@umd-dit-ai-workspace/shared-ui";
import { Button as Button16 } from "@umd-dit-ai-workspace/shared-ui";
import { MoreHorizontal as MoreHorizontal3, Pencil, Trash2 as Trash27 } from "lucide-react";
import { jsx as jsx29, jsxs as jsxs25 } from "react/jsx-runtime";

// src/components/llm/LLMManagement.tsx
import React14, { useState as useState21 } from "react";
import { useQuery as useQuery12, useMutation as useMutation8, useQueryClient as useQueryClient12 } from "@tanstack/react-query";
import { Button as Button19 } from "@umd-dit-ai-workspace/shared-ui";
import { Card as Card8, CardContent as CardContent8, CardDescription as CardDescription6, CardHeader as CardHeader8, CardTitle as CardTitle8 } from "@umd-dit-ai-workspace/shared-ui";
import {
  Dialog as Dialog12,
  DialogContent as DialogContent12,
  DialogDescription as DialogDescription12,
  DialogFooter as DialogFooter9,
  DialogHeader as DialogHeader12,
  DialogTitle as DialogTitle12
} from "@umd-dit-ai-workspace/shared-ui";
import { Switch as Switch5 } from "@umd-dit-ai-workspace/shared-ui";
import { Label as Label10 } from "@umd-dit-ai-workspace/shared-ui";
import {
  Table as Table4,
  TableBody as TableBody4,
  TableCell as TableCell4,
  TableHead as TableHead4,
  TableHeader as TableHeader4,
  TableRow as TableRow4
} from "@umd-dit-ai-workspace/shared-ui";
import { useToast as useToast6 } from "@umd-dit-ai-workspace/shared-ui";
import { cn as cn7 } from "@umd-dit-ai-workspace/shared-ui";
import { Badge as Badge4 } from "@umd-dit-ai-workspace/shared-ui";
import { Plus as Plus5, Search as Search4, Edit as Edit4, Trash2 as Trash28, Filter, Download, Upload, Copy } from "lucide-react";

// src/components/llm/CreateLLMDialog.tsx
import React12, { useState as useState19 } from "react";
import {
  Dialog as Dialog10,
  DialogContent as DialogContent10,
  DialogDescription as DialogDescription10,
  DialogFooter as DialogFooter7,
  DialogHeader as DialogHeader10,
  DialogTitle as DialogTitle10
} from "@umd-dit-ai-workspace/shared-ui";
import { Button as Button17 } from "@umd-dit-ai-workspace/shared-ui";
import { Input as Input7 } from "@umd-dit-ai-workspace/shared-ui";
import { Label as Label8 } from "@umd-dit-ai-workspace/shared-ui";
import { Switch as Switch3 } from "@umd-dit-ai-workspace/shared-ui";
import { jsx as jsx30, jsxs as jsxs26 } from "react/jsx-runtime";
var CreateLLMDialog = ({
  open,
  onOpenChange,
  onSave,
  isPending = false,
  defaultValues
}) => {
  const getInitialFormData = () => ({
    model_name: "",
    model_id: "",
    provider: "",
    display_name: "",
    is_active: true,
    caching_supported: false,
    reasoning_supported: false,
    vision_supported: false,
    input_token_cost: 0,
    output_token_cost: 0,
    cache_write_token_cost: 0,
    cache_read_token_cost: 0,
    unsupported_parameters: [],
    reasoning_effort: null,
    max_tokens: null,
    max_thinking_tokens: null,
    ...defaultValues
  });
  const [formData, setFormData] = useState19(getInitialFormData());
  const [unsupportedParamsInput, setUnsupportedParamsInput] = useState19("");
  const [errors, setErrors] = useState19({});
  React12.useEffect(() => {
    if (open) {
      const initialData = getInitialFormData();
      setFormData(initialData);
      setUnsupportedParamsInput(initialData.unsupported_parameters?.join(", ") || "");
      setErrors({});
    }
  }, [open, defaultValues]);
  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.model_name.trim()) {
      newErrors.model_name = "Model name is required";
    }
    if (!formData.model_id.trim()) {
      newErrors.model_id = "Model ID is required";
    }
    if (!formData.provider.trim()) {
      newErrors.provider = "Provider is required";
    }
    if (!formData.display_name.trim()) {
      newErrors.display_name = "Display name is required";
    }
    if (formData.input_token_cost < 0) {
      newErrors.input_token_cost = "Input token cost must be non-negative";
    }
    if (formData.output_token_cost < 0) {
      newErrors.output_token_cost = "Output token cost must be non-negative";
    }
    if (formData.cache_write_token_cost !== void 0 && formData.cache_write_token_cost < 0) {
      newErrors.cache_write_token_cost = "Cache write token cost must be non-negative";
    }
    if (formData.cache_read_token_cost !== void 0 && formData.cache_read_token_cost < 0) {
      newErrors.cache_read_token_cost = "Cache read token cost must be non-negative";
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onSave(formData);
  };
  const handleClose = () => {
    setFormData({
      model_name: "",
      model_id: "",
      provider: "",
      display_name: "",
      is_active: true,
      caching_supported: false,
      reasoning_supported: false,
      vision_supported: false,
      input_token_cost: 0,
      output_token_cost: 0,
      cache_write_token_cost: 0,
      cache_read_token_cost: 0,
      unsupported_parameters: [],
      reasoning_effort: null,
      max_tokens: null,
      max_thinking_tokens: null
    });
    setUnsupportedParamsInput("");
    setErrors({});
    onOpenChange(false);
  };
  return /* @__PURE__ */ jsx30(Dialog10, { open, onOpenChange: handleClose, children: /* @__PURE__ */ jsxs26(DialogContent10, { className: "sm:max-w-[700px] max-h-[90vh] flex flex-col", children: [
    /* @__PURE__ */ jsxs26(DialogHeader10, { children: [
      /* @__PURE__ */ jsx30(DialogTitle10, { children: "Add LLM Model" }),
      /* @__PURE__ */ jsx30(DialogDescription10, { children: "Configure a new LLM model. The model ID should match the identifier used by the provider." })
    ] }),
    /* @__PURE__ */ jsxs26("form", { onSubmit: handleSubmit, className: "flex flex-col flex-1 overflow-hidden", children: [
      /* @__PURE__ */ jsxs26("div", { className: "grid gap-4 py-4 px-1 overflow-y-auto pr-2", children: [
        /* @__PURE__ */ jsxs26("div", { className: "grid gap-2", children: [
          /* @__PURE__ */ jsxs26(Label8, { htmlFor: "model_name", children: [
            "Model Name ",
            /* @__PURE__ */ jsx30("span", { className: "text-destructive", children: "*" })
          ] }),
          /* @__PURE__ */ jsx30(
            Input7,
            {
              id: "model_name",
              placeholder: "e.g., bedrock_claude_45_sonnet",
              value: formData.model_name,
              onChange: (e) => {
                setFormData({ ...formData, model_name: e.target.value });
                setErrors({ ...errors, model_name: "" });
              },
              disabled: isPending,
              className: errors.model_name ? "border-destructive" : ""
            }
          ),
          errors.model_name && /* @__PURE__ */ jsx30("p", { className: "text-sm text-destructive", children: errors.model_name }),
          /* @__PURE__ */ jsx30("p", { className: "text-xs text-muted-foreground", children: "A unique identifier for this model configuration (e.g., 'bedrock_claude_45_sonnet')" })
        ] }),
        /* @__PURE__ */ jsxs26("div", { className: "grid gap-2", children: [
          /* @__PURE__ */ jsxs26(Label8, { htmlFor: "model_id", children: [
            "Model ID ",
            /* @__PURE__ */ jsx30("span", { className: "text-destructive", children: "*" })
          ] }),
          /* @__PURE__ */ jsx30(
            Input7,
            {
              id: "model_id",
              placeholder: "e.g., gpt-4, claude-3-opus-20240229",
              value: formData.model_id,
              onChange: (e) => {
                setFormData({ ...formData, model_id: e.target.value });
                setErrors({ ...errors, model_id: "" });
              },
              disabled: isPending,
              className: errors.model_id ? "border-destructive" : ""
            }
          ),
          errors.model_id && /* @__PURE__ */ jsx30("p", { className: "text-sm text-destructive", children: errors.model_id }),
          /* @__PURE__ */ jsx30("p", { className: "text-xs text-muted-foreground", children: "The unique identifier for this model as used by the provider" })
        ] }),
        /* @__PURE__ */ jsxs26("div", { className: "grid gap-2", children: [
          /* @__PURE__ */ jsxs26(Label8, { htmlFor: "provider", children: [
            "Provider ",
            /* @__PURE__ */ jsx30("span", { className: "text-destructive", children: "*" })
          ] }),
          /* @__PURE__ */ jsx30(
            Input7,
            {
              id: "provider",
              placeholder: "e.g., OpenAI, Anthropic, AWS Bedrock",
              value: formData.provider,
              onChange: (e) => {
                setFormData({ ...formData, provider: e.target.value });
                setErrors({ ...errors, provider: "" });
              },
              disabled: isPending,
              className: errors.provider ? "border-destructive" : ""
            }
          ),
          errors.provider && /* @__PURE__ */ jsx30("p", { className: "text-sm text-destructive", children: errors.provider }),
          /* @__PURE__ */ jsx30("p", { className: "text-xs text-muted-foreground", children: "The LLM service provider (e.g., OpenAI, Anthropic)" })
        ] }),
        /* @__PURE__ */ jsxs26("div", { className: "grid gap-2", children: [
          /* @__PURE__ */ jsxs26(Label8, { htmlFor: "display_name", children: [
            "Display Name ",
            /* @__PURE__ */ jsx30("span", { className: "text-destructive", children: "*" })
          ] }),
          /* @__PURE__ */ jsx30(
            Input7,
            {
              id: "display_name",
              placeholder: "e.g., GPT-4 Turbo, Claude 3 Opus",
              value: formData.display_name,
              onChange: (e) => {
                setFormData({ ...formData, display_name: e.target.value });
                setErrors({ ...errors, display_name: "" });
              },
              disabled: isPending,
              className: errors.display_name ? "border-destructive" : ""
            }
          ),
          errors.display_name && /* @__PURE__ */ jsx30("p", { className: "text-sm text-destructive", children: errors.display_name }),
          /* @__PURE__ */ jsx30("p", { className: "text-xs text-muted-foreground", children: "A human-readable name shown in the UI" })
        ] }),
        /* @__PURE__ */ jsxs26("div", { className: "grid grid-cols-2 gap-4", children: [
          /* @__PURE__ */ jsxs26("div", { className: "grid gap-2", children: [
            /* @__PURE__ */ jsxs26(Label8, { htmlFor: "input_token_cost", children: [
              "Input Token Cost (per 1M) ",
              /* @__PURE__ */ jsx30("span", { className: "text-destructive", children: "*" })
            ] }),
            /* @__PURE__ */ jsx30(
              Input7,
              {
                id: "input_token_cost",
                type: "number",
                step: "0.000001",
                min: "0",
                placeholder: "e.g., 0.003",
                value: formData.input_token_cost,
                onChange: (e) => {
                  setFormData({ ...formData, input_token_cost: parseFloat(e.target.value) || 0 });
                  setErrors({ ...errors, input_token_cost: "" });
                },
                disabled: isPending,
                className: errors.input_token_cost ? "border-destructive" : ""
              }
            ),
            errors.input_token_cost && /* @__PURE__ */ jsx30("p", { className: "text-sm text-destructive", children: errors.input_token_cost }),
            /* @__PURE__ */ jsx30("p", { className: "text-xs text-muted-foreground", children: "Cost in USD per 1 thousand input tokens" })
          ] }),
          /* @__PURE__ */ jsxs26("div", { className: "grid gap-2", children: [
            /* @__PURE__ */ jsxs26(Label8, { htmlFor: "output_token_cost", children: [
              "Output Token Cost (per 1K) ",
              /* @__PURE__ */ jsx30("span", { className: "text-destructive", children: "*" })
            ] }),
            /* @__PURE__ */ jsx30(
              Input7,
              {
                id: "output_token_cost",
                type: "number",
                step: "0.000001",
                min: "0",
                placeholder: "e.g., 0.015",
                value: formData.output_token_cost,
                onChange: (e) => {
                  setFormData({ ...formData, output_token_cost: parseFloat(e.target.value) || 0 });
                  setErrors({ ...errors, output_token_cost: "" });
                },
                disabled: isPending,
                className: errors.output_token_cost ? "border-destructive" : ""
              }
            ),
            errors.output_token_cost && /* @__PURE__ */ jsx30("p", { className: "text-sm text-destructive", children: errors.output_token_cost }),
            /* @__PURE__ */ jsx30("p", { className: "text-xs text-muted-foreground", children: "Cost in USD per 1 thousand output tokens" })
          ] })
        ] }),
        /* @__PURE__ */ jsxs26("div", { className: "flex items-center justify-between space-x-2 rounded-lg border p-4", children: [
          /* @__PURE__ */ jsxs26("div", { className: "space-y-0.5", children: [
            /* @__PURE__ */ jsx30(Label8, { htmlFor: "is_active", className: "text-base", children: "Active" }),
            /* @__PURE__ */ jsx30("p", { className: "text-sm text-muted-foreground", children: "Make this model available for use immediately" })
          ] }),
          /* @__PURE__ */ jsx30(
            Switch3,
            {
              id: "is_active",
              checked: formData.is_active,
              onCheckedChange: (checked) => setFormData({ ...formData, is_active: checked }),
              disabled: isPending
            }
          )
        ] }),
        /* @__PURE__ */ jsxs26("div", { className: "flex items-center justify-between space-x-2 rounded-lg border p-4", children: [
          /* @__PURE__ */ jsxs26("div", { className: "space-y-0.5", children: [
            /* @__PURE__ */ jsx30(Label8, { htmlFor: "caching_supported", className: "text-base", children: "Caching Supported" }),
            /* @__PURE__ */ jsx30("p", { className: "text-sm text-muted-foreground", children: "Enable if this model supports prompt caching" })
          ] }),
          /* @__PURE__ */ jsx30(
            Switch3,
            {
              id: "caching_supported",
              checked: formData.caching_supported,
              onCheckedChange: (checked) => setFormData({ ...formData, caching_supported: checked }),
              disabled: isPending
            }
          )
        ] }),
        /* @__PURE__ */ jsxs26("div", { className: "flex items-center justify-between space-x-2 rounded-lg border p-4", children: [
          /* @__PURE__ */ jsxs26("div", { className: "space-y-0.5", children: [
            /* @__PURE__ */ jsx30(Label8, { htmlFor: "reasoning_supported", className: "text-base", children: "Reasoning Supported" }),
            /* @__PURE__ */ jsx30("p", { className: "text-sm text-muted-foreground", children: "Enable if this model supports extended thinking/reasoning" })
          ] }),
          /* @__PURE__ */ jsx30(
            Switch3,
            {
              id: "reasoning_supported",
              checked: formData.reasoning_supported,
              onCheckedChange: (checked) => setFormData({ ...formData, reasoning_supported: checked }),
              disabled: isPending
            }
          )
        ] }),
        /* @__PURE__ */ jsxs26("div", { className: "flex items-center justify-between space-x-2 rounded-lg border p-4", children: [
          /* @__PURE__ */ jsxs26("div", { className: "space-y-0.5", children: [
            /* @__PURE__ */ jsx30(Label8, { htmlFor: "vision_supported", className: "text-base", children: "Vision Supported" }),
            /* @__PURE__ */ jsx30("p", { className: "text-sm text-muted-foreground", children: "Enable if this model supports image/vision inputs" })
          ] }),
          /* @__PURE__ */ jsx30(
            Switch3,
            {
              id: "vision_supported",
              checked: formData.vision_supported,
              onCheckedChange: (checked) => setFormData({ ...formData, vision_supported: checked }),
              disabled: isPending
            }
          )
        ] }),
        /* @__PURE__ */ jsxs26("div", { className: "grid grid-cols-2 gap-4", children: [
          /* @__PURE__ */ jsxs26("div", { className: "grid gap-2", children: [
            /* @__PURE__ */ jsx30(Label8, { htmlFor: "cache_write_token_cost", children: "Cache Write Cost (per 1K)" }),
            /* @__PURE__ */ jsx30(
              Input7,
              {
                id: "cache_write_token_cost",
                type: "number",
                step: "0.000001",
                min: "0",
                placeholder: "e.g., 0.00375",
                value: formData.cache_write_token_cost,
                onChange: (e) => {
                  setFormData({ ...formData, cache_write_token_cost: parseFloat(e.target.value) || 0 });
                  setErrors({ ...errors, cache_write_token_cost: "" });
                },
                disabled: isPending,
                className: errors.cache_write_token_cost ? "border-destructive" : ""
              }
            ),
            errors.cache_write_token_cost && /* @__PURE__ */ jsx30("p", { className: "text-sm text-destructive", children: errors.cache_write_token_cost }),
            /* @__PURE__ */ jsx30("p", { className: "text-xs text-muted-foreground", children: "Cost in USD per 1K tokens to write to cache" })
          ] }),
          /* @__PURE__ */ jsxs26("div", { className: "grid gap-2", children: [
            /* @__PURE__ */ jsx30(Label8, { htmlFor: "cache_read_token_cost", children: "Cache Read Cost (per 1K)" }),
            /* @__PURE__ */ jsx30(
              Input7,
              {
                id: "cache_read_token_cost",
                type: "number",
                step: "0.000001",
                min: "0",
                placeholder: "e.g., 0.0003",
                value: formData.cache_read_token_cost,
                onChange: (e) => {
                  setFormData({ ...formData, cache_read_token_cost: parseFloat(e.target.value) || 0 });
                  setErrors({ ...errors, cache_read_token_cost: "" });
                },
                disabled: isPending,
                className: errors.cache_read_token_cost ? "border-destructive" : ""
              }
            ),
            errors.cache_read_token_cost && /* @__PURE__ */ jsx30("p", { className: "text-sm text-destructive", children: errors.cache_read_token_cost }),
            /* @__PURE__ */ jsx30("p", { className: "text-xs text-muted-foreground", children: "Cost in USD per 1K tokens to read from cache" })
          ] })
        ] }),
        /* @__PURE__ */ jsxs26("div", { className: "grid gap-2", children: [
          /* @__PURE__ */ jsx30(Label8, { htmlFor: "unsupported_parameters", children: "Unsupported Parameters" }),
          /* @__PURE__ */ jsx30(
            Input7,
            {
              id: "unsupported_parameters",
              placeholder: "e.g., temperature, top_p, max_tokens",
              value: unsupportedParamsInput,
              onChange: (e) => {
                setUnsupportedParamsInput(e.target.value);
                const params = e.target.value.split(",").map((p) => p.trim()).filter((p) => p.length > 0);
                setFormData({ ...formData, unsupported_parameters: params });
              },
              disabled: isPending
            }
          ),
          /* @__PURE__ */ jsx30("p", { className: "text-xs text-muted-foreground", children: "Comma-separated list of parameters not supported by this model" })
        ] }),
        /* @__PURE__ */ jsxs26("div", { className: "grid gap-2", children: [
          /* @__PURE__ */ jsx30(Label8, { htmlFor: "reasoning_effort", children: "Reasoning Effort" }),
          /* @__PURE__ */ jsx30(
            Input7,
            {
              id: "reasoning_effort",
              placeholder: "e.g., low, medium, high",
              value: formData.reasoning_effort || "",
              onChange: (e) => {
                setFormData({ ...formData, reasoning_effort: e.target.value || null });
              },
              disabled: isPending
            }
          ),
          /* @__PURE__ */ jsx30("p", { className: "text-xs text-muted-foreground", children: "Default reasoning effort level for models that support it (e.g., OpenAI o1 models)" })
        ] }),
        /* @__PURE__ */ jsxs26("div", { className: "grid grid-cols-2 gap-4", children: [
          /* @__PURE__ */ jsxs26("div", { className: "grid gap-2", children: [
            /* @__PURE__ */ jsx30(Label8, { htmlFor: "max_tokens", children: "Max Tokens" }),
            /* @__PURE__ */ jsx30(
              Input7,
              {
                id: "max_tokens",
                type: "number",
                min: "1",
                placeholder: "e.g., 4096",
                value: formData.max_tokens ?? "",
                onChange: (e) => {
                  const value = e.target.value ? parseInt(e.target.value, 10) : null;
                  setFormData({ ...formData, max_tokens: value });
                },
                disabled: isPending
              }
            ),
            /* @__PURE__ */ jsx30("p", { className: "text-xs text-muted-foreground", children: "Maximum output tokens for this model" })
          ] }),
          /* @__PURE__ */ jsxs26("div", { className: "grid gap-2", children: [
            /* @__PURE__ */ jsx30(Label8, { htmlFor: "max_thinking_tokens", children: "Max Thinking Tokens" }),
            /* @__PURE__ */ jsx30(
              Input7,
              {
                id: "max_thinking_tokens",
                type: "number",
                min: "1",
                placeholder: "e.g., 10000",
                value: formData.max_thinking_tokens ?? "",
                onChange: (e) => {
                  const value = e.target.value ? parseInt(e.target.value, 10) : null;
                  setFormData({ ...formData, max_thinking_tokens: value });
                },
                disabled: isPending
              }
            ),
            /* @__PURE__ */ jsx30("p", { className: "text-xs text-muted-foreground", children: "Maximum thinking/reasoning tokens for models that support extended thinking" })
          ] })
        ] })
      ] }),
      /* @__PURE__ */ jsxs26(DialogFooter7, { children: [
        /* @__PURE__ */ jsx30(
          Button17,
          {
            type: "button",
            variant: "outline",
            onClick: handleClose,
            disabled: isPending,
            children: "Cancel"
          }
        ),
        /* @__PURE__ */ jsx30(Button17, { type: "submit", disabled: isPending, children: isPending ? "Creating..." : "Create Model" })
      ] })
    ] })
  ] }) });
};

// src/components/llm/EditLLMDialog.tsx
import { useState as useState20, useEffect as useEffect9 } from "react";
import {
  Dialog as Dialog11,
  DialogContent as DialogContent11,
  DialogDescription as DialogDescription11,
  DialogFooter as DialogFooter8,
  DialogHeader as DialogHeader11,
  DialogTitle as DialogTitle11
} from "@umd-dit-ai-workspace/shared-ui";
import { Button as Button18 } from "@umd-dit-ai-workspace/shared-ui";
import { Input as Input8 } from "@umd-dit-ai-workspace/shared-ui";
import { Label as Label9 } from "@umd-dit-ai-workspace/shared-ui";
import { Switch as Switch4 } from "@umd-dit-ai-workspace/shared-ui";
import { jsx as jsx31, jsxs as jsxs27 } from "react/jsx-runtime";
var EditLLMDialog = ({
  llm,
  open,
  onOpenChange,
  onSave,
  isPending = false
}) => {
  const [formData, setFormData] = useState20({
    model_id: llm.model_id,
    provider: llm.provider,
    display_name: llm.display_name,
    is_active: llm.is_active,
    caching_supported: llm.caching_supported,
    reasoning_supported: llm.reasoning_supported,
    vision_supported: llm.vision_supported,
    input_token_cost: llm.input_token_cost,
    output_token_cost: llm.output_token_cost,
    cache_write_token_cost: llm.cache_write_token_cost,
    cache_read_token_cost: llm.cache_read_token_cost,
    unsupported_parameters: llm.unsupported_parameters,
    reasoning_effort: llm.reasoning_effort,
    max_tokens: llm.max_tokens,
    max_thinking_tokens: llm.max_thinking_tokens
  });
  const [unsupportedParamsInput, setUnsupportedParamsInput] = useState20(
    llm.unsupported_parameters?.join(", ") || ""
  );
  const [errors, setErrors] = useState20({});
  useEffect9(() => {
    setFormData({
      model_id: llm.model_id,
      provider: llm.provider,
      display_name: llm.display_name,
      is_active: llm.is_active,
      caching_supported: llm.caching_supported,
      reasoning_supported: llm.reasoning_supported,
      vision_supported: llm.vision_supported,
      input_token_cost: llm.input_token_cost,
      output_token_cost: llm.output_token_cost,
      cache_write_token_cost: llm.cache_write_token_cost,
      cache_read_token_cost: llm.cache_read_token_cost,
      unsupported_parameters: llm.unsupported_parameters,
      reasoning_effort: llm.reasoning_effort,
      max_tokens: llm.max_tokens,
      max_thinking_tokens: llm.max_thinking_tokens
    });
    setUnsupportedParamsInput(llm.unsupported_parameters?.join(", ") || "");
    setErrors({});
  }, [llm]);
  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (formData.model_id && !formData.model_id.trim()) {
      newErrors.model_id = "Model ID cannot be empty";
    }
    if (formData.provider && !formData.provider.trim()) {
      newErrors.provider = "Provider cannot be empty";
    }
    if (formData.display_name && !formData.display_name.trim()) {
      newErrors.display_name = "Display name cannot be empty";
    }
    if (formData.input_token_cost !== void 0 && formData.input_token_cost < 0) {
      newErrors.input_token_cost = "Input token cost must be non-negative";
    }
    if (formData.output_token_cost !== void 0 && formData.output_token_cost < 0) {
      newErrors.output_token_cost = "Output token cost must be non-negative";
    }
    if (formData.cache_write_token_cost !== void 0 && formData.cache_write_token_cost < 0) {
      newErrors.cache_write_token_cost = "Cache write token cost must be non-negative";
    }
    if (formData.cache_read_token_cost !== void 0 && formData.cache_read_token_cost < 0) {
      newErrors.cache_read_token_cost = "Cache read token cost must be non-negative";
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    const changedData = {};
    if (formData.model_id !== llm.model_id) {
      changedData.model_id = formData.model_id;
    }
    if (formData.provider !== llm.provider) {
      changedData.provider = formData.provider;
    }
    if (formData.display_name !== llm.display_name) {
      changedData.display_name = formData.display_name;
    }
    if (formData.is_active !== llm.is_active) {
      changedData.is_active = formData.is_active;
    }
    if (formData.caching_supported !== llm.caching_supported) {
      changedData.caching_supported = formData.caching_supported;
    }
    if (formData.reasoning_supported !== llm.reasoning_supported) {
      changedData.reasoning_supported = formData.reasoning_supported;
    }
    if (formData.vision_supported !== llm.vision_supported) {
      changedData.vision_supported = formData.vision_supported;
    }
    if (formData.input_token_cost !== llm.input_token_cost) {
      changedData.input_token_cost = formData.input_token_cost;
    }
    if (formData.output_token_cost !== llm.output_token_cost) {
      changedData.output_token_cost = formData.output_token_cost;
    }
    if (formData.cache_write_token_cost !== llm.cache_write_token_cost) {
      changedData.cache_write_token_cost = formData.cache_write_token_cost;
    }
    if (formData.cache_read_token_cost !== llm.cache_read_token_cost) {
      changedData.cache_read_token_cost = formData.cache_read_token_cost;
    }
    if (JSON.stringify(formData.unsupported_parameters) !== JSON.stringify(llm.unsupported_parameters)) {
      changedData.unsupported_parameters = formData.unsupported_parameters;
    }
    if (formData.reasoning_effort !== llm.reasoning_effort) {
      changedData.reasoning_effort = formData.reasoning_effort;
    }
    if (formData.max_tokens !== llm.max_tokens) {
      changedData.max_tokens = formData.max_tokens;
    }
    if (formData.max_thinking_tokens !== llm.max_thinking_tokens) {
      changedData.max_thinking_tokens = formData.max_thinking_tokens;
    }
    onSave(changedData);
  };
  const handleClose = () => {
    setFormData({
      model_id: llm.model_id,
      provider: llm.provider,
      display_name: llm.display_name,
      is_active: llm.is_active,
      caching_supported: llm.caching_supported,
      reasoning_supported: llm.reasoning_supported,
      vision_supported: llm.vision_supported,
      input_token_cost: llm.input_token_cost,
      output_token_cost: llm.output_token_cost,
      cache_write_token_cost: llm.cache_write_token_cost,
      cache_read_token_cost: llm.cache_read_token_cost,
      unsupported_parameters: llm.unsupported_parameters,
      reasoning_effort: llm.reasoning_effort,
      max_tokens: llm.max_tokens,
      max_thinking_tokens: llm.max_thinking_tokens
    });
    setUnsupportedParamsInput(llm.unsupported_parameters?.join(", ") || "");
    setErrors({});
    onOpenChange(false);
  };
  return /* @__PURE__ */ jsx31(Dialog11, { open, onOpenChange: handleClose, children: /* @__PURE__ */ jsxs27(DialogContent11, { className: "sm:max-w-[700px] max-h-[90vh] flex flex-col", children: [
    /* @__PURE__ */ jsxs27(DialogHeader11, { children: [
      /* @__PURE__ */ jsx31(DialogTitle11, { children: "Edit LLM Model" }),
      /* @__PURE__ */ jsxs27(DialogDescription11, { children: [
        "Update the configuration for ",
        /* @__PURE__ */ jsx31("span", { className: "font-mono", children: llm.model_id })
      ] })
    ] }),
    /* @__PURE__ */ jsxs27("form", { onSubmit: handleSubmit, className: "flex flex-col flex-1 overflow-hidden", children: [
      /* @__PURE__ */ jsxs27("div", { className: "grid gap-4 py-4 px-1 overflow-y-auto pr-2", children: [
        /* @__PURE__ */ jsxs27("div", { className: "grid gap-2", children: [
          /* @__PURE__ */ jsx31(Label9, { htmlFor: "model_name_display", children: "Model Name" }),
          /* @__PURE__ */ jsx31(
            Input8,
            {
              id: "model_name_display",
              value: llm.model_name,
              disabled: true,
              className: "bg-muted"
            }
          ),
          /* @__PURE__ */ jsx31("p", { className: "text-xs text-muted-foreground", children: "Model name cannot be changed after creation" })
        ] }),
        /* @__PURE__ */ jsxs27("div", { className: "grid gap-2", children: [
          /* @__PURE__ */ jsx31(Label9, { htmlFor: "model_id", children: "Model ID" }),
          /* @__PURE__ */ jsx31(
            Input8,
            {
              id: "model_id",
              placeholder: "e.g., gpt-4-turbo, claude-3-opus-20240229",
              value: formData.model_id || "",
              onChange: (e) => {
                setFormData({ ...formData, model_id: e.target.value });
                setErrors({ ...errors, model_id: "" });
              },
              disabled: isPending,
              className: errors.model_id ? "border-destructive" : ""
            }
          ),
          errors.model_id && /* @__PURE__ */ jsx31("p", { className: "text-sm text-destructive", children: errors.model_id })
        ] }),
        /* @__PURE__ */ jsxs27("div", { className: "grid gap-2", children: [
          /* @__PURE__ */ jsx31(Label9, { htmlFor: "provider", children: "Provider" }),
          /* @__PURE__ */ jsx31(
            Input8,
            {
              id: "provider",
              placeholder: "e.g., openai, anthropic, bedrock",
              value: formData.provider || "",
              onChange: (e) => {
                setFormData({ ...formData, provider: e.target.value });
                setErrors({ ...errors, provider: "" });
              },
              disabled: isPending,
              className: errors.provider ? "border-destructive" : ""
            }
          ),
          errors.provider && /* @__PURE__ */ jsx31("p", { className: "text-sm text-destructive", children: errors.provider })
        ] }),
        /* @__PURE__ */ jsxs27("div", { className: "grid gap-2", children: [
          /* @__PURE__ */ jsx31(Label9, { htmlFor: "display_name", children: "Display Name" }),
          /* @__PURE__ */ jsx31(
            Input8,
            {
              id: "display_name",
              placeholder: "e.g., GPT-4 Turbo, Claude 3 Opus",
              value: formData.display_name || "",
              onChange: (e) => {
                setFormData({ ...formData, display_name: e.target.value });
                setErrors({ ...errors, display_name: "" });
              },
              disabled: isPending,
              className: errors.display_name ? "border-destructive" : ""
            }
          ),
          errors.display_name && /* @__PURE__ */ jsx31("p", { className: "text-sm text-destructive", children: errors.display_name })
        ] }),
        /* @__PURE__ */ jsxs27("div", { className: "grid grid-cols-2 gap-4", children: [
          /* @__PURE__ */ jsxs27("div", { className: "grid gap-2", children: [
            /* @__PURE__ */ jsx31(Label9, { htmlFor: "input_token_cost", children: "Input Token Cost (per 1K)" }),
            /* @__PURE__ */ jsx31(
              Input8,
              {
                id: "input_token_cost",
                type: "number",
                step: "0.000001",
                min: "0",
                placeholder: "e.g., 0.003",
                value: formData.input_token_cost ?? 0,
                onChange: (e) => {
                  setFormData({ ...formData, input_token_cost: parseFloat(e.target.value) || 0 });
                  setErrors({ ...errors, input_token_cost: "" });
                },
                disabled: isPending,
                className: errors.input_token_cost ? "border-destructive" : ""
              }
            ),
            errors.input_token_cost && /* @__PURE__ */ jsx31("p", { className: "text-sm text-destructive", children: errors.input_token_cost }),
            /* @__PURE__ */ jsx31("p", { className: "text-xs text-muted-foreground", children: "Cost in USD per 1 thousand input tokens" })
          ] }),
          /* @__PURE__ */ jsxs27("div", { className: "grid gap-2", children: [
            /* @__PURE__ */ jsx31(Label9, { htmlFor: "output_token_cost", children: "Output Token Cost (per 1K)" }),
            /* @__PURE__ */ jsx31(
              Input8,
              {
                id: "output_token_cost",
                type: "number",
                step: "0.000001",
                min: "0",
                placeholder: "e.g., 0.015",
                value: formData.output_token_cost ?? 0,
                onChange: (e) => {
                  setFormData({ ...formData, output_token_cost: parseFloat(e.target.value) || 0 });
                  setErrors({ ...errors, output_token_cost: "" });
                },
                disabled: isPending,
                className: errors.output_token_cost ? "border-destructive" : ""
              }
            ),
            errors.output_token_cost && /* @__PURE__ */ jsx31("p", { className: "text-sm text-destructive", children: errors.output_token_cost }),
            /* @__PURE__ */ jsx31("p", { className: "text-xs text-muted-foreground", children: "Cost in USD per 1 thousand output tokens" })
          ] })
        ] }),
        /* @__PURE__ */ jsxs27("div", { className: "flex items-center justify-between space-x-2 rounded-lg border p-4", children: [
          /* @__PURE__ */ jsxs27("div", { className: "space-y-0.5", children: [
            /* @__PURE__ */ jsx31(Label9, { htmlFor: "is_active", className: "text-base", children: "Active" }),
            /* @__PURE__ */ jsx31("p", { className: "text-sm text-muted-foreground", children: "Control whether this model is available for use" })
          ] }),
          /* @__PURE__ */ jsx31(
            Switch4,
            {
              id: "is_active",
              checked: formData.is_active ?? false,
              onCheckedChange: (checked) => setFormData({ ...formData, is_active: checked }),
              disabled: isPending
            }
          )
        ] }),
        /* @__PURE__ */ jsxs27("div", { className: "flex items-center justify-between space-x-2 rounded-lg border p-4", children: [
          /* @__PURE__ */ jsxs27("div", { className: "space-y-0.5", children: [
            /* @__PURE__ */ jsx31(Label9, { htmlFor: "caching_supported", className: "text-base", children: "Caching Supported" }),
            /* @__PURE__ */ jsx31("p", { className: "text-sm text-muted-foreground", children: "Enable if this model supports prompt caching" })
          ] }),
          /* @__PURE__ */ jsx31(
            Switch4,
            {
              id: "caching_supported",
              checked: formData.caching_supported ?? false,
              onCheckedChange: (checked) => setFormData({ ...formData, caching_supported: checked }),
              disabled: isPending
            }
          )
        ] }),
        /* @__PURE__ */ jsxs27("div", { className: "flex items-center justify-between space-x-2 rounded-lg border p-4", children: [
          /* @__PURE__ */ jsxs27("div", { className: "space-y-0.5", children: [
            /* @__PURE__ */ jsx31(Label9, { htmlFor: "reasoning_supported", className: "text-base", children: "Reasoning Supported" }),
            /* @__PURE__ */ jsx31("p", { className: "text-sm text-muted-foreground", children: "Enable if this model supports extended thinking/reasoning" })
          ] }),
          /* @__PURE__ */ jsx31(
            Switch4,
            {
              id: "reasoning_supported",
              checked: formData.reasoning_supported ?? false,
              onCheckedChange: (checked) => setFormData({ ...formData, reasoning_supported: checked }),
              disabled: isPending
            }
          )
        ] }),
        /* @__PURE__ */ jsxs27("div", { className: "flex items-center justify-between space-x-2 rounded-lg border p-4", children: [
          /* @__PURE__ */ jsxs27("div", { className: "space-y-0.5", children: [
            /* @__PURE__ */ jsx31(Label9, { htmlFor: "vision_supported", className: "text-base", children: "Vision Supported" }),
            /* @__PURE__ */ jsx31("p", { className: "text-sm text-muted-foreground", children: "Enable if this model supports image/vision inputs" })
          ] }),
          /* @__PURE__ */ jsx31(
            Switch4,
            {
              id: "vision_supported",
              checked: formData.vision_supported ?? false,
              onCheckedChange: (checked) => setFormData({ ...formData, vision_supported: checked }),
              disabled: isPending
            }
          )
        ] }),
        /* @__PURE__ */ jsxs27("div", { className: "grid grid-cols-2 gap-4", children: [
          /* @__PURE__ */ jsxs27("div", { className: "grid gap-2", children: [
            /* @__PURE__ */ jsx31(Label9, { htmlFor: "cache_write_token_cost", children: "Cache Write Cost (per 1K)" }),
            /* @__PURE__ */ jsx31(
              Input8,
              {
                id: "cache_write_token_cost",
                type: "number",
                step: "0.000001",
                min: "0",
                placeholder: "e.g., 0.00375",
                value: formData.cache_write_token_cost ?? 0,
                onChange: (e) => {
                  setFormData({ ...formData, cache_write_token_cost: parseFloat(e.target.value) || 0 });
                  setErrors({ ...errors, cache_write_token_cost: "" });
                },
                disabled: isPending,
                className: errors.cache_write_token_cost ? "border-destructive" : ""
              }
            ),
            errors.cache_write_token_cost && /* @__PURE__ */ jsx31("p", { className: "text-sm text-destructive", children: errors.cache_write_token_cost }),
            /* @__PURE__ */ jsx31("p", { className: "text-xs text-muted-foreground", children: "Cost in USD per 1K tokens to write to cache" })
          ] }),
          /* @__PURE__ */ jsxs27("div", { className: "grid gap-2", children: [
            /* @__PURE__ */ jsx31(Label9, { htmlFor: "cache_read_token_cost", children: "Cache Read Cost (per 1K)" }),
            /* @__PURE__ */ jsx31(
              Input8,
              {
                id: "cache_read_token_cost",
                type: "number",
                step: "0.000001",
                min: "0",
                placeholder: "e.g., 0.0003",
                value: formData.cache_read_token_cost ?? 0,
                onChange: (e) => {
                  setFormData({ ...formData, cache_read_token_cost: parseFloat(e.target.value) || 0 });
                  setErrors({ ...errors, cache_read_token_cost: "" });
                },
                disabled: isPending,
                className: errors.cache_read_token_cost ? "border-destructive" : ""
              }
            ),
            errors.cache_read_token_cost && /* @__PURE__ */ jsx31("p", { className: "text-sm text-destructive", children: errors.cache_read_token_cost }),
            /* @__PURE__ */ jsx31("p", { className: "text-xs text-muted-foreground", children: "Cost in USD per 1K tokens to read from cache" })
          ] })
        ] }),
        /* @__PURE__ */ jsxs27("div", { className: "grid gap-2", children: [
          /* @__PURE__ */ jsx31(Label9, { htmlFor: "unsupported_parameters", children: "Unsupported Parameters" }),
          /* @__PURE__ */ jsx31(
            Input8,
            {
              id: "unsupported_parameters",
              placeholder: "e.g., temperature, top_p, max_tokens",
              value: unsupportedParamsInput,
              onChange: (e) => {
                setUnsupportedParamsInput(e.target.value);
                const params = e.target.value.split(",").map((p) => p.trim()).filter((p) => p.length > 0);
                setFormData({ ...formData, unsupported_parameters: params });
              },
              disabled: isPending
            }
          ),
          /* @__PURE__ */ jsx31("p", { className: "text-xs text-muted-foreground", children: "Comma-separated list of parameters not supported by this model" })
        ] }),
        /* @__PURE__ */ jsxs27("div", { className: "grid gap-2", children: [
          /* @__PURE__ */ jsx31(Label9, { htmlFor: "reasoning_effort", children: "Reasoning Effort" }),
          /* @__PURE__ */ jsx31(
            Input8,
            {
              id: "reasoning_effort",
              placeholder: "e.g., low, medium, high",
              value: formData.reasoning_effort || "",
              onChange: (e) => {
                setFormData({ ...formData, reasoning_effort: e.target.value || null });
              },
              disabled: isPending
            }
          ),
          /* @__PURE__ */ jsx31("p", { className: "text-xs text-muted-foreground", children: "Default reasoning effort level for models that support it (e.g., OpenAI o1 models)" })
        ] }),
        /* @__PURE__ */ jsxs27("div", { className: "grid grid-cols-2 gap-4", children: [
          /* @__PURE__ */ jsxs27("div", { className: "grid gap-2", children: [
            /* @__PURE__ */ jsx31(Label9, { htmlFor: "max_tokens", children: "Max Tokens" }),
            /* @__PURE__ */ jsx31(
              Input8,
              {
                id: "max_tokens",
                type: "number",
                min: "1",
                placeholder: "e.g., 4096",
                value: formData.max_tokens ?? "",
                onChange: (e) => {
                  const value = e.target.value ? parseInt(e.target.value, 10) : null;
                  setFormData({ ...formData, max_tokens: value });
                },
                disabled: isPending
              }
            ),
            /* @__PURE__ */ jsx31("p", { className: "text-xs text-muted-foreground", children: "Maximum output tokens for this model" })
          ] }),
          /* @__PURE__ */ jsxs27("div", { className: "grid gap-2", children: [
            /* @__PURE__ */ jsx31(Label9, { htmlFor: "max_thinking_tokens", children: "Max Thinking Tokens" }),
            /* @__PURE__ */ jsx31(
              Input8,
              {
                id: "max_thinking_tokens",
                type: "number",
                min: "1",
                placeholder: "e.g., 10000",
                value: formData.max_thinking_tokens ?? "",
                onChange: (e) => {
                  const value = e.target.value ? parseInt(e.target.value, 10) : null;
                  setFormData({ ...formData, max_thinking_tokens: value });
                },
                disabled: isPending
              }
            ),
            /* @__PURE__ */ jsx31("p", { className: "text-xs text-muted-foreground", children: "Maximum thinking/reasoning tokens for models that support extended thinking" })
          ] })
        ] }),
        /* @__PURE__ */ jsxs27("div", { className: "grid grid-cols-2 gap-4 pt-2 text-xs text-muted-foreground", children: [
          /* @__PURE__ */ jsxs27("div", { children: [
            /* @__PURE__ */ jsx31("p", { className: "font-medium", children: "Created by:" }),
            /* @__PURE__ */ jsx31("p", { children: llm.created_by })
          ] }),
          /* @__PURE__ */ jsxs27("div", { children: [
            /* @__PURE__ */ jsx31("p", { className: "font-medium", children: "Created at:" }),
            /* @__PURE__ */ jsx31("p", { children: new Date(llm.created_at).toLocaleString() })
          ] }),
          /* @__PURE__ */ jsxs27("div", { children: [
            /* @__PURE__ */ jsx31("p", { className: "font-medium", children: "Updated by:" }),
            /* @__PURE__ */ jsx31("p", { children: llm.updated_by })
          ] }),
          /* @__PURE__ */ jsxs27("div", { children: [
            /* @__PURE__ */ jsx31("p", { className: "font-medium", children: "Updated at:" }),
            /* @__PURE__ */ jsx31("p", { children: new Date(llm.updated_at).toLocaleString() })
          ] })
        ] })
      ] }),
      /* @__PURE__ */ jsxs27(DialogFooter8, { children: [
        /* @__PURE__ */ jsx31(
          Button18,
          {
            type: "button",
            variant: "outline",
            onClick: handleClose,
            disabled: isPending,
            children: "Cancel"
          }
        ),
        /* @__PURE__ */ jsx31(Button18, { type: "submit", disabled: isPending, children: isPending ? "Saving..." : "Save Changes" })
      ] })
    ] })
  ] }) });
};

// src/components/llm/DeleteLLMDialog.tsx
import {
  AlertDialog as AlertDialog2,
  AlertDialogAction as AlertDialogAction2,
  AlertDialogCancel as AlertDialogCancel2,
  AlertDialogContent as AlertDialogContent2,
  AlertDialogDescription as AlertDialogDescription2,
  AlertDialogFooter as AlertDialogFooter2,
  AlertDialogHeader as AlertDialogHeader2,
  AlertDialogTitle as AlertDialogTitle2
} from "@umd-dit-ai-workspace/shared-ui";
import { jsx as jsx32, jsxs as jsxs28 } from "react/jsx-runtime";
var DeleteLLMDialog = ({
  llm,
  open,
  onOpenChange,
  onDelete,
  isPending = false
}) => {
  return /* @__PURE__ */ jsx32(AlertDialog2, { open, onOpenChange, children: /* @__PURE__ */ jsxs28(AlertDialogContent2, { children: [
    /* @__PURE__ */ jsxs28(AlertDialogHeader2, { children: [
      /* @__PURE__ */ jsx32(AlertDialogTitle2, { children: "Delete LLM Configuration" }),
      /* @__PURE__ */ jsxs28(AlertDialogDescription2, { children: [
        "Are you sure you want to delete the LLM configuration for",
        " ",
        /* @__PURE__ */ jsx32("span", { className: "font-semibold", children: llm.display_name }),
        " ",
        "(",
        /* @__PURE__ */ jsx32("span", { className: "font-mono text-sm", children: llm.model_id }),
        ")?"
      ] })
    ] }),
    /* @__PURE__ */ jsxs28("div", { className: "rounded-lg border border-destructive/50 bg-destructive/10 p-4 space-y-2", children: [
      /* @__PURE__ */ jsx32("p", { className: "text-sm font-medium text-destructive", children: "Warning:" }),
      /* @__PURE__ */ jsxs28("ul", { className: "text-sm text-muted-foreground space-y-1 list-disc list-inside", children: [
        /* @__PURE__ */ jsx32("li", { children: "This action cannot be undone" }),
        /* @__PURE__ */ jsx32("li", { children: "Any applications or features using this model may be affected" }),
        /* @__PURE__ */ jsx32("li", { children: "Historical data referencing this model will remain but the configuration will be removed" })
      ] })
    ] }),
    /* @__PURE__ */ jsxs28("div", { className: "rounded-lg border bg-muted p-4 space-y-1 text-xs", children: [
      /* @__PURE__ */ jsxs28("p", { children: [
        /* @__PURE__ */ jsx32("span", { className: "font-medium", children: "Model ID:" }),
        " ",
        llm.model_id
      ] }),
      /* @__PURE__ */ jsxs28("p", { children: [
        /* @__PURE__ */ jsx32("span", { className: "font-medium", children: "Provider:" }),
        " ",
        llm.provider
      ] }),
      /* @__PURE__ */ jsxs28("p", { children: [
        /* @__PURE__ */ jsx32("span", { className: "font-medium", children: "Status:" }),
        " ",
        llm.is_active ? "Active" : "Inactive"
      ] }),
      /* @__PURE__ */ jsxs28("p", { children: [
        /* @__PURE__ */ jsx32("span", { className: "font-medium", children: "Created:" }),
        " ",
        new Date(llm.created_at).toLocaleString()
      ] })
    ] }),
    /* @__PURE__ */ jsxs28(AlertDialogFooter2, { children: [
      /* @__PURE__ */ jsx32(AlertDialogCancel2, { disabled: isPending, children: "Cancel" }),
      /* @__PURE__ */ jsx32(
        AlertDialogAction2,
        {
          onClick: onDelete,
          disabled: isPending,
          className: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
          children: isPending ? "Deleting..." : "Delete Configuration"
        }
      )
    ] })
  ] }) });
};

// src/components/llm/LLMManagement.tsx
import {
  Select as Select4,
  SelectContent as SelectContent4,
  SelectItem as SelectItem4,
  SelectTrigger as SelectTrigger4,
  SelectValue as SelectValue4
} from "@umd-dit-ai-workspace/shared-ui";
import { Fragment as Fragment8, jsx as jsx33, jsxs as jsxs29 } from "react/jsx-runtime";
var LLMManagementInner = ({
  className
}) => {
  const [searchQuery, setSearchQuery] = useState21("");
  const [createDialogOpen, setCreateDialogOpen] = useState21(false);
  const [editDialogOpen, setEditDialogOpen] = useState21(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState21(false);
  const [selectedLLM, setSelectedLLM] = useState21(null);
  const [providerFilter, setProviderFilter] = useState21("all");
  const [statusFilter, setStatusFilter] = useState21("all");
  const [importDialogOpen, setImportDialogOpen] = useState21(false);
  const [selectedFile, setSelectedFile] = useState21(null);
  const [mergeExisting, setMergeExisting] = useState21(false);
  const [copyDefaultValues, setCopyDefaultValues] = useState21(void 0);
  const fileInputRef = React14.useRef(null);
  const queryClient2 = useQueryClient12();
  const { toast } = useToast6();
  const llmApi = useLLMConfigApi();
  const queryParams = {};
  if (providerFilter !== "all") {
    queryParams.provider = providerFilter;
  }
  if (statusFilter === "active") {
    queryParams.active_only = true;
  } else if (statusFilter === "inactive") {
    queryParams.active_only = false;
  }
  const { data: llmConfigs, isLoading } = useQuery12({
    queryKey: ["llm-configs", queryParams],
    queryFn: () => llmApi.listLLMConfigs(queryParams)
  });
  const createLLMMutation = useMutation8({
    mutationFn: llmApi.createLLMConfig,
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["llm-configs"] });
      toast({
        title: "LLM Configuration created",
        description: "The LLM configuration has been created successfully."
      });
      setCreateDialogOpen(false);
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to create LLM configuration";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });
  const updateLLMMutation = useMutation8({
    mutationFn: ({ model_name, data }) => llmApi.updateLLMConfig(model_name, data),
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["llm-configs"] });
      toast({
        title: "LLM Configuration updated",
        description: "The LLM configuration has been updated successfully."
      });
      setEditDialogOpen(false);
      setSelectedLLM(null);
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to update LLM configuration";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });
  const deleteLLMMutation = useMutation8({
    mutationFn: llmApi.deleteLLMConfig,
    onSuccess: () => {
      queryClient2.invalidateQueries({ queryKey: ["llm-configs"] });
      toast({
        title: "LLM Configuration deleted",
        description: "The LLM configuration has been deleted successfully."
      });
      setDeleteDialogOpen(false);
      setSelectedLLM(null);
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to delete LLM configuration";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });
  const exportMutation = useMutation8({
    mutationFn: llmApi.exportToCSV,
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `llm_configs_${(/* @__PURE__ */ new Date()).toISOString().split("T")[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast({
        title: "Export successful",
        description: "LLM configurations have been exported to CSV."
      });
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to export LLM configurations";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });
  const importMutation = useMutation8({
    mutationFn: ({ file, merge }) => llmApi.importFromCSV(file, merge),
    onSuccess: (result) => {
      queryClient2.invalidateQueries({ queryKey: ["llm-configs"] });
      toast({
        title: "Import successful",
        description: result.message
      });
      setImportDialogOpen(false);
      setSelectedFile(null);
      setMergeExisting(false);
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to import LLM configurations";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith(".csv")) {
        toast({
          title: "Invalid file",
          description: "Please select a CSV file.",
          variant: "destructive"
        });
        return;
      }
      setSelectedFile(file);
      setImportDialogOpen(true);
    }
  };
  const handleImport = () => {
    if (selectedFile) {
      importMutation.mutate({ file: selectedFile, merge: mergeExisting });
    }
  };
  const handleCopy = (llm) => {
    const defaultValues = {
      model_name: "",
      // Clear model_name so user must enter a new unique name
      model_id: llm.model_id,
      provider: llm.provider,
      display_name: `${llm.display_name} (Copy)`,
      is_active: llm.is_active,
      caching_supported: llm.caching_supported,
      reasoning_supported: llm.reasoning_supported,
      vision_supported: llm.vision_supported,
      input_token_cost: llm.input_token_cost,
      output_token_cost: llm.output_token_cost,
      cache_write_token_cost: llm.cache_write_token_cost,
      cache_read_token_cost: llm.cache_read_token_cost,
      unsupported_parameters: llm.unsupported_parameters,
      reasoning_effort: llm.reasoning_effort
    };
    setCopyDefaultValues(defaultValues);
    setCreateDialogOpen(true);
  };
  const filteredLLMs = React14.useMemo(() => {
    if (!llmConfigs)
      return [];
    if (!searchQuery)
      return llmConfigs;
    const query = searchQuery.toLowerCase();
    return llmConfigs.filter(
      (llm) => llm.model_name.toLowerCase().includes(query) || llm.model_id.toLowerCase().includes(query) || llm.display_name.toLowerCase().includes(query) || llm.provider.toLowerCase().includes(query)
    );
  }, [llmConfigs, searchQuery]);
  const providers = React14.useMemo(() => {
    if (!llmConfigs)
      return [];
    return Array.from(new Set(llmConfigs.map((llm) => llm.provider)));
  }, [llmConfigs]);
  return /* @__PURE__ */ jsxs29("div", { className: cn7("llm-management", className), children: [
    /* @__PURE__ */ jsxs29("div", { className: "space-y-6", children: [
      /* @__PURE__ */ jsxs29("div", { className: "flex items-center justify-between", children: [
        /* @__PURE__ */ jsxs29("div", { children: [
          /* @__PURE__ */ jsx33("h2", { className: "text-2xl font-bold", children: "LLM Configuration" }),
          /* @__PURE__ */ jsx33("p", { className: "text-foreground/60", children: "Manage available LLM models and their configurations" })
        ] }),
        /* @__PURE__ */ jsxs29("div", { className: "flex gap-2", children: [
          /* @__PURE__ */ jsxs29(
            Button19,
            {
              variant: "outline",
              onClick: () => exportMutation.mutate(),
              disabled: exportMutation.isPending,
              children: [
                /* @__PURE__ */ jsx33(Download, { className: "mr-2 h-4 w-4" }),
                exportMutation.isPending ? "Exporting..." : "Export CSV"
              ]
            }
          ),
          /* @__PURE__ */ jsx33(Label10, { htmlFor: "csv-file-input", className: "sr-only", children: "Import CSV file" }),
          /* @__PURE__ */ jsxs29(
            Button19,
            {
              variant: "outline",
              onClick: () => fileInputRef.current?.click(),
              disabled: importMutation.isPending,
              children: [
                /* @__PURE__ */ jsx33(Upload, { className: "mr-2 h-4 w-4" }),
                "Import CSV"
              ]
            }
          ),
          /* @__PURE__ */ jsx33(
            "input",
            {
              id: "csv-file-input",
              ref: fileInputRef,
              type: "file",
              accept: ".csv",
              onChange: handleFileChange,
              className: "hidden"
            }
          ),
          /* @__PURE__ */ jsxs29(Button19, { onClick: () => {
            setCopyDefaultValues(void 0);
            setCreateDialogOpen(true);
          }, children: [
            /* @__PURE__ */ jsx33(Plus5, { className: "mr-2 h-4 w-4" }),
            "Add LLM Model"
          ] })
        ] })
      ] }),
      /* @__PURE__ */ jsxs29("div", { className: "flex flex-col sm:flex-row gap-4", children: [
        /* @__PURE__ */ jsxs29("div", { className: "flex-1 max-w-md", children: [
          /* @__PURE__ */ jsx33(Label10, { htmlFor: "llm-search", className: "sr-only", children: "Search LLM models" }),
          /* @__PURE__ */ jsxs29("div", { className: "flex items-center gap-2 px-3 py-1.5 text-sm border rounded-md bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2", children: [
            /* @__PURE__ */ jsx33(Search4, { className: "h-3.5 w-3.5 text-muted-foreground shrink-0" }),
            /* @__PURE__ */ jsx33(
              "input",
              {
                id: "llm-search",
                type: "search",
                placeholder: "Search by model ID, name, or provider...",
                value: searchQuery,
                onChange: (e) => setSearchQuery(e.target.value),
                className: "flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
              }
            )
          ] })
        ] }),
        /* @__PURE__ */ jsxs29(Select4, { value: providerFilter, onValueChange: setProviderFilter, children: [
          /* @__PURE__ */ jsxs29(SelectTrigger4, { className: "w-[180px]", children: [
            /* @__PURE__ */ jsx33(Filter, { className: "mr-2 h-4 w-4" }),
            /* @__PURE__ */ jsx33(SelectValue4, { placeholder: "All Providers" })
          ] }),
          /* @__PURE__ */ jsxs29(SelectContent4, { children: [
            /* @__PURE__ */ jsx33(SelectItem4, { value: "all", children: "All Providers" }),
            providers.map((provider) => /* @__PURE__ */ jsx33(SelectItem4, { value: provider, children: provider }, provider))
          ] })
        ] }),
        /* @__PURE__ */ jsxs29(Select4, { value: statusFilter, onValueChange: setStatusFilter, children: [
          /* @__PURE__ */ jsx33(SelectTrigger4, { className: "w-[150px]", children: /* @__PURE__ */ jsx33(SelectValue4, { placeholder: "All Status" }) }),
          /* @__PURE__ */ jsxs29(SelectContent4, { children: [
            /* @__PURE__ */ jsx33(SelectItem4, { value: "all", children: "All Status" }),
            /* @__PURE__ */ jsx33(SelectItem4, { value: "active", children: "Active" }),
            /* @__PURE__ */ jsx33(SelectItem4, { value: "inactive", children: "Inactive" })
          ] })
        ] })
      ] }),
      isLoading ? /* @__PURE__ */ jsx33("div", { className: "text-center py-8", children: "Loading LLM configurations..." }) : filteredLLMs.length === 0 ? /* @__PURE__ */ jsx33("div", { className: "text-center py-8 text-muted-foreground", children: searchQuery || providerFilter !== "all" || statusFilter !== "all" ? "No LLM configurations found matching your filters." : "No LLM configurations found. Create one to get started." }) : /* @__PURE__ */ jsxs29(Card8, { children: [
        /* @__PURE__ */ jsxs29(CardHeader8, { children: [
          /* @__PURE__ */ jsx33(CardTitle8, { children: "LLM Models" }),
          /* @__PURE__ */ jsxs29(CardDescription6, { children: [
            filteredLLMs.length,
            " model",
            filteredLLMs.length !== 1 ? "s" : "",
            " found"
          ] })
        ] }),
        /* @__PURE__ */ jsx33(CardContent8, { children: /* @__PURE__ */ jsx33("div", { className: "rounded-md border", children: /* @__PURE__ */ jsxs29(Table4, { children: [
          /* @__PURE__ */ jsx33(TableHeader4, { children: /* @__PURE__ */ jsxs29(TableRow4, { children: [
            /* @__PURE__ */ jsx33(TableHead4, { className: "text-xs", children: "Model Name" }),
            /* @__PURE__ */ jsx33(TableHead4, { className: "text-xs", children: "Display Name" }),
            /* @__PURE__ */ jsx33(TableHead4, { className: "text-xs", children: "Provider" }),
            /* @__PURE__ */ jsx33(TableHead4, { className: "text-xs", children: "Status" }),
            /* @__PURE__ */ jsx33(TableHead4, { className: "text-xs", children: "Created" }),
            /* @__PURE__ */ jsx33(TableHead4, { className: "text-xs w-[200px]", children: "Actions" })
          ] }) }),
          /* @__PURE__ */ jsx33(TableBody4, { children: filteredLLMs.map((llm) => /* @__PURE__ */ jsxs29(TableRow4, { children: [
            /* @__PURE__ */ jsx33(TableCell4, { className: "font-mono text-sm", children: llm.model_name }),
            /* @__PURE__ */ jsx33(TableCell4, { className: "font-medium", children: llm.display_name }),
            /* @__PURE__ */ jsx33(TableCell4, { children: /* @__PURE__ */ jsx33(Badge4, { variant: "outline", children: llm.provider }) }),
            /* @__PURE__ */ jsx33(TableCell4, { children: llm.is_active ? /* @__PURE__ */ jsx33(Badge4, { variant: "default", className: "bg-green-700 hover:bg-green-700", children: "Active" }) : /* @__PURE__ */ jsx33(Badge4, { variant: "secondary", children: "Inactive" }) }),
            /* @__PURE__ */ jsx33(TableCell4, { className: "text-sm text-muted-foreground", children: new Date(llm.created_at).toLocaleDateString() }),
            /* @__PURE__ */ jsx33(TableCell4, { children: /* @__PURE__ */ jsxs29("div", { className: "flex items-center gap-1", children: [
              /* @__PURE__ */ jsx33(
                Button19,
                {
                  variant: "ghost",
                  size: "sm",
                  onClick: () => handleCopy(llm),
                  title: "Copy",
                  children: /* @__PURE__ */ jsx33(Copy, { className: "h-4 w-4" })
                }
              ),
              /* @__PURE__ */ jsx33(
                Button19,
                {
                  variant: "ghost",
                  size: "sm",
                  onClick: () => {
                    setSelectedLLM(llm);
                    setEditDialogOpen(true);
                  },
                  title: "Edit",
                  children: /* @__PURE__ */ jsx33(Edit4, { className: "h-4 w-4" })
                }
              ),
              /* @__PURE__ */ jsx33(
                Button19,
                {
                  variant: "ghost",
                  size: "sm",
                  onClick: () => {
                    setSelectedLLM(llm);
                    setDeleteDialogOpen(true);
                  },
                  title: "Delete",
                  children: /* @__PURE__ */ jsx33(Trash28, { className: "h-4 w-4 text-destructive" })
                }
              )
            ] }) })
          ] }, llm.model_name)) })
        ] }) }) })
      ] })
    ] }),
    /* @__PURE__ */ jsx33(
      CreateLLMDialog,
      {
        open: createDialogOpen,
        onOpenChange: (open) => {
          setCreateDialogOpen(open);
          if (!open) {
            setCopyDefaultValues(void 0);
          }
        },
        onSave: (data) => {
          createLLMMutation.mutate(data);
        },
        isPending: createLLMMutation.isPending,
        defaultValues: copyDefaultValues
      }
    ),
    selectedLLM && /* @__PURE__ */ jsxs29(Fragment8, { children: [
      /* @__PURE__ */ jsx33(
        EditLLMDialog,
        {
          llm: selectedLLM,
          open: editDialogOpen,
          onOpenChange: (open) => {
            setEditDialogOpen(open);
            if (!open)
              setSelectedLLM(null);
          },
          onSave: (data) => {
            updateLLMMutation.mutate({ model_name: selectedLLM.model_name, data });
          },
          isPending: updateLLMMutation.isPending
        }
      ),
      /* @__PURE__ */ jsx33(
        DeleteLLMDialog,
        {
          llm: selectedLLM,
          open: deleteDialogOpen,
          onOpenChange: (open) => {
            setDeleteDialogOpen(open);
            if (!open)
              setSelectedLLM(null);
          },
          onDelete: () => {
            deleteLLMMutation.mutate(selectedLLM.model_name);
          },
          isPending: deleteLLMMutation.isPending
        }
      )
    ] }),
    /* @__PURE__ */ jsx33(Dialog12, { open: importDialogOpen, onOpenChange: setImportDialogOpen, children: /* @__PURE__ */ jsxs29(DialogContent12, { children: [
      /* @__PURE__ */ jsxs29(DialogHeader12, { children: [
        /* @__PURE__ */ jsx33(DialogTitle12, { children: "Import LLM Configurations" }),
        /* @__PURE__ */ jsx33(DialogDescription12, { children: "Import LLM configurations from a CSV file." })
      ] }),
      /* @__PURE__ */ jsxs29("div", { className: "space-y-4", children: [
        selectedFile && /* @__PURE__ */ jsxs29("div", { className: "rounded-md border p-3", children: [
          /* @__PURE__ */ jsx33("p", { className: "text-sm font-medium", children: "Selected file:" }),
          /* @__PURE__ */ jsx33("p", { className: "text-sm text-muted-foreground", children: selectedFile.name })
        ] }),
        /* @__PURE__ */ jsxs29("div", { className: "flex items-center justify-between space-x-2 rounded-lg border p-4", children: [
          /* @__PURE__ */ jsxs29("div", { className: "space-y-0.5", children: [
            /* @__PURE__ */ jsx33(Label10, { htmlFor: "merge_existing", className: "text-base", children: "Merge Existing" }),
            /* @__PURE__ */ jsx33("p", { className: "text-sm text-muted-foreground", children: "Update existing configurations. If disabled, existing records will be skipped." })
          ] }),
          /* @__PURE__ */ jsx33(
            Switch5,
            {
              id: "merge_existing",
              checked: mergeExisting,
              onCheckedChange: setMergeExisting,
              disabled: importMutation.isPending
            }
          )
        ] })
      ] }),
      /* @__PURE__ */ jsxs29(DialogFooter9, { children: [
        /* @__PURE__ */ jsx33(
          Button19,
          {
            type: "button",
            variant: "outline",
            onClick: () => {
              setImportDialogOpen(false);
              setSelectedFile(null);
              setMergeExisting(false);
            },
            disabled: importMutation.isPending,
            children: "Cancel"
          }
        ),
        /* @__PURE__ */ jsx33(
          Button19,
          {
            onClick: handleImport,
            disabled: !selectedFile || importMutation.isPending,
            children: importMutation.isPending ? "Importing..." : "Import"
          }
        )
      ] })
    ] }) })
  ] });
};
var LLMManagement = ({
  app_id,
  className,
  debug = false
}) => {
  return /* @__PURE__ */ jsx33(AppConfigProvider, { app_id, debug, children: /* @__PURE__ */ jsx33(LLMManagementInner, { className }) });
};

// src/components/llm/LLMModelSelect.tsx
import React15 from "react";
import { useQuery as useQuery13 } from "@tanstack/react-query";
import {
  Select as Select5,
  SelectContent as SelectContent5,
  SelectItem as SelectItem5,
  SelectTrigger as SelectTrigger5,
  SelectValue as SelectValue5,
  SelectGroup,
  SelectLabel
} from "@umd-dit-ai-workspace/shared-ui";
import { Badge as Badge5 } from "@umd-dit-ai-workspace/shared-ui";
import { cn as cn8 } from "@umd-dit-ai-workspace/shared-ui";
import { jsx as jsx34, jsxs as jsxs30 } from "react/jsx-runtime";
var LLMModelSelectInternal = ({
  value,
  onValueChange,
  placeholder = "Select a model",
  disabled = false,
  className,
  activeOnly = true,
  provider,
  groupByProvider = false,
  showProviderBadge = false
}) => {
  const llmApi = useLLMConfigApi();
  const queryParams = {};
  if (activeOnly) {
    queryParams.active_only = true;
  }
  if (provider) {
    queryParams.provider = provider;
  }
  const { data: llmConfigs = [], isLoading } = useQuery13({
    queryKey: ["llm-configs", queryParams],
    queryFn: () => llmApi.listLLMConfigs(queryParams)
  });
  const groupedModels = React15.useMemo(() => {
    if (!groupByProvider)
      return null;
    const groups = {};
    llmConfigs.forEach((model) => {
      if (!groups[model.provider]) {
        groups[model.provider] = [];
      }
      groups[model.provider].push(model);
    });
    return groups;
  }, [llmConfigs, groupByProvider]);
  const selectedModel = React15.useMemo(() => {
    return llmConfigs.find((model) => model.model_name === value);
  }, [llmConfigs, value]);
  return /* @__PURE__ */ jsxs30(Select5, { value, onValueChange, disabled: disabled || isLoading, children: [
    /* @__PURE__ */ jsx34(SelectTrigger5, { className: cn8("w-full", className), children: /* @__PURE__ */ jsx34(SelectValue5, { placeholder: isLoading ? "Loading models..." : placeholder, children: selectedModel && /* @__PURE__ */ jsxs30("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx34("span", { children: selectedModel.display_name }),
      showProviderBadge && /* @__PURE__ */ jsx34(Badge5, { variant: "outline", className: "text-xs", children: selectedModel.provider })
    ] }) }) }),
    /* @__PURE__ */ jsx34(SelectContent5, { children: isLoading ? /* @__PURE__ */ jsx34("div", { className: "py-6 text-center text-sm text-muted-foreground", children: "Loading models..." }) : llmConfigs.length === 0 ? /* @__PURE__ */ jsx34("div", { className: "py-6 text-center text-sm text-muted-foreground", children: activeOnly ? "No active models available" : provider ? `No models found for ${provider}` : "No models available" }) : groupedModels ? (
      // Grouped by provider
      Object.entries(groupedModels).map(([providerName, models]) => /* @__PURE__ */ jsxs30(SelectGroup, { children: [
        /* @__PURE__ */ jsx34(SelectLabel, { children: providerName }),
        models.map((model) => /* @__PURE__ */ jsx34(SelectItem5, { value: model.model_name, children: /* @__PURE__ */ jsxs30("div", { className: "flex items-center justify-between w-full", children: [
          /* @__PURE__ */ jsx34("span", { children: model.display_name }),
          /* @__PURE__ */ jsx34("span", { className: "text-xs text-muted-foreground ml-2 font-mono", children: model.model_id })
        ] }) }, model.model_name))
      ] }, providerName))
    ) : (
      // Flat list
      llmConfigs.map((model) => /* @__PURE__ */ jsx34(SelectItem5, { value: model.model_name, children: /* @__PURE__ */ jsxs30("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx34("span", { children: model.display_name }),
        /* @__PURE__ */ jsx34(Badge5, { variant: "outline", className: "text-xs", children: model.provider }),
        /* @__PURE__ */ jsx34("span", { className: "text-xs text-muted-foreground font-mono", children: model.model_id })
      ] }) }, model.model_name))
    ) })
  ] });
};
var LLMModelSelect = ({
  app_id,
  debug = false,
  ...props
}) => {
  return /* @__PURE__ */ jsx34(AppConfigProvider, { app_id, debug, children: /* @__PURE__ */ jsx34(LLMModelSelectInternal, { ...props }) });
};

// src/pages/AdminPage.tsx
import { useState as useState22 } from "react";
import { Navigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@umd-dit-ai-workspace/shared-ui";
import { jsx as jsx35, jsxs as jsxs31 } from "react/jsx-runtime";
function AdminPage({
  app_id,
  appName,
  debugMode = false,
  additionalTabs = []
}) {
  const { isAdmin, user } = useAuth();
  const [adminSelectedOrgId, setAdminSelectedOrgId] = useState22(void 0);
  const [adminSelectedWorkspaceId, setAdminSelectedWorkspaceId] = useState22(void 0);
  if (!isAdmin) {
    return /* @__PURE__ */ jsx35(Navigate, { to: "/", replace: true });
  }
  console.log("[AdminPage] isAdmin:", isAdmin);
  console.log("[AdminPage] user:", user);
  const handleAdminOrgSelect = (orgId) => {
    setAdminSelectedOrgId(orgId || void 0);
    setAdminSelectedWorkspaceId(void 0);
  };
  const handleAdminWorkspaceSelect = (orgId, workspaceId) => {
    setAdminSelectedOrgId(orgId);
    setAdminSelectedWorkspaceId(workspaceId);
  };
  const displayName = appName || app_id.replace(/-/g, " ");
  const totalTabs = 2 + additionalTabs.length;
  const gridCols = `grid-cols-${totalTabs}`;
  return /* @__PURE__ */ jsxs31("div", { className: "container mx-auto px-4 py-4", children: [
    /* @__PURE__ */ jsx35("h1", { className: "text-2xl font-bold mb-6", children: "Administration" }),
    /* @__PURE__ */ jsxs31(Tabs, { defaultValue: "workspaces", className: "w-full", children: [
      /* @__PURE__ */ jsxs31(TabsList, { className: `grid w-full ${gridCols}`, children: [
        /* @__PURE__ */ jsx35(TabsTrigger, { value: "workspaces", children: "Workspaces & Users" }),
        /* @__PURE__ */ jsx35(TabsTrigger, { value: "llm-config", children: "LLM Configuration" }),
        additionalTabs.map((tab) => /* @__PURE__ */ jsx35(TabsTrigger, { value: tab.value, children: tab.label }, tab.value))
      ] }),
      /* @__PURE__ */ jsxs31(TabsContent, { value: "workspaces", className: "space-y-8", children: [
        /* @__PURE__ */ jsxs31("section", { className: "border-t pt-8", children: [
          /* @__PURE__ */ jsxs31("div", { className: "mb-6", children: [
            /* @__PURE__ */ jsx35("h2", { className: "text-xl font-bold", children: "Organization & Workspace Management" }),
            /* @__PURE__ */ jsxs31("p", { className: "text-muted-foreground", children: [
              "Manage organizations and workspaces for the ",
              displayName,
              " application."
            ] })
          ] }),
          /* @__PURE__ */ jsx35(
            WorkspacesComponent,
            {
              app_id,
              viewType: "global-admin",
              selectedOrganizationId: adminSelectedOrgId,
              selectedWorkspaceId: adminSelectedWorkspaceId,
              debug: debugMode,
              onOrganizationSelect: handleAdminOrgSelect,
              onWorkspaceSelect: handleAdminWorkspaceSelect
            }
          )
        ] }),
        /* @__PURE__ */ jsxs31("section", { className: "border-t pt-8", children: [
          /* @__PURE__ */ jsx35("h2", { className: "text-xl font-semibold mb-3", children: "User Management" }),
          /* @__PURE__ */ jsx35(
            UserManagement,
            {
              app_id,
              debug: debugMode
            }
          )
        ] })
      ] }),
      /* @__PURE__ */ jsx35(TabsContent, { value: "llm-config", className: "pt-6", children: /* @__PURE__ */ jsx35(
        LLMManagement,
        {
          app_id,
          debug: debugMode
        }
      ) }),
      additionalTabs.map((tab) => /* @__PURE__ */ jsx35(TabsContent, { value: tab.value, className: "pt-6", children: tab.content }, tab.value))
    ] })
  ] });
}
export {
  AdminPage,
  AppHeader,
  AuthProvider,
  DEFAULT_ORG_ID,
  DEFAULT_WS_ID,
  ImpersonationBanner,
  ImpersonationMenuItem,
  LLMManagement,
  LLMModelSelect,
  ORGANIZATIONS_QUERY_KEY,
  OrganizationSelector,
  ProfileMenu,
  STORAGE_KEY_SELECTED_WORKSPACE,
  USER_PROFILE_QUERY_KEY,
  UserManagement,
  WorkspaceSelector,
  WorkspacesComponent,
  applicationApi,
  clearActiveWorkspace,
  fetchOrgWorkspaces,
  fetchOrganizations,
  fetchUserProfile,
  getActiveWorkspace,
  getOrganizationId,
  initializeWorkspacesApi,
  llmConfigApi,
  saveActiveWorkspace,
  useAuth,
  useLLMConfigApi,
  useOrgWorkspaces,
  useOrgWorkspacesCache,
  useOrganizationApi,
  useOrganizations,
  useOrganizationsCache,
  usePrefetchOrgWorkspaces,
  usePrefetchOrganizations,
  usePrefetchUserProfile,
  useUserProfile,
  useUserProfileCache,
  useUsersApi,
  useWorkspaceApi,
  workspacesQueryKey
};
//# sourceMappingURL=index.js.map